import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  updateProfile as updateDbProfile,
  addJob,
  updateJob,
  deleteJob,
  getJobDetails,
  getJobsSummary,
  dbJobToAppJob,
  dbJobSummaryToApp,
  addInsight,
  upsertSkillAssessment,
  appProfileToDbProfile,
  canUserAnalyze,
  canUserGenerateResume,
  incrementResumesUsed,
  incrementAnalysesUsed,
  canUserFetch,
  incrementFetchesUsed,
  canAccessInterviewPrep,
  canAccessDevelopment,
  canAccessJobRadar,
  getEffectiveSubscription,
  createCheckoutSession,
  addInterview,
  updateInterview,
  deleteInterview as deleteInterviewFromDb,
  getInterviewsForJob,
  getTrackedCompanies,
  addTrackedCompany,
  updateTrackedCompany,
  deleteTrackedCompany,
  getJobAlerts,
  updateJobAlert,
  markAlertsAsRead,
  getUnreadAlertCount,
  dbTrackedCompanyToApp,
  dbJobAlertToApp,
  getBragBookEntries,
  addBragBookEntry,
  updateBragBookEntry,
  deleteBragBookEntry,
  dbBragBookEntryToApp,
  getCompanyResearch,
  saveCompanyResearch,
  getProfileEducation,
  connectGmail,
  disconnectGmail,
  updateGmailConnection,
  getApplicationEmails,
  addApplicationEmail,
  updateEmailRead,
  archiveEmail,
  linkEmailToJob,
  getUnreadEmailCount,
  dbApplicationEmailToApp
} from './supabaseClient';
import SubscriptionGate from './SubscriptionGate';
import OnboardingChecklist from './components/OnboardingChecklist';
import Confetti from './components/Confetti';
import {
  ProfileIcon, ResumeIcon, InsightsIcon, DataIcon, SettingsIcon, AnalysisIcon,
  TrackerIcon, SubscriptionIcon, SkillsIcon, RadarIcon, InterviewIcon,
  StarIcon, ClipboardIcon, LightbulbIcon, LightningIcon, RocketIcon,
  LockIcon, CheckIcon, TrophyIcon, ChatIcon, TargetIcon, ToolsIcon,
  PlanningIcon, SparkleIcon,
  SmallStarIcon, SmallClipboardIcon, SmallLightbulbIcon, SmallLightningIcon,
  SmallRocketIcon, SmallLockIcon, SmallCheckIcon, SmallTrophyIcon,
  SmallChatIcon, SmallSparkleIcon, SmallTargetIcon, SmallProfileIcon, SmallChartIcon, SmallResumeIcon
} from './components/FeatureIcons';
import SkillHeatmap from './components/SkillHeatmap';
import LearningPathCard from './components/LearningPathCard';
import AIResearchGapAnalysis from './components/AIResearchGapAnalysis';
import SwipeView from './components/SwipeView';
import RoleTrackCircles from './components/RoleTrackCircles';
import ConversionFunnel from './components/ConversionFunnel';
import WeeklyLearnings from './components/WeeklyLearnings';
import StatusDropdown from './components/StatusDropdown';
import { STANFORD_RL_COURSE } from './data/stanfordRLCourse';

const INITIAL_PROFILE = {
  name: "",
  title: "",
  email: "",
  targetRole: "",
  currentSalary: 0,
  targetSalaryMin: 0,
  targetSalaryMax: 0,
  summary: "",
  currentProjects: [],
  skills: [],
  experiences: [],
  education: [],
  activities: "",
  roleTracks: [],
  activeTrackId: null,
};

// Track limits by subscription tier
const TRACK_LIMITS = { free: 1, plus: 3, pro: 5 };

// Probability model: estimate chance of landing at least 1 offer across tracks
function computeCombinedProbability(tracks) {
  if (!tracks || tracks.length === 0) return 0;
  // P(no offer from any track) = product of (1 - pi)^ni
  let pNoOffer = 1;
  for (const track of tracks) {
    const n = track.estimatedJobCount || 10; // default estimate
    const p = track.estimatedSuccessRate || 0.006; // ~0.6% per app default
    pNoOffer *= Math.pow(1 - p, n);
  }
  return Math.min(0.99, 1 - pNoOffer); // cap at 99%
}

// Migrate single-role users to multi-track
function migrateProfileToTracks(profile) {
  if (profile.roleTracks && profile.roleTracks.length > 0) return profile;
  if (!profile.targetRole) return profile;

  const defaultTrack = {
    id: 'track_' + Date.now(),
    label: 'My Track',
    targetRole: profile.targetRole,
    targetSalaryMin: profile.targetSalaryMin || 0,
    targetSalaryMax: profile.targetSalaryMax || 0,
    estimatedJobCount: null,
    estimatedSuccessRate: null,
    lastMarketCheck: null,
    createdAt: new Date().toISOString(),
  };

  return {
    ...profile,
    roleTracks: [defaultTrack],
    activeTrackId: defaultTrack.id,
  };
}

export default function CareerAgent({ user, initialData, onSignOut, pendingPlan, onClearPendingPlan }) {
  // Use initial data from Supabase or fall back to defaults
  const [profile, setProfile] = useState(() => {
    if (initialData?.profile) {
      return migrateProfileToTracks({ ...INITIAL_PROFILE, ...initialData.profile });
    }
    return INITIAL_PROFILE;
  });
  const [jobLedger, setJobLedger] = useState(initialData?.jobs || []);
  const [discoveredInsights, setDiscoveredInsights] = useState(initialData?.insights || []);
  const [skillAssessments, setSkillAssessments] = useState(initialData?.skillAssessments || {});
  const [interviews, setInterviews] = useState(initialData?.interviews || []);
  const [bragBookEntries, setBragBookEntries] = useState(initialData?.bragBookEntries || []);
  const [subscriptionStatus, setSubscriptionStatus] = useState(initialData?.subscriptionStatus || 'free');
  const [effectiveTier, setEffectiveTier] = useState(() =>
    getEffectiveSubscription(initialData?.subscriptionStatus || 'free')
  );
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null); // 'resume', 'interview_prep', 'development'
  // Note: Resume limits checked when generating, fetch limits checked when fetching
  const [remainingFetches, setRemainingFetches] = useState(effectiveTier === 'pro' ? -1 : 5);
  const [remainingAnalyses, setRemainingAnalyses] = useState(effectiveTier === 'pro' ? -1 : 5);
  // Track if data is still loading - starts true, becomes false when profile loads
  const [dataLoading, setDataLoading] = useState(!initialData?.profile?.name);
  const userId = initialData?.userId || user?.id;

  // Pagination state for job list
  const [totalJobCount, setTotalJobCount] = useState(initialData?.totalJobCount || 0);
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);
  const hasMoreJobs = jobLedger.length < totalJobCount;

  // Sync state when initialData changes (e.g., after async data load)
  useEffect(() => {
    if (initialData) {
      console.log('CareerAgent: initialData changed, syncing state');
      console.log('🔵 Subscription sync:', {
        subscriptionStatus: initialData.subscriptionStatus,
        calculatedTier: getEffectiveSubscription(initialData.subscriptionStatus || 'free')
      });
      if (initialData.profile) {
        setProfile(migrateProfileToTracks({ ...INITIAL_PROFILE, ...initialData.profile }));
        if (initialData.profile.name) {
          setDataLoading(false); // Profile loaded - stop loading animation
        }
      }
      // Sync jobs from DB - on hard refresh, replace empty local state with DB jobs
      if (initialData.jobs) {
        setJobLedger(prev => {
          // If local state is empty, just use DB jobs directly
          if (prev.length === 0) {
            console.log('🔄 Job ledger init from DB:', initialData.jobs.length, 'jobs');
            return initialData.jobs;
          }
          // Otherwise merge: local jobs take precedence, add new DB jobs
          const localJobIds = new Set(prev.map(j => j.id));
          const newFromDb = initialData.jobs.filter(j => !localJobIds.has(j.id));
          // Check for local jobs with temp IDs that were saved and now have real IDs
          const mergedJobs = prev.map(localJob => {
            if (typeof localJob.id === 'number' && localJob._saved) {
              const savedJob = initialData.jobs.find(
                dbJob => dbJob.company === localJob.company &&
                        dbJob.role === localJob.role &&
                        Math.abs(new Date(dbJob.addedAt) - new Date(localJob.addedAt)) < 60000
              );
              if (savedJob) {
                return { ...localJob, id: savedJob.id };
              }
            }
            return localJob;
          });
          const combined = [...mergedJobs, ...newFromDb];
          console.log('🔄 Job ledger merge:', { local: prev.length, fromDb: initialData.jobs.length, merged: combined.length });
          return combined;
        });
      }
      if (initialData.insights) {
        setDiscoveredInsights(initialData.insights);
      }
      if (initialData.skillAssessments) {
        setSkillAssessments(initialData.skillAssessments);
      }
      if (initialData.interviews) {
        setInterviews(initialData.interviews);
      }
      if (initialData.bragBookEntries) {
        setBragBookEntries(initialData.bragBookEntries);
      }
      // Sync total job count for pagination
      if (initialData.totalJobCount !== undefined) {
        setTotalJobCount(initialData.totalJobCount);
      }
      // Always sync subscription status (even if 'free')
      setSubscriptionStatus(initialData.subscriptionStatus || 'free');
      setEffectiveTier(getEffectiveSubscription(initialData.subscriptionStatus || 'free'));
    }
  }, [initialData]);

  // Show subscription gate if user selected a plan from marketing page
  useEffect(() => {
    if (pendingPlan && subscriptionStatus === 'free') {
      setPaywallFeature(pendingPlan === 'pro' ? 'interview_coaching' : 'resume');
      setShowPaywall(true);
    }
  }, [pendingPlan, subscriptionStatus]);

  // Note: Fetch limits are checked when user actually tries to auto-fetch a job URL
  // This avoids unnecessary database calls on initial load

const SKILL_SALARY_IMPACT = {
  "Python": { impact: 15000, category: "core" },
  "SQL / Databases": { impact: 12000, category: "core" },
  "API Design": { impact: 14000, category: "core" },
  "System Design": { impact: 18000, category: "core" },
  "Data Pipelines": { impact: 16000, category: "core" },
  "Product Development": { impact: 20000, category: "core" },
  "Kubernetes / Docker": { impact: 18000, category: "infrastructure" },
  "LLM/AI Integration": { impact: 25000, category: "emerging" },
  "ML Ops": { impact: 22000, category: "emerging" },
  "Real-time Systems": { impact: 20000, category: "infrastructure" },
  "AWS/GCP Cloud": { impact: 18000, category: "infrastructure" },
  "TypeScript": { impact: 14000, category: "frontend" },
  "React": { impact: 14000, category: "frontend" },
  "CI/CD Pipelines": { impact: 12000, category: "infrastructure" },
  "Observability/Monitoring": { impact: 15000, category: "infrastructure" },
  "GraphQL": { impact: 12000, category: "api" },
  "Kafka/Event Streaming": { impact: 18000, category: "infrastructure" },
  "Technical Leadership": { impact: 25000, category: "leadership" },
};

  const [activeView, setActiveView] = useState('opportunities');
  
  // Current job analysis state
  const [currentJD, setCurrentJD] = useState('');
  const [currentJDUrl, setCurrentJDUrl] = useState('');
  const [currentJobPostedDate, setCurrentJobPostedDate] = useState('');
  const [urlParseStatus, setUrlParseStatus] = useState(null); // null | 'parsing' | 'success' | 'failed'
  const [showManualPaste, setShowManualPaste] = useState(false);
  const [parsedJobTitle, setParsedJobTitle] = useState('');
  const [parsedCompany, setParsedCompany] = useState('');
  const [currentFeedback, setCurrentFeedback] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(null); // null=ledger, input, analyzing, questions, result
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [gapQuestions, setGapQuestions] = useState([]);
  const [gapAnswers, setGapAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answerInput, setAnswerInput] = useState('');
  const [generatedResume, setGeneratedResume] = useState(null);
  const [suggestedProjects, setSuggestedProjects] = useState([]);
  const [expandedProjectIndex, setExpandedProjectIndex] = useState(null); // Track which project is expanded
  const [currentJobId, setCurrentJobId] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [settingsSection, setSettingsSection] = useState('profile'); // profile, resume, insights (Q&A log), data
  const [showAbout, setShowAbout] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [devSubTab, setDevSubTab] = useState('planning'); // planning, skills, projects
  const [copySuccess, setCopySuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [wasOnboardingIncomplete, setWasOnboardingIncomplete] = useState(null);

  // Interview tracking state
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [selectedJobForInterview, setSelectedJobForInterview] = useState(null);
  const [interviewForm, setInterviewForm] = useState({
    interviewType: 'phone_screen',
    scheduledAt: '',
    durationMinutes: 60,
    location: '',
    meetingLink: '',
    recruiterName: '',
    recruiterEmail: '',
    interviewerNames: [],
    emailThread: '',
    notes: ''
  });
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [interviewView, setInterviewView] = useState('list'); // list, detail
  const [generatingPrep, setGeneratingPrep] = useState(false);
  const [generatingSTAR, setGeneratingSTAR] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState({}); // Track which company accordions are open

  // Brag Book state
  const [showBragBookForm, setShowBragBookForm] = useState(false);
  const [editingBragEntry, setEditingBragEntry] = useState(null);
  const [bragBookForm, setBragBookForm] = useState({
    title: '',
    situation: '',
    task: '',
    action: '',
    result: '',
    skills: [],
    date: '',
    company: '',
    impact: ''
  });
  const [selectedBragEntry, setSelectedBragEntry] = useState(null);
  const [prepModeCompany, setPrepModeCompany] = useState(null);
  const [generatingBragSuggestion, setGeneratingBragSuggestion] = useState(false);

  // STAR Generator state
  const [showStarGenerator, setShowStarGenerator] = useState(false);
  const [generatingStars, setGeneratingStars] = useState(false);
  const [draftStars, setDraftStars] = useState([]);
  const [selectedDraftStar, setSelectedDraftStar] = useState(null);
  const [amplificationAnswers, setAmplificationAnswers] = useState({});
  const [additionalContext, setAdditionalContext] = useState('');
  const [amplifyingStars, setAmplifyingStars] = useState(false);

  // Pro-only interview coaching chat state
  const [coachingMessages, setCoachingMessages] = useState([]);
  const [coachingInput, setCoachingInput] = useState('');
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [showCoaching, setShowCoaching] = useState(false);

  // Job Radar state (Pro feature)
  const [opportunitiesSubTab, setOpportunitiesSubTab] = useState('tracker'); // tracker, dailymatch, radar
  const [interviewsSubTab, setInterviewsSubTab] = useState('interviews'); // interviews, brag_book
  const [trackedCompanies, setTrackedCompanies] = useState([]);
  const [jobAlerts, setJobAlerts] = useState([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    careersUrl: '',
    targetRoles: [],
    trackAllJobs: true
  });
  const [loadingRadar, setLoadingRadar] = useState(false);

  // Gmail/Email integration state
  const [gmailConnected, setGmailConnected] = useState(initialData?.profile?.gmail_connected || false);
  const [applicationEmails, setApplicationEmails] = useState([]);
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  const [emailSyncing, setEmailSyncing] = useState(false);
  const [emailsViewMode, setEmailsViewMode] = useState('chronological'); // 'chronological' | 'company'
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailFilter, setEmailFilter] = useState('all'); // 'all' | 'unread' | 'interviews' | 'offers' | 'rejections'
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);

  // Resume/profile import state
  const [profileImportMethod, setProfileImportMethod] = useState(null); // null | 'upload' | 'linkedin' | 'manual'
  const [resumeUploadStep, setResumeUploadStep] = useState(null); // null | 'extracting' | 'parsing' | 'preview' | 'error'
  const [resumeUploadError, setResumeUploadError] = useState(null);
  const [parsedResumeData, setParsedResumeData] = useState(null);
  const [manualProfileText, setManualProfileText] = useState('');

  // LinkedIn import state
  const [linkedInStep, setLinkedInStep] = useState(null); // null | 'input' | 'parsing' | 'preview' | 'error'
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [linkedInText, setLinkedInText] = useState('');
  const [linkedInError, setLinkedInError] = useState(null);
  const [parsedLinkedInData, setParsedLinkedInData] = useState(null);
  const [showManualLinkedIn, setShowManualLinkedIn] = useState(false);

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(true); // Data comes from props now

  // Multi-track toggle state
  const [showTrackSwitcher, setShowTrackSwitcher] = useState(false);
  const [showAddTrackModal, setShowAddTrackModal] = useState(false);

  // Save profile changes to Supabase
  const saveProfile = useCallback(async (newProfile) => {
    if (!userId) {
      console.warn('saveProfile: No userId, skipping save');
      return;
    }
    try {
      const dbProfile = appProfileToDbProfile(newProfile);
      console.log('💾 saveProfile called:', {
        userId,
        experiences: dbProfile.experiences?.length || 0,
        skills: dbProfile.skills?.length || 0,
        name: dbProfile.name
      });

      const { data, error } = await updateDbProfile(userId, dbProfile);
      if (error) {
        console.error('❌ saveProfile error:', error);
      } else {
        console.log('✅ saveProfile success');
      }
    } catch (e) {
      console.error('❌ saveProfile exception:', e.message);
    }
  }, [userId]);

  // Debounced profile save - only save if profile has meaningful data
  // This prevents overwriting saved data with empty defaults on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userId && profile) {
        // Only auto-save if profile has at least a name or experiences
        // This prevents the empty default profile from overwriting saved data
        const hasData = profile.name || (profile.experiences && profile.experiences.length > 0);
        if (hasData) {
          console.log('🔄 Auto-save triggered');
          saveProfile(profile);
        } else {
          console.log('⏭️ Auto-save skipped (no meaningful data yet)');
        }
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [profile, userId, saveProfile]);

  // Multi-track derived values
  const activeTrack = useMemo(() => {
    if (!profile.roleTracks || profile.roleTracks.length === 0) return null;
    return profile.roleTracks.find(t => t.id === profile.activeTrackId) || profile.roleTracks[0];
  }, [profile.roleTracks, profile.activeTrackId]);

  const maxTracks = useMemo(() => TRACK_LIMITS[effectiveTier] || 1, [effectiveTier]);

  const trackProbability = useMemo(() => computeCombinedProbability(profile.roleTracks || []), [profile.roleTracks]);

  // Track CRUD functions
  const switchTrack = useCallback((trackId) => {
    setProfile(prev => {
      const track = prev.roleTracks.find(t => t.id === trackId);
      if (!track) return prev;
      return {
        ...prev,
        activeTrackId: trackId,
        targetRole: track.targetRole,
        targetSalaryMin: track.targetSalaryMin,
        targetSalaryMax: track.targetSalaryMax,
      };
    });
  }, []);

  const addTrack = useCallback((label, targetRole, salaryMin, salaryMax) => {
    setProfile(prev => {
      if (prev.roleTracks.length >= maxTracks) return prev;
      const newTrack = {
        id: 'track_' + Date.now(),
        label,
        targetRole,
        targetSalaryMin: salaryMin || 0,
        targetSalaryMax: salaryMax || 0,
        estimatedJobCount: null,
        estimatedSuccessRate: null,
        lastMarketCheck: null,
        createdAt: new Date().toISOString(),
      };
      const newTracks = [...prev.roleTracks, newTrack];
      return {
        ...prev,
        roleTracks: newTracks,
        activeTrackId: prev.activeTrackId || newTrack.id,
        targetRole: prev.activeTrackId ? prev.targetRole : newTrack.targetRole,
        targetSalaryMin: prev.activeTrackId ? prev.targetSalaryMin : newTrack.targetSalaryMin,
        targetSalaryMax: prev.activeTrackId ? prev.targetSalaryMax : newTrack.targetSalaryMax,
      };
    });
  }, [maxTracks]);

  const updateTrack = useCallback((trackId, updates) => {
    setProfile(prev => {
      const newTracks = prev.roleTracks.map(t =>
        t.id === trackId ? { ...t, ...updates } : t
      );
      const updatedTrack = newTracks.find(t => t.id === trackId);
      if (trackId === prev.activeTrackId && updatedTrack) {
        return {
          ...prev,
          roleTracks: newTracks,
          targetRole: updatedTrack.targetRole,
          targetSalaryMin: updatedTrack.targetSalaryMin,
          targetSalaryMax: updatedTrack.targetSalaryMax,
        };
      }
      return { ...prev, roleTracks: newTracks };
    });
  }, []);

  const removeTrack = useCallback((trackId) => {
    setProfile(prev => {
      if (prev.roleTracks.length <= 1) return prev;
      const newTracks = prev.roleTracks.filter(t => t.id !== trackId);
      if (prev.activeTrackId === trackId) {
        const fallback = newTracks[0];
        return {
          ...prev,
          roleTracks: newTracks,
          activeTrackId: fallback.id,
          targetRole: fallback.targetRole,
          targetSalaryMin: fallback.targetSalaryMin,
          targetSalaryMax: fallback.targetSalaryMax,
        };
      }
      return { ...prev, roleTracks: newTracks };
    });
  }, []);

  // Estimate market size for a track by calling job aggregator
  const estimateTrackMarket = useCallback(async (trackId) => {
    const track = profile.roleTracks.find(t => t.id === trackId);
    if (!track || !track.targetRole) return;

    try {
      const response = await fetch('/api/job-aggregator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetRole: track.targetRole,
          limit: 1, // We just want the count
        }),
      });
      const data = await response.json();
      const jobCount = data.count || 0;
      // Rough monthly estimate: daily results * 30
      const estimatedMonthly = Math.max(jobCount * 3, 10);

      // Estimate success rate from avg match scores
      const avgMatch = 70; // Default; TODO: compute from user's actual data
      const pResponse = 0.08 * (avgMatch / 70);
      const pOffer = pResponse * 0.40 * 0.20;

      updateTrack(trackId, {
        estimatedJobCount: estimatedMonthly,
        estimatedSuccessRate: Math.max(0.002, pOffer),
        lastMarketCheck: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Market estimate failed:', err);
    }
  }, [profile.roleTracks, updateTrack]);

  // Load Job Radar data when switching to radar tab (Pro feature)
  useEffect(() => {
    const loadRadarData = async () => {
      if (!userId || opportunitiesSubTab !== 'radar') return;

      // Check if user has access first
      const { canAccess } = await canAccessJobRadar(userId);
      if (!canAccess) return;

      setLoadingRadar(true);
      try {
        const [companiesRes, alertsRes, countRes] = await Promise.all([
          getTrackedCompanies(userId),
          getJobAlerts(userId, { limit: 50 }),
          getUnreadAlertCount(userId)
        ]);

        if (companiesRes.data) {
          setTrackedCompanies(companiesRes.data.map(dbTrackedCompanyToApp));
        }
        if (alertsRes.data) {
          setJobAlerts(alertsRes.data.map(dbJobAlertToApp));
        }
        if (countRes.count !== undefined) {
          setUnreadAlertCount(countRes.count);
        }
      } catch (e) {
        console.error('Failed to load radar data:', e);
      }
      setLoadingRadar(false);
    };

    loadRadarData();
  }, [userId, opportunitiesSubTab]);

  // Load Brag Book entries when switching to brag_book tab
  const [bragBookLoaded, setBragBookLoaded] = useState(false);
  useEffect(() => {
    const loadBragBookData = async () => {
      if (!userId || interviewsSubTab !== 'brag_book' || bragBookLoaded) return;

      try {
        const { data, error } = await getBragBookEntries(userId);
        if (error) {
          console.error('Failed to load brag book entries:', error);
          return;
        }
        if (data && data.length > 0) {
          setBragBookEntries(data.map(dbBragBookEntryToApp));
        }
        setBragBookLoaded(true);
      } catch (e) {
        console.error('Failed to load brag book entries:', e);
      }
    };

    loadBragBookData();
  }, [userId, interviewsSubTab, bragBookLoaded]);

  // Load application emails when switching to emails tab
  useEffect(() => {
    const loadEmailsData = async () => {
      if (!userId || interviewsSubTab !== 'emails' || emailsLoaded) return;

      try {
        const { data, error } = await getApplicationEmails(userId);
        if (error) {
          console.error('Failed to load application emails:', error);
          return;
        }
        if (data && data.length > 0) {
          setApplicationEmails(data.map(dbApplicationEmailToApp));
        }
        setEmailsLoaded(true);

        // Also get unread count
        const { count } = await getUnreadEmailCount(userId);
        setUnreadEmailCount(count || 0);
      } catch (e) {
        console.error('Failed to load application emails:', e);
      }
    };

    loadEmailsData();
  }, [userId, interviewsSubTab, emailsLoaded]);

  // Track onboarding completion and trigger confetti celebration
  useEffect(() => {
    const hasName = profile?.name && profile.name.trim() !== '';
    const hasTargetRole = profile?.targetRole && profile.targetRole.trim() !== '';
    const hasSalary = (profile?.targetSalaryMin > 0 || profile?.targetSalaryMax > 0);
    const hasResume = profile?.experiences && profile.experiences.length > 0;
    const isComplete = hasName && hasTargetRole && hasSalary && hasResume;

    // If we previously tracked incomplete and now it's complete, celebrate!
    if (wasOnboardingIncomplete === true && isComplete) {
      setShowConfetti(true);
    }

    // Track current state for next comparison
    setWasOnboardingIncomplete(!isComplete);
  }, [profile?.name, profile?.targetRole, profile?.targetSalaryMin, profile?.targetSalaryMax, profile?.experiences?.length]);

  // Helper to compute salaryFit based on range overlap (don't trust AI to get this right)
  const computeSalaryFit = (salaryRange, targetMin, targetMax) => {
    if (!salaryRange || typeof salaryRange !== 'string') return 'unknown';

    // Extract numbers from salary string (handles $175,000, $175K, 175000, etc.)
    const numbers = salaryRange.match(/\$?(\d{1,3}(?:,\d{3})*|\d+)(?:\s*[Kk])?/g);
    if (!numbers || numbers.length === 0) return 'unknown';

    // Parse each number, handling K suffix
    const parsedNumbers = numbers.map(n => {
      const cleaned = n.replace(/[$,]/g, '');
      const hasK = /[Kk]/.test(n);
      const num = parseFloat(cleaned);
      return hasK ? num * 1000 : (num < 1000 ? num * 1000 : num); // Assume small numbers are in K
    }).filter(n => !isNaN(n) && n > 10000); // Filter out non-salary numbers

    if (parsedNumbers.length === 0) return 'unknown';

    const jobMin = Math.min(...parsedNumbers);
    const jobMax = Math.max(...parsedNumbers);

    // Use defaults if target salary not set
    const tMin = targetMin || 0;
    const tMax = targetMax || Infinity;

    // Check for overlap: ranges overlap if job_max >= target_min AND job_min <= target_max
    if (jobMax >= tMin && jobMin <= tMax) {
      return 'in-range';
    } else if (jobMax < tMin) {
      return 'below-range';
    } else {
      return 'above-range';
    }
  };

  // Job Radar helper functions
  const handleAddCompany = async () => {
    if (!userId || !companyForm.companyName || !companyForm.careersUrl) return;

    try {
      const { data, error } = await addTrackedCompany(userId, companyForm);
      if (error) throw error;

      setTrackedCompanies(prev => [dbTrackedCompanyToApp(data), ...prev]);
      setCompanyForm({ companyName: '', careersUrl: '', targetRoles: [], trackAllJobs: true });
      setShowAddCompany(false);
    } catch (e) {
      console.error('Failed to add company:', e);
      alert('Failed to add company. It may already be tracked.');
    }
  };

  const handleRemoveCompany = async (companyId) => {
    if (!confirm('Remove this company from tracking?')) return;

    try {
      await deleteTrackedCompany(companyId);
      setTrackedCompanies(prev => prev.filter(c => c.id !== companyId));
      setJobAlerts(prev => prev.filter(a => a.trackedCompanyId !== companyId));
    } catch (e) {
      console.error('Failed to remove company:', e);
    }
  };

  const handleToggleCompanyActive = async (companyId, isActive) => {
    try {
      await updateTrackedCompany(companyId, { isActive: !isActive });
      setTrackedCompanies(prev => prev.map(c =>
        c.id === companyId ? { ...c, isActive: !isActive } : c
      ));
    } catch (e) {
      console.error('Failed to toggle company:', e);
    }
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      await updateJobAlert(alertId, { isRead: true });
      setJobAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, isRead: true } : a
      ));
      setUnreadAlertCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark alert read:', e);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await updateJobAlert(alertId, { status: 'dismissed', isRead: true });
      setJobAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, status: 'dismissed', isRead: true } : a
      ));
    } catch (e) {
      console.error('Failed to dismiss alert:', e);
    }
  };

  // Helper to add job to Supabase with retry logic
  const saveJobToDb = async (jobData, retryCount = 0) => {
    if (!userId) {
      console.warn('⚠️ Cannot save job: no userId');
      return { ...jobData, _saveError: 'No user ID' };
    }
    try {
      const { data, error } = await addJob(userId, {
        company: jobData.company,
        role: jobData.role,
        url: jobData.url,
        status: jobData.status,
        matchScore: jobData.matchScore,
        confidence: jobData.confidence,
        isTarget: jobData.isTarget,
        analysis: jobData.analysis,
        gapAnswers: jobData.gapAnswers,
        generatedResume: jobData.generatedResume,
        rawJd: jobData.rawJD || jobData.rawJd,
        suggestedProjects: jobData.suggestedProjects || [],
      });
      if (error) throw error;
      console.log('✅ Job saved to Supabase:', data?.id);
      return { ...jobData, id: data.id, _saved: true };
    } catch (e) {
      console.error(`❌ Failed to save job (attempt ${retryCount + 1}):`, e);
      // Retry up to 2 times with exponential backoff
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return saveJobToDb(jobData, retryCount + 1);
      }
      // After all retries failed, return with error flag
      console.error('❌ All save attempts failed for job:', jobData.company, jobData.role);
      return { ...jobData, _saveError: e.message || 'Save failed' };
    }
  };

  // Helper to update job in Supabase
  const updateJobInDb = async (jobId, updates) => {
    if (!userId || !jobId) return;
    try {
      await updateJob(jobId, {
        status: updates.status,
        match_score: updates.matchScore,
        confidence: updates.confidence,
        is_target: updates.isTarget,
        analysis: updates.analysis,
        gap_answers: updates.gapAnswers,
        generated_resume: updates.generatedResume,
        suggested_projects: updates.suggestedProjects,
        rejected_at_stage: updates.rejectedAtStage,
        applied_at: updates.appliedAt,
      });
    } catch (e) {
      console.error('Failed to update job:', e);
    }
  };

  // Helper to add insight to Supabase
  const saveInsightToDb = async (insight) => {
    if (!userId) return insight;
    try {
      const { data, error } = await addInsight(userId, insight);
      if (error) throw error;
      return { ...insight, id: data.id };
    } catch (e) {
      console.error('Failed to save insight:', e);
      return insight;
    }
  };

  // Helper to save skill assessment
  const saveSkillAssessment = async (skill, level, notes) => {
    if (!userId) return;
    try {
      await upsertSkillAssessment(userId, skill, level, notes);
    } catch (e) {
      console.error('Failed to save skill assessment:', e);
    }
  };

  // Helper to save interview to Supabase
  const saveInterviewToDb = async (interviewData) => {
    console.log('💾 saveInterviewToDb called:', { userId, interviewData });
    if (!userId) {
      console.warn('⚠️ No userId, interview will not be saved to DB');
      return interviewData;
    }
    try {
      const { data, error } = await addInterview(userId, interviewData);
      console.log('💾 addInterview result:', { data, error });
      if (error) throw error;
      // Get the job info to add to the interview (use string comparison for ID matching)
      const job = jobLedger.find(j => String(j.id) === String(interviewData.jobId));
      console.log('✅ Interview saved successfully with id:', data.id);
      return {
        ...interviewData,
        id: data.id,
        company: interviewData.company || job?.company,
        role: interviewData.role || job?.role
      };
    } catch (e) {
      console.error('❌ Failed to save interview:', e);
      return interviewData;
    }
  };

  // Helper to update interview in Supabase
  const updateInterviewInDb = async (interviewId, updates) => {
    if (!userId) return;
    try {
      await updateInterview(interviewId, updates);
    } catch (e) {
      console.error('Failed to update interview:', e);
    }
  };

  // Helper to delete interview
  const handleDeleteInterview = async (interviewId) => {
    try {
      await deleteInterviewFromDb(interviewId);
      setInterviews(prev => prev.filter(i => i.id !== interviewId));
      if (selectedInterview?.id === interviewId) {
        setSelectedInterview(null);
        setInterviewView('list');
      }
    } catch (e) {
      console.error('Failed to delete interview:', e);
    }
  };

  // Schedule a new interview
  const scheduleInterview = async () => {
    if (!selectedJobForInterview) return;

    const newInterview = {
      ...interviewForm,
      jobId: selectedJobForInterview.id,
      company: selectedJobForInterview.company,
      role: selectedJobForInterview.role,
      status: 'scheduled'
    };

    const savedInterview = await saveInterviewToDb(newInterview);
    setInterviews(prev => [...prev, savedInterview]);

    // Update job status to reflect interview scheduled
    if (selectedJobForInterview.status === 'ready') {
      setJobLedger(prev => prev.map(j =>
        j.id === selectedJobForInterview.id ? { ...j, status: 'applied' } : j
      ));
      await updateJobInDb(selectedJobForInterview.id, { status: 'applied' });
    }

    // Reset form
    setShowScheduleInterview(false);
    setSelectedJobForInterview(null);
    setInterviewForm({
      interviewType: 'phone_screen',
      scheduledAt: '',
      durationMinutes: 60,
      location: '',
      meetingLink: '',
      recruiterName: '',
      recruiterEmail: '',
      interviewerNames: [],
      emailThread: '',
      notes: ''
    });
  };

  // Generate STAR examples for an interview (Pro feature)
  const generateSTARExamples = async (interview) => {
    // Check if user has Pro access
    const { canAccess } = await canAccessInterviewPrep(userId);
    if (!canAccess) {
      setPaywallFeature('interview_prep');
      setShowPaywall(true);
      return;
    }

    setGeneratingSTAR(true);
    const job = jobLedger.find(j => String(j.id) === String(interview.jobId));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);

      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          messages: [{
            role: "user",
            content: `Generate 3 STAR (Situation, Task, Action, Result) examples for this upcoming interview.

INTERVIEW TYPE: ${interview.interviewType}
COMPANY: ${job?.company || interview.company}
ROLE: ${job?.role || interview.role}

JOB REQUIREMENTS:
${JSON.stringify(job?.analysis?.requiredSkills || [], null, 2)}

KEY RESPONSIBILITIES:
${JSON.stringify(job?.analysis?.keyResponsibilities || [], null, 2)}

IDENTIFIED GAPS TO ADDRESS:
${JSON.stringify(job?.analysis?.gaps || [], null, 2)}

CANDIDATE PROFILE:
${JSON.stringify(profile.experiences, null, 2)}

CANDIDATE'S CURRENT PROJECTS:
${JSON.stringify(profile.currentProjects?.filter(p => p.status === 'active'), null, 2)}

CANDIDATE'S SKILLS:
${profile.skills?.join(', ')}

EMAIL CONTEXT FROM RECRUITER:
${interview.emailThread || 'None provided'}

Generate 5 STAR examples that:
1. Map directly to the job's required skills and responsibilities
2. Address identified gaps with concrete experience
3. Highlight leadership and impact where possible
4. Are specific with metrics and outcomes

Respond in JSON:
{
  "starExamples": [
    {
      "skill": "skill this demonstrates",
      "situation": "The context/background",
      "task": "What you needed to accomplish",
      "action": "Specific steps you took",
      "result": "Quantified outcome",
      "followUpQuestions": ["Likely follow-up Q1", "Likely follow-up Q2"],
      "talkingPoints": ["Key point to emphasize", "Another key point"]
    }
  ]
}`
          }]
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        const updatedInterview = {
          ...interview,
          starExamples: result.starExamples
        };

        setInterviews(prev => prev.map(i =>
          i.id === interview.id ? updatedInterview : i
        ));
        setSelectedInterview(updatedInterview);
        await updateInterviewInDb(interview.id, { starExamples: result.starExamples });
      }
    } catch (e) {
      console.error('Failed to generate STAR examples:', e);
      if (e.name === 'AbortError') {
        alert('Failed to generate STAR examples. The request may have timed out. Please try again.');
      }
    }
    setGeneratingSTAR(false);
  };

  // Generate prep document for an interview (Pro feature)
  // Enhanced with Market Analysis, VC-style Company Brief, Role Impact & Equity, and Brag Book
  const generatePrepDocument = async (interview) => {
    // Check if user has Pro access
    const { canAccess } = await canAccessInterviewPrep(userId);
    if (!canAccess) {
      setPaywallFeature('interview_prep');
      setShowPaywall(true);
      return;
    }

    setGeneratingPrep(true);
    const job = jobLedger.find(j => String(j.id) === String(interview.jobId));

    // Get top relevant brag book entries with relevance scoring
    const scoredEntries = bragBookEntries.map(entry => {
      let score = 0;
      const matchedSkills = [];

      entry.skills?.forEach(skill => {
        const skillLower = skill.toLowerCase();

        // Check against required skills (critical = 3 points)
        const matchesRequired = job?.analysis?.requiredSkills?.some(rs =>
          skillLower.includes(rs.toLowerCase()) || rs.toLowerCase().includes(skillLower)
        );
        if (matchesRequired) {
          score += 3;
          matchedSkills.push({ skill, importance: 'required' });
          return;
        }

        // Check against nice-to-have skills (1 point)
        const matchesNiceToHave = job?.analysis?.niceToHaveSkills?.some(ns =>
          skillLower.includes(ns.toLowerCase()) || ns.toLowerCase().includes(skillLower)
        );
        if (matchesNiceToHave) {
          score += 1;
          matchedSkills.push({ skill, importance: 'nice-to-have' });
          return;
        }

        // Fallback: check against keySkills if requiredSkills doesn't exist
        const matchesKeySkill = job?.analysis?.keySkills?.some(ks =>
          skillLower.includes(ks.toLowerCase()) || ks.toLowerCase().includes(skillLower)
        );
        if (matchesKeySkill) {
          score += 2;
          matchedSkills.push({ skill, importance: 'key' });
        }
      });

      return { ...entry, relevanceScore: score, matchedSkills };
    })
    .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Take top 3 relevant entries, fill with unscored entries if needed
    const relevantEntries = scoredEntries.filter(e => e.relevanceScore > 0).slice(0, 3);
    const bragEntriesForPrep = relevantEntries.length >= 3
      ? relevantEntries
      : [...relevantEntries, ...scoredEntries.filter(e => e.relevanceScore === 0).slice(0, 3 - relevantEntries.length)];

    const companyName = job?.company || interview.company;

    try {
      // Step 1: Check for cached company research
      let marketAnalysis = null;
      let companyBrief = null;
      const cachedResearch = await getCompanyResearch(companyName);

      if (cachedResearch.data) {
        console.log('Using cached company research for:', companyName);
        marketAnalysis = cachedResearch.data.market_analysis;
        companyBrief = cachedResearch.data.company_brief;
      } else {
        // Step 2: Generate company research (same pattern as STAR examples)
        console.log('Generating company research for:', companyName);
        const researchController = new AbortController();
        const researchTimeout = setTimeout(() => researchController.abort(), 30000);

        const researchResponse = await fetch("/api/anthropic", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: researchController.signal,
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 1000,
            messages: [{
              role: "user",
              content: `Generate market analysis and company brief for: ${companyName}

ROLE CONTEXT: ${job?.role || interview.role}
INDUSTRY HINTS FROM JOB: ${job?.analysis?.keySkills?.join(', ') || 'Not specified'}

Generate a VC-style analysis. Respond in JSON:
{
  "marketAnalysis": {
    "industry": "industry name",
    "marketSize": "TAM/SAM estimate",
    "growthRate": "X% CAGR or growth description",
    "keyTrends": ["trend 1", "trend 2", "trend 3"],
    "competitiveLandscape": "market position analysis"
  },
  "companyBrief": {
    "overview": "what they do in 1-2 sentences",
    "stage": "Seed/Series A/B/C/Public/Unknown",
    "fundingEstimate": "estimated funding if known",
    "headcount": "employee count estimate",
    "momentum": ["positive signal 1", "positive signal 2"],
    "risks": ["risk 1", "risk 2"],
    "bullCase": "why this company could win big",
    "bearCase": "what could go wrong",
    "vcVerdict": "Would you invest? 1-2 sentence assessment"
  }
}`
            }]
          })
        });

        clearTimeout(researchTimeout);

        if (researchResponse.ok) {
          const researchData = await researchResponse.json();
          const researchText = researchData.content?.map(c => c.text || '').join('') || '';
          const researchMatch = researchText.match(/\{[\s\S]*\}/);
          if (researchMatch) {
            const parsed = JSON.parse(researchMatch[0]);
            marketAnalysis = parsed.marketAnalysis;
            companyBrief = parsed.companyBrief;
            await saveCompanyResearch(companyName, marketAnalysis, companyBrief);
            console.log('Saved company research to cache');
          }
        }
      }

      // Step 3: Generate interview-specific prep (same pattern as STAR examples)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: `Interview prep for ${interview.interviewType} at ${companyName} (${job?.role || interview.role}).

Skills needed: ${(job?.analysis?.requiredSkills || job?.analysis?.keySkills || []).slice(0, 5).join(', ')}
Candidate skills: ${profile.skills?.slice(0, 5).join(', ')}
Top achievement: ${bragEntriesForPrep[0]?.title || 'N/A'} - ${bragEntriesForPrep[0]?.result || ''}

JSON response only:
{"roleImpact":{"whyThisRoleMatters":"1 sentence","growthTrajectory":"IC->Senior->Lead path","skillsDeveloped":["skill1","skill2"]},"equityAnalysis":{"questionsToAsk":["q1","q2"]},"talkingPoints":{"topStories":[{"achievement":"from brag book","howToFrame":"framing tip"}]},"interviewStrategy":{"questionsToAsk":[{"question":"q","why":"reason"}],"likelyQuestions":[{"question":"q","approach":"how to answer"}],"closingStrategy":"1 sentence"}}`
          }]
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Prep document API error:', response.status, errorText);
        alert('Failed to generate prep document. Please try again.');
        setGeneratingPrep(false);
        return;
      }

      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        const parsedPrep = JSON.parse(jsonMatch[0]);
        // Merge cached company research if we used it
        const prepDocument = {
          ...parsedPrep,
          marketAnalysis: marketAnalysis || parsedPrep.marketAnalysis,
          companyBrief: companyBrief || parsedPrep.companyBrief
        };
        const updatedInterview = {
          ...interview,
          prepDocument,
          questionsToAsk: prepDocument.interviewStrategy?.questionsToAsk
        };

        setInterviews(prev => prev.map(i =>
          i.id === interview.id ? updatedInterview : i
        ));
        setSelectedInterview(updatedInterview);
        await updateInterviewInDb(interview.id, {
          prepDocument,
          questionsToAsk: prepDocument.interviewStrategy?.questionsToAsk
        });
      } else {
        console.error('Failed to parse prep document response:', text);
        alert('Failed to generate prep document. Please try again.');
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.error('Prep document request timed out');
        alert('Request timed out. The AI is taking too long. Please try again.');
      } else {
        console.error('Failed to generate prep document:', e);
        alert('Failed to generate prep document. Please try again.');
      }
    }
    setGeneratingPrep(false);
  };

  // Pro-only: Interactive AI coaching chat for interview prep
  const sendCoachingMessage = async (interview, userMessage) => {
    if (!userMessage.trim() || effectiveTier !== 'pro') return;

    const job = jobLedger.find(j => String(j.id) === String(interview.jobId));
    const newUserMessage = { role: 'user', content: userMessage };
    setCoachingMessages(prev => [...prev, newUserMessage]);
    setCoachingInput('');
    setCoachingLoading(true);

    try {
      // Build context from interview prep and history
      const systemContext = `You are an expert interview coach helping a candidate prepare for their upcoming interview. Be encouraging but honest. Provide specific, actionable advice.

INTERVIEW CONTEXT:
- Company: ${job?.company || interview.company}
- Role: ${job?.role || interview.role}
- Interview Type: ${interview.interviewType}
- Date: ${new Date(interview.scheduledAt).toLocaleDateString()}

CANDIDATE PROFILE:
- Name: ${profile.name}
- Current Title: ${profile.title}
- Target Role: ${profile.targetRole}
- Skills: ${profile.skills?.join(', ') || 'Not specified'}

JOB ANALYSIS:
${JSON.stringify(job?.analysis || {}, null, 2)}

${interview.prepDocument ? `PREP DOCUMENT ALREADY GENERATED:
${JSON.stringify(interview.prepDocument, null, 2)}` : ''}

${interview.starExamples?.length > 0 ? `STAR EXAMPLES PREPARED:
${interview.starExamples.map(s => s.skill).join(', ')}` : ''}

Help the candidate with:
- Practicing answers to likely questions
- Refining their STAR stories
- Building confidence
- Addressing concerns or weak spots
- Mock interview practice with feedback

Keep responses concise but helpful. If they want to practice answering a question, give them the question first, wait for their answer, then provide constructive feedback.`;

      const messages = [
        { role: 'user', content: systemContext },
        { role: 'assistant', content: "I'm your interview coach! I've reviewed your profile and the role you're preparing for. How can I help you get ready for this interview? I can help with:\n\n- Practice answering likely questions\n- Refine your STAR stories\n- Mock interview scenarios\n- Address any concerns\n\nWhat would you like to focus on?" },
        ...coachingMessages,
        newUserMessage
      ];

      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1500,
          messages
        })
      });

      const data = await response.json();
      const assistantMessage = data.content?.map(c => c.text || '').join('') || "I apologize, I couldn't process that. Could you try rephrasing?";

      setCoachingMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
    } catch (e) {
      console.error('Coaching error:', e);
      setCoachingMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I encountered an error. Please try again." }]);
    }
    setCoachingLoading(false);
  };

  // Start coaching session with context
  const startCoachingSession = (interview) => {
    setCoachingMessages([]);
    setShowCoaching(true);
  };

  // Record interview outcome and feedback
  const recordInterviewOutcome = async (interview, outcome, notes, feedback) => {
    const updatedInterview = {
      ...interview,
      status: 'completed',
      outcome,
      outcomeNotes: notes,
      feedback,
      completedAt: new Date().toISOString()
    };

    setInterviews(prev => prev.map(i =>
      i.id === interview.id ? updatedInterview : i
    ));
    setSelectedInterview(updatedInterview);

    await updateInterviewInDb(interview.id, {
      status: 'completed',
      outcome,
      outcomeNotes: notes,
      feedback,
      completedAt: new Date().toISOString()
    });

    // Add feedback insights
    if (feedback?.skillsToImprove?.length > 0) {
      for (const skill of feedback.skillsToImprove) {
        const insight = {
          skill,
          context: `Interview feedback: Need to improve ${skill} based on ${interview.company} ${interview.interviewType} interview`,
          source: 'interview_feedback',
          jobContext: `${interview.role} at ${interview.company}`
        };
        const savedInsight = await saveInsightToDb(insight);
        setDiscoveredInsights(prev => [...prev, savedInsight]);
      }
    }
  };

  // Calculate confidence score
  // Confidence = how ready you are to apply (not just how good a match you are)
  const calculateConfidence = (analysis, answers) => {
    const baseScore = analysis?.matchScore || 50;
    
    // Count gaps by importance
    const criticalGaps = analysis?.gaps?.filter(g => g.importance === 'critical') || [];
    const importantGaps = analysis?.gaps?.filter(g => g.importance === 'important') || [];
    const totalCritical = criticalGaps.length;
    const totalImportant = importantGaps.length;
    
    // Start from base score
    let confidence = baseScore;
    
    // Proportional penalties (not hard caps)
    // Each critical gap reduces confidence by 8%
    confidence -= totalCritical * 8;
    
    // Each important gap reduces confidence by 3%
    confidence -= totalImportant * 3;
    
    // Answering questions gives modest boost (max +10)
    // Shows you've thought about the gaps
    const answerBoost = Math.min(10, Object.keys(answers).length * 2);
    confidence += answerBoost;
    
    // Ready threshold is 80%
    // With critical gaps, cap below ready threshold
    if (totalCritical >= 2) {
      confidence = Math.min(confidence, 70); // Definitely defer
    } else if (totalCritical === 1) {
      confidence = Math.min(confidence, 78); // Borderline - probably defer
    }
    
    return Math.min(90, Math.max(35, Math.round(confidence)));
  };

  // Handle subscription (with optional promo code)
  const handleSubscribe = async (plan = 'entry', promoCode = null) => {
    try {
      const { url, error } = await createCheckoutSession(userId, user?.email, plan, promoCode);
      if (error) throw error;
      if (url) {
        window.location.href = url;
      }
    } catch (e) {
      console.error('Subscription error:', e);
      alert('Unable to start checkout. Please try again.');
    }
  };

  // Parse job URL with Gemini
  const parseJobUrl = async () => {
    console.log('🔵 parseJobUrl called');
    console.log('🔵 currentJDUrl:', currentJDUrl);
    console.log('🔵 effectiveTier:', effectiveTier);
    console.log('🔵 userId:', userId);

    if (!currentJDUrl.trim()) {
      console.log('🔴 No URL provided, returning early');
      return;
    }

    // Check if user can fetch (Pro unlimited, Free 5/mo)
    // Skip limit check for now - let users fetch freely during testing
    if (effectiveTier !== 'pro' && false) { // Disabled temporarily
      console.log('🔵 Checking fetch limit...');
      try {
        // Add 3s timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 3000)
        );
        const fetchCheck = await Promise.race([canUserFetch(userId), timeoutPromise]);
        console.log('🔵 fetchCheck result:', fetchCheck);
        const { canFetch, remaining } = fetchCheck;
        console.log('🔵 canFetch:', canFetch, 'remaining:', remaining);
        if (!canFetch) {
          console.log('🔴 Fetch limit reached');
          setUrlParseStatus('limit_reached');
          setShowManualPaste(true);
          return;
        }
        setRemainingFetches(remaining);
      } catch (fetchError) {
        console.error('🔴 Error checking fetch limit:', fetchError);
        // Allow fetch on error (don't block user)
      }
    }

    console.log('🔵 Setting status to parsing...');
    setUrlParseStatus('parsing');
    try {
      console.log('🔵 Fetching job URL:', currentJDUrl.trim());
      const response = await fetch('/api/parse-job-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: currentJDUrl.trim() }),
      });

      console.log('Response status:', response.status);
      if (!response.ok) {
        console.error('API error:', response.status, response.statusText);
        setUrlParseStatus('failed');
        setShowManualPaste(true);
        return;
      }

      const data = await response.json();
      console.log('Parse result:', data);

      if (data.success && data.job) {
        // Successfully parsed - populate the fields
        const description = data.job.description || '';
        console.log('🔵 Setting currentJD, length:', description.length, 'first 100 chars:', description.slice(0, 100));
        setCurrentJD(description);
        setParsedJobTitle(data.job.title || '');
        setParsedCompany(data.job.company || '');
        setUrlParseStatus('success');
        setShowManualPaste(false);

        // Increment fetch usage for non-Pro users
        if (effectiveTier !== 'pro') {
          const { remaining } = await incrementFetchesUsed(userId);
          setRemainingFetches(Math.max(0, remaining));
        }
      } else {
        // Parsing failed - show manual input (don't count against limit)
        setUrlParseStatus('failed');
        setShowManualPaste(true);
      }
    } catch (error) {
      console.error('URL parse error:', error);
      setUrlParseStatus('failed');
      setShowManualPaste(true);
    }
  };

  // Helper: Find duplicate job by URL or company+role
  const findDuplicateJob = (url, company, role) => {
    // First check by URL (most reliable)
    if (url) {
      const urlMatch = jobLedger.find(j => j.url && j.url.toLowerCase() === url.toLowerCase());
      if (urlMatch) return urlMatch;
    }
    // Then check by company + role (case-insensitive)
    if (company && role) {
      const companyRoleMatch = jobLedger.find(j =>
        j.company?.toLowerCase() === company.toLowerCase() &&
        j.role?.toLowerCase() === role.toLowerCase()
      );
      if (companyRoleMatch) return companyRoleMatch;
    }
    return null;
  };

  // Helper: Navigate to existing job
  const navigateToExistingJob = (existingJob) => {
    setCurrentJobId(existingJob.id);
    setCurrentAnalysis(existingJob.analysis);
    setCurrentJD(existingJob.rawJD || '');
    setCurrentJDUrl(existingJob.url || '');
    setAnalysisStep('questions');
    // Set gap questions from existing analysis
    const questions = existingJob.analysis?.gaps
      ?.filter(g => g.importance !== 'nice-to-have')
      ?.map(g => ({
        skill: g.skill,
        importance: g.importance,
        question: g.question
      })) || [];
    setGapQuestions(questions);
    setGapAnswers({});
    setCurrentQuestionIndex(0);
  };

  // Analyze JD - optionally pass jdOverride to avoid React state timing issues
  const analyzeJobDescription = async (jdOverride = null) => {
    const jdToAnalyze = jdOverride || currentJD;
    console.log('🔵 analyzeJobDescription called, JD length:', jdToAnalyze.length);
    if (!jdToAnalyze.trim()) {
      console.log('❌ No JD content, returning');
      return;
    }

    // Check for duplicate by URL before analysis
    if (currentJDUrl) {
      const existingByUrl = findDuplicateJob(currentJDUrl, null, null);
      if (existingByUrl) {
        const viewExisting = confirm(
          `You've already analyzed this job: "${existingByUrl.role}" at ${existingByUrl.company}.\n\nClick OK to view the existing analysis, or Cancel to analyze again.`
        );
        if (viewExisting) {
          navigateToExistingJob(existingByUrl);
          return;
        }
      }
    }

    // Check analysis limit for free users (5/month, pro/admin unlimited)
    const { canAnalyze, remaining } = await canUserAnalyze(userId);
    if (!canAnalyze) {
      alert('You\'ve used all 5 free job analyses this month. Upgrade to Pro for unlimited analyses!');
      setPaywallFeature('analysis');
      setShowPaywall(true);
      return;
    }
    setRemainingAnalyses(remaining);

    setAnalysisStep('analyzing');
    setLoading(true);
    console.log('🔵 Starting Anthropic API fetch...');

    try {
      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2500,
          messages: [{
            role: "user",
            content: `Analyze this job description against the candidate profile. Be rigorous.

TARGET ROLE: ${profile.targetRole || 'Your Target Role'}
TARGET SALARY RANGE: $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K - $${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K

JOB DESCRIPTION:
${jdToAnalyze}

CANDIDATE PROFILE (work history):
${JSON.stringify(profile.experiences, null, 2)}

CANDIDATE'S CURRENT PROJECTS (actively building):
${JSON.stringify(profile.currentProjects?.filter(p => p.status === 'active').map(p => ({
  name: p.name,
  description: p.description,
  impact: p.impact,
  skills: p.skills
})), null, 2)}

CANDIDATE'S SKILLS:
${profile.skills?.join(', ')}

PREVIOUSLY DISCOVERED INSIGHTS:
${JSON.stringify(discoveredInsights.slice(-10), null, 2)}

When evaluating gaps, consider that the candidate may be ACTIVELY DEVELOPING skills through their current projects. If a required skill is being built in a current project, it's a MODERATE gap, not critical.

Respond in JSON:
{
  "company": "company name",
  "role": "job title",
  "location": "location if mentioned",
  "salaryRange": null,
  "salaryFit": "unknown",
  "requiredSkills": ["skill1", "skill2"],
  "niceToHaveSkills": ["skill1"],
  "keyResponsibilities": ["resp1", "resp2"],
  "matchScore": 65,
  "strengths": [
    {"skill": "skill", "evidence": "specific evidence from resume OR current projects", "strength": "strong/moderate"}
  ],
  "gaps": [
    {"skill": "skill", "importance": "critical/important/nice-to-have", "question": "A specific question to ask the candidate to see if they have hidden experience with this"}
  ],
  "relevantProjects": ["Names of candidate's current projects that are relevant to this role"],
  "overallAssessment": "2-3 sentence honest assessment including mention of relevant current projects"
}

IMPORTANT: For salaryRange, ONLY include an actual salary/compensation if it is EXPLICITLY stated in the job description (e.g. "$150,000 - $180,000" or "$200K+" or "competitive salary of $X"). If no salary is mentioned, set salaryRange to null and salaryFit to "unknown". Do NOT guess or infer a salary.

SALARY FIT LOGIC - FOLLOW THESE STEPS EXACTLY:
Candidate's TARGET range: $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K - $${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K

Step 1: Extract job's salary range (min and max numbers)
Step 2: Check for OVERLAP using this formula:
        IF (job_max >= ${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K) AND (job_min <= ${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K) → "in-range"
Step 3: If no overlap, determine direction:
        IF job_max < ${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K → "below-range"
        IF job_min > ${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K → "above-range"

CRITICAL EXAMPLES (target $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K-$${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K):
✓ Job $150K-$220K → IN-RANGE (220 >= ${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)} AND 150 <= ${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}, overlap exists!)
✓ Job $160K-$210K → IN-RANGE (overlap at $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K-$210K)
✓ Job $180K-$300K → IN-RANGE (overlap at $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K-$${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K)
✗ Job $120K-$150K → BELOW-RANGE (max $150K < min target $${((profile.targetSalaryMin || 200000) / 1000).toFixed(0)}K, NO overlap)
✗ Job $300K-$400K → ABOVE-RANGE (min $300K > max target $${((profile.targetSalaryMax || 250000) / 1000).toFixed(0)}K, NO overlap)
? No salary mentioned → "unknown"`
          }]
        })
      });

      console.log('🔵 Fetch completed, status:', response.status);
      const data = await response.json();
      console.log('🔵 Response data keys:', Object.keys(data));

      // Check for API errors
      if (data.error) {
        console.error('❌ Anthropic API error:', data.error);
        throw new Error(data.error.message || 'API request failed');
      }

      const text = data.content?.map(c => c.text || '').join('') || '';
      console.log('🔵 Response text length:', text.length);
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      console.log('🔵 JSON match found:', !!jsonMatch);

      if (jsonMatch) {
        const analysis = JSON.parse(jsonMatch[0]);
        console.log('🔵 Parsed analysis:', { company: analysis.company, role: analysis.role, matchScore: analysis.matchScore });

        // Recompute salaryFit ourselves (AI often gets overlap logic wrong)
        if (analysis.salaryRange) {
          const computedFit = computeSalaryFit(analysis.salaryRange, profile.targetSalaryMin, profile.targetSalaryMax);
          console.log('🔵 Salary fit - AI said:', analysis.salaryFit, '| Computed:', computedFit);
          analysis.salaryFit = computedFit;
        }

        setCurrentAnalysis(analysis);

        // Extract questions from gaps
        const questions = analysis.gaps
          ?.filter(g => g.importance !== 'nice-to-have')
          ?.map(g => ({
            skill: g.skill,
            importance: g.importance,
            question: g.question
          })) || [];

        setGapQuestions(questions);
        setGapAnswers({});
        setCurrentQuestionIndex(0);

        // Check for duplicate by company+role (catches manual JD paste without URL)
        const existingByCompanyRole = findDuplicateJob(null, analysis.company, analysis.role);
        if (existingByCompanyRole) {
          setLoading(false);
          const viewExisting = confirm(
            `You've already analyzed a similar job: "${existingByCompanyRole.role}" at ${existingByCompanyRole.company}.\n\nClick OK to view the existing analysis, or Cancel to save this as a new entry.`
          );
          if (viewExisting) {
            navigateToExistingJob(existingByCompanyRole);
            return;
          }
        }

        // Add to ledger as "analyzing"
        const tempId = Date.now();
        const newJob = {
          id: tempId,
          company: analysis.company,
          role: analysis.role,
          url: currentJDUrl || null,
          jobPostedDate: currentJobPostedDate || null,
          feedback: currentFeedback || null,
          rawJD: currentJD,
          status: 'analyzing',
          matchScore: analysis.matchScore,
          addedAt: new Date().toISOString(),
          analysis
        };
        setJobLedger(prev => [newJob, ...prev]);
        setCurrentJobId(tempId);

        // Persist to database and get real ID
        const savedJob = await saveJobToDb(newJob);
        if (savedJob._saveError) {
          // Save failed after retries - mark in ledger but continue
          console.error('⚠️ Job not saved to database:', savedJob._saveError);
          setJobLedger(prev => prev.map(j => j.id === tempId ? { ...j, _saveError: savedJob._saveError } : j));
          // Show alert to user
          alert(`Warning: Job analysis completed but failed to save to database. Error: ${savedJob._saveError}. Please try refreshing if this persists.`);
        } else if (savedJob.id !== tempId) {
          // Update ledger with real database ID
          setJobLedger(prev => prev.map(j => j.id === tempId ? { ...j, id: savedJob.id, _saved: true } : j));
          setCurrentJobId(savedJob.id);
          console.log('✅ Job saved to database:', savedJob.id);
        }

        // Track analysis usage for free users (pro/admin are unlimited)
        if (effectiveTier !== 'pro') {
          const { remaining } = await incrementAnalysesUsed(userId);
          setRemainingAnalyses(Math.max(0, remaining));
          console.log('🔵 Analysis complete, usage tracked. Remaining:', remaining);
        } else {
          console.log('🔵 Analysis complete (pro/admin - unlimited)');
        }

        // Store feedback as discovered insight if provided
        if (currentFeedback.trim()) {
          setDiscoveredInsights(prev => [...prev, {
            id: Date.now(),
            skill: 'General Context',
            context: currentFeedback,
            source: 'human_feedback',
            jobContext: `${analysis.role} at ${analysis.company}`,
            timestamp: new Date().toISOString()
          }]);
        }

        // Show gap questions (user can skip via Skip All button if desired)
        setAnalysisStep('questions');
      } else {
        // No valid JSON in response - show error
        console.error('No JSON found in API response:', text);
        throw new Error('Failed to parse job analysis response');
      }
    } catch (e) {
      console.error('Analysis failed:', e);
      // Fallback: manual analysis
      setCurrentAnalysis({
        company: "Unknown",
        role: "Product Engineer",
        matchScore: 70,
        strengths: [],
        gaps: [{ skill: "Unknown", importance: "important", question: "Tell me about relevant experience not on your resume" }],
        overallAssessment: "API unavailable - please describe your relevant experience manually."
      });
      setGapQuestions([{ skill: "General", importance: "important", question: "What relevant experience do you have that's not fully captured in your resume?" }]);
      setAnalysisStep('questions');
    }
    setLoading(false);
  };

  // Submit feedback and recalculate match
  const submitFeedback = async () => {
    if (!currentFeedback.trim() || !currentAnalysis) return;
    setLoading(true);
    
    try {
      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1500,
          messages: [{
            role: "user",
            content: `Recalculate the match score based on this feedback. The candidate is providing additional context.

ORIGINAL ANALYSIS:
${JSON.stringify(currentAnalysis, null, 2)}

CANDIDATE'S FEEDBACK:
${currentFeedback}

CANDIDATE PROFILE:
${JSON.stringify(profile, null, 2)}

Based on this feedback, provide an updated assessment. Respond in JSON:
{
  "adjustedMatchScore": 75,
  "adjustedAssessment": "Updated 2-3 sentence assessment incorporating the feedback",
  "gapsAddressed": ["skill1", "skill2"],
  "remainingGaps": [
    {"skill": "skill", "importance": "critical/important/nice-to-have", "question": "Updated question if still relevant"}
  ]
}`
          }]
        })
      });
      
      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        
        // Update analysis with new score
        setCurrentAnalysis(prev => ({
          ...prev,
          matchScore: result.adjustedMatchScore,
          overallAssessment: result.adjustedAssessment,
          gaps: result.remainingGaps || prev.gaps
        }));
        
        // Update gap questions if they changed
        const newQuestions = result.remainingGaps
          ?.filter(g => g.importance !== 'nice-to-have')
          ?.map(g => ({ skill: g.skill, importance: g.importance, question: g.question })) || [];
        if (newQuestions.length > 0) {
          setGapQuestions(newQuestions);
          setCurrentQuestionIndex(0);
        }
        
        // Update job in ledger
        setJobLedger(prev => prev.map(j => 
          j.id === currentJobId ? {
            ...j,
            matchScore: result.adjustedMatchScore,
            feedback: currentFeedback,
            analysis: { ...j.analysis, matchScore: result.adjustedMatchScore, overallAssessment: result.adjustedAssessment }
          } : j
        ));
      }
      
      // Save feedback as insight
      setDiscoveredInsights(prev => [...prev, {
        id: Date.now(),
        skill: 'Feedback',
        context: currentFeedback,
        source: 'human_feedback',
        jobContext: `${currentAnalysis.role} at ${currentAnalysis.company}`,
        timestamp: new Date().toISOString()
      }]);
      
      setFeedbackSubmitted(true);
    } catch (e) {
      console.error('Feedback recalculation failed:', e);
      // Still mark as submitted even if API fails
      setFeedbackSubmitted(true);
    }
    setLoading(false);
  };

  // Submit answer and move to next question
  const submitAnswer = () => {
    if (!answerInput.trim()) return;
    
    const currentQ = gapQuestions[currentQuestionIndex];
    const newAnswers = {
      ...gapAnswers,
      [currentQ.skill]: answerInput
    };
    setGapAnswers(newAnswers);
    
    // Store as discovered insight
    setDiscoveredInsights(prev => [...prev, {
      id: Date.now(),
      skill: currentQ.skill,
      context: answerInput,
      source: 'gap_question',
      timestamp: new Date().toISOString()
    }]);
    
    setAnswerInput('');
    
    if (currentQuestionIndex < gapQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // All questions answered, finalize
      finalizeAnalysis(currentAnalysis, newAnswers);
    }
  };

  // Skip current question without saving an answer
  const skipQuestion = () => {
    setAnswerInput('');
    if (currentQuestionIndex < gapQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Last question, finalize with current answers
      finalizeAnalysis(currentAnalysis, gapAnswers);
    }
  };

  // Skip all remaining questions and finalize
  const skipAllQuestions = () => {
    setAnswerInput('');
    finalizeAnalysis(currentAnalysis, gapAnswers);
  };

  // Finalize analysis and determine outcome
  const finalizeAnalysis = async (analysis, answers) => {
    setLoading(true);
    setAnalysisStep('generating'); // Show loading indicator
    const confidence = calculateConfidence(analysis, answers);
    const newStatus = confidence >= 80 ? 'ready' : 'deferred';

    // Update ledger
    setJobLedger(prev => prev.map(j =>
      j.id === currentJobId ? {
        ...j,
        status: newStatus,
        confidence,
        gapAnswers: answers,
        feedback: currentFeedback || j.feedback,
        updatedAt: new Date().toISOString()
      } : j
    ));

    // Persist status change to database (don't await - run in parallel with generation)
    const statusUpdatePromise = updateJobInDb(currentJobId, {
      status: newStatus,
      confidence,
      gapAnswers: answers
    }).then(() => console.log('✅ Job status updated to:', newStatus, 'confidence:', confidence));

    // Start generation immediately (don't wait for status update)
    if (confidence >= 80) {
      // Generate resume in parallel with status update
      await Promise.all([statusUpdatePromise, generateResume(analysis, answers)]);
    } else {
      // Generate project suggestions in parallel with status update
      await Promise.all([statusUpdatePromise, generateDeferredProjects(analysis, answers)]);
    }

    setAnalysisStep('result');
    setLoading(false);
  };

  // Generate tailored resume
  const generateResume = async (analysis, answers) => {
    try {
      // Use education from profile (now included in initial fetch)
      // Fallback to lazy-load from DB if somehow missing
      let educationData = profile.education;
      console.log('📚 Education from profile:', JSON.stringify(educationData));
      if (!educationData || educationData.length === 0) {
        console.log('📚 Education missing from profile, lazy-loading from DB...');
        const { data: eduData } = await getProfileEducation(userId);
        console.log('📚 Education from DB:', JSON.stringify(eduData));
        if (eduData && eduData.length > 0) {
          educationData = eduData;
          setProfile(prev => ({ ...prev, education: eduData }));
        }
      }

      const educationStr = Array.isArray(educationData) && educationData.length > 0
        ? educationData.map(edu => {
            if (typeof edu === 'string') return edu;
            // Handle object format from Gemini parse: { institution, degree, year }
            const parts = [];
            if (edu.institution) parts.push(edu.institution);
            if (edu.degree) parts.push(edu.degree);
            if (edu.year) parts.push(edu.year);
            return parts.join(' — ');
          }).filter(Boolean).join(', ')
        : (typeof educationData === 'string' && educationData ? educationData : 'Not provided');

      // Sort experiences by date (most recent first) then number for tracking
      const sortedExperiences = [...profile.experiences].sort((a, b) => {
        // Extract end date from period string (e.g., "Jan 2020 - Present" or "2018 - 2020")
        const getEndDate = (period) => {
          if (!period) return 0;
          const str = period.toLowerCase();
          if (str.includes('present') || str.includes('current')) return Date.now();
          // Try to parse the end portion (after the dash)
          const parts = str.split(/\s*[-–—]\s*/);
          const endPart = parts.length > 1 ? parts[parts.length - 1].trim() : parts[0].trim();
          const parsed = new Date(endPart);
          return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
        };
        return getEndDate(b.period) - getEndDate(a.period);
      });
      const numberedExperiences = sortedExperiences.map((exp, i) => ({
        ...exp,
        _index: i + 1
      }));

      // Calculate bullet limits to fit 1 page
      const expCount = numberedExperiences.length;
      const maxBullets = expCount <= 3 ? 4 : expCount <= 5 ? 3 : 2;

      console.log('📝 Resume generation input:', {
        educationStr,
        activities: profile.activities || 'Not provided',
        experienceCount: expCount,
        maxBulletsPerJob: maxBullets,
        experienceOrder: numberedExperiences.map(e => `${e._index}. ${e.company} (${e.period})`)
      });

      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `Generate a tailored 1-page resume for this role. Output ONLY valid JSON, no markdown.

TARGET ROLE: ${analysis.role} at ${analysis.company}
REQUIRED SKILLS: ${analysis.requiredSkills?.join(', ')}
KEY RESPONSIBILITIES: ${analysis.keyResponsibilities?.join(', ')}

CANDIDATE'S WORK HISTORY (${profile.experiences.length} total experiences - ALL MUST appear in output):
${JSON.stringify(numberedExperiences, null, 2)}

CANDIDATE'S CURRENT PROJECTS (can be mentioned in summary or as recent work):
${JSON.stringify(profile.currentProjects?.filter(p => p.status === 'active').map(p => ({
  name: p.name,
  description: p.description,
  impact: p.impact,
  skills: p.skills
})), null, 2)}

CANDIDATE'S EDUCATION (use exactly as provided):
${educationStr}

CANDIDATE'S ACTIVITIES (use exactly as provided):
${profile.activities || 'Not provided'}

ADDITIONAL CONTEXT FROM CANDIDATE (incorporate relevant details):
${JSON.stringify(answers, null, 2)}

HUMAN FEEDBACK & CORRECTIONS:
${currentFeedback || 'None provided'}

ALL DISCOVERED INSIGHTS (ONLY use to add quantified details to bullets that ALREADY exist at the CORRECT company - never use insights to create new achievements or move them between companies):
${JSON.stringify(discoveredInsights, null, 2)}

CRITICAL INSTRUCTIONS:

EXPERIENCE COMPLETENESS (HIGHEST PRIORITY):
- The candidate has EXACTLY ${profile.experiences.length} work experiences. Your output MUST contain EXACTLY ${profile.experiences.length} experiences.
- EVERY experience from the input MUST appear in the output. DO NOT drop, merge, or skip ANY experience.
- After generating, COUNT your output experiences. If the count does not equal ${profile.experiences.length}, you have a bug - fix it.
- List of companies that MUST appear: ${profile.experiences.map(e => e.company).join(', ')}

EXPERIENCE ORDERING (MANDATORY):
- Experiences are pre-sorted in REVERSE CHRONOLOGICAL order (most recent first).
- Output them in the EXACT SAME ORDER as input: ${numberedExperiences.map(e => `#${e._index} ${e.company}`).join(', ')}
- Do NOT reorder, rearrange, or shuffle experiences under any circumstances.

DATA INTEGRITY (MANDATORY - NEVER VIOLATE):
- NEVER transfer or mix achievements between companies. Each bullet MUST stay with its original company.
- NEVER fabricate experience, skills, or timelines. Only use facts from the provided work history.
- NEVER exaggerate experience duration. Calculate years from the ACTUAL periods when that skill appears in bullets:
  * Look at which experiences mention a skill (e.g., "AI", "ML", "Python")
  * Calculate duration ONLY from when it FIRST appears to present
  * Example: If AI/ML first appears in a 2025 role, say "2 years AI/ML experience", NOT "10+ years"
- The company name, role, and period for each experience are IMMUTABLE - do not change them.
- Each bullet must describe work that was ACTUALLY done at that specific company during that time period.
- In the summary, do NOT claim years of experience for skills that only appear in recent roles.

EDUCATION:
- The "education" field in the output MUST contain the EXACT education text provided above.
- Do NOT omit, summarize, or modify the education data.

TAILORING (within data integrity constraints):
1. Tailor the summary to directly address this specific role's requirements - mention relevant current projects if applicable
2. Keep each company's bullets with that company - NEVER transfer achievements between companies
3. REPHRASE bullets to emphasize relevant skills, but the underlying facts must match the original
4. Incorporate the additional context the candidate provided
5. QUANTIFY EVERYTHING - Include $ amounts, %, revenue impact, time saved, users served, AUM, team sizes, etc.
6. Frame achievements in terms of BUSINESS IMPACT - revenue generated, costs saved, efficiency gains, scale achieved
7. INCLUDE ALL ${profile.experiences.length} EXPERIENCES - use EXACTLY ${maxBullets} bullets per job (no more, no less). Pick the most compelling achievements for the target role.
8. Output experiences in the EXACT SAME ORDER as the input (experience #1 first, #2 second, etc.)
9. If current projects are highly relevant, mention them in the summary (e.g., "Currently building [project] demonstrating [skill]")
10. MUST FIT ON ONE PAGE. With ${expCount} experiences at ${maxBullets} bullets each, keep bullets concise (1-2 lines max). Summary should be 2 sentences max.

Also provide a "customizations" array listing the specific changes made for this role.

BEFORE OUTPUTTING:
1. COUNT your experiences array. It MUST have exactly ${profile.experiences.length} entries.
2. Verify these companies are ALL present: ${profile.experiences.map(e => e.company).join(', ')}
3. Verify each experience's bullets match the ORIGINAL company from the input.
4. Verify the education field is populated with the candidate's actual education data.

Output JSON in this exact structure:
{
  "name": "Lori Corpuz",
  "summary": "Tailored 2-3 sentence summary based ONLY on actual experience - do not claim years of experience beyond what the work history shows...",
  "customizations": [
    "Rephrased [Company] bullets to emphasize [skill] (facts unchanged)",
    "Referenced current project [X] in summary"
  ],
  "experiences": [
    {
      "company": "MUST match original company name exactly",
      "type": "Company Type",
      "location": "City, ST",
      "role": "Job Title - MUST match original",
      "period": "MUST match original period exactly",
      "bullets": [
        "Rephrased bullet that describes work ACTUALLY done at THIS company",
        "Another bullet - facts must match the original input for this company",
        "Third bullet - never transfer achievements from other companies"
      ]
    }
  ],
  "skills": ["Skill 1", "Skill 2", "Skill 3"],
  "activities": "Copy EXACTLY from CANDIDATE'S ACTIVITIES above, or empty string if not provided",
  "education": "Copy EXACTLY from CANDIDATE'S EDUCATION above, or empty string if not provided"
}`
          }]
        })
      });
      
      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const resumeData = JSON.parse(jsonMatch[0]);
        setGeneratedResume(resumeData);
        // Save to job ledger for later retrieval
        setJobLedger(prev => prev.map(j =>
          j.id === currentJobId ? { ...j, generatedResume: resumeData } : j
        ));
        // Persist to database (don't await - UI already updated)
        updateJobInDb(currentJobId, { generatedResume: resumeData })
          .then(() => console.log('✅ Resume saved to database for job:', currentJobId))
          .catch(err => console.error('Failed to save resume to DB:', err));
      } else {
        throw new Error('No JSON found');
      }
    } catch (e) {
      console.error('Resume generation failed:', e);
      // Fallback to profile data - include ALL experiences
      const fallbackResume = {
        name: profile.name,
        summary: profile.summary,
        experiences: profile.experiences.map(exp => ({
          company: exp.company,
          type: exp.type || "",
          location: exp.location || "",
          role: exp.role,
          period: exp.period,
          bullets: (exp.bullets || exp.achievements)?.slice(0, 3) || []
        })),
        skills: profile.skills || [],
        activities: profile.activities || '',
        education: Array.isArray(educationData) && educationData.length > 0
          ? educationData.join(', ')
          : (typeof educationData === 'string' && educationData ? educationData : '')
      };
      setGeneratedResume(fallbackResume);
      setJobLedger(prev => prev.map(j =>
        j.id === currentJobId ? { ...j, generatedResume: fallbackResume } : j
      ));
      // Persist fallback to database (don't await - UI already updated)
      updateJobInDb(currentJobId, { generatedResume: fallbackResume })
        .then(() => console.log('✅ Fallback resume saved to database for job:', currentJobId))
        .catch(err => console.error('Failed to save fallback resume to DB:', err));
    }
  };

  // Download resume as text file
  const downloadResumeText = () => {
    if (!generatedResume) return;
    const text = `${generatedResume.name}

${generatedResume.summary}

RELEVANT EXPERIENCE
${generatedResume.experiences?.map(exp => 
`${exp.company}${exp.type ? ` (${exp.type})` : ''}${exp.location ? `, ${exp.location}` : ''} — ${exp.role} | ${exp.period}
${exp.bullets?.map(b => `• ${b}`).join('\n')}`
).join('\n\n')}

SKILLS: ${Array.isArray(generatedResume.skills) ? generatedResume.skills.join(', ') : generatedResume.skills}
${generatedResume.activities ? `ACTIVITIES: ${generatedResume.activities}` : ''}
EDUCATION: ${generatedResume.education}`;
    
    // Use data URL as fallback for sandboxed environments
    const dataUrl = 'data:text/plain;charset=utf-8,' + encodeURIComponent(text);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${generatedResume.name.replace(/\s+/g, '_')}_Resume.txt`;
    a.click();
  };
  
  // Copy resume text to clipboard
  const copyResumeText = async () => {
    if (!generatedResume) return;
    const text = `${generatedResume.name}

${generatedResume.summary}

RELEVANT EXPERIENCE
${generatedResume.experiences?.map(exp => 
`${exp.company}${exp.type ? ` (${exp.type})` : ''}${exp.location ? `, ${exp.location}` : ''} — ${exp.role} | ${exp.period}
${exp.bullets?.map(b => `• ${b}`).join('\n')}`
).join('\n\n')}

SKILLS: ${Array.isArray(generatedResume.skills) ? generatedResume.skills.join(', ') : generatedResume.skills}
${generatedResume.activities ? `ACTIVITIES: ${generatedResume.activities}` : ''}
EDUCATION: ${generatedResume.education}`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (e) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.cssText = 'position:fixed;left:-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Copy failed', err);
      }
      document.body.removeChild(textArea);
    }
  };
  
  // Download resume as HTML (printable)
  const downloadResumeHTML = () => {
    if (!generatedResume) return;
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${generatedResume.name} - Resume</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: letter; margin: 0.5in 0.6in; }
    body { font-family: 'Libre Franklin', Arial, sans-serif; font-size: 10pt; line-height: 1.3; color: #000; background: white; max-width: 8.5in; margin: 0 auto; padding: 0.5in 0.6in; }
    .name { font-size: 18pt; font-weight: 700; text-align: center; margin-bottom: 8px; }
    .summary { text-align: justify; margin-bottom: 12px; font-size: 9.5pt; line-height: 1.35; }
    .section-header { font-weight: 700; font-size: 10pt; border-bottom: 1px solid #000; margin-top: 10px; margin-bottom: 8px; padding-bottom: 2px; }
    .job { margin-bottom: 10px; }
    .job-header { display: flex; justify-content: space-between; margin-bottom: 3px; }
    .job-company { font-weight: 600; }
    .job-title { font-style: italic; }
    .bullets { padding-left: 16px; }
    .bullet { margin-bottom: 2px; text-align: justify; position: relative; padding-left: 12px; }
    .bullet::before { content: "●"; position: absolute; left: 0; font-size: 6pt; top: 2px; }
    .skills-line { margin-top: 10px; font-size: 9.5pt; }
    .skills-label { font-weight: 700; }
    .bottom-section { margin-top: 6px; font-size: 9.5pt; }
  </style>
</head>
<body>
  <div class="name">${generatedResume.name}</div>
  <div class="summary">${generatedResume.summary}</div>
  <div class="section-header">RELEVANT EXPERIENCE</div>
  ${generatedResume.experiences?.map(exp => `
    <div class="job">
      <div class="job-header">
        <span><span class="job-company">${exp.company}</span>${exp.type ? ` (${exp.type})` : ''}${exp.location ? `, ${exp.location}` : ''} — <span class="job-title">${exp.role}</span></span>
        <span>${exp.period}</span>
      </div>
      <div class="bullets">
        ${exp.bullets?.map(bullet => `<div class="bullet">${bullet}</div>`).join('') || ''}
      </div>
    </div>
  `).join('') || ''}
  <div class="skills-line"><span class="skills-label">SKILLS:</span> ${Array.isArray(generatedResume.skills) ? generatedResume.skills.join(', ') : generatedResume.skills}</div>
  ${generatedResume.activities ? `<div class="bottom-section"><span class="skills-label">ACTIVITIES:</span> ${generatedResume.activities}</div>` : ''}
  <div class="bottom-section"><span class="skills-label">EDUCATION:</span> ${generatedResume.education}</div>
</body>
</html>`;
    
    // Use data URL for sandboxed environments
    const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(html);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${generatedResume.name.replace(/\s+/g, '_')}_Resume.html`;
    a.click();
  };

  // Generate deferred projects
  const generateDeferredProjects = async (analysis, answers, jobId = null) => {
    const targetJobId = jobId || currentJobId;
    const gaps = analysis.gaps?.filter(g => g.importance === 'critical' || g.importance === 'important') || [];
    const gapSkills = gaps.map(g => g.skill).join(', ') || 'general skills';
    
    // Generate fallback projects first (sync, guaranteed)
    const fallbackProjects = [
      { 
        timeframe: "2 days", 
        name: `Quick Demo: ${gaps[0]?.skill || 'Skill'} Integration`, 
        description: `Build a focused demo integrating ${gapSkills} into your existing work. Start with a minimal proof-of-concept that you can show in interviews.`, 
        extendsExperience: profile.experiences?.[0] ? `${profile.experiences[0].role} at ${profile.experiences[0].company}` : "Your existing projects",
        targetGaps: gaps.map(g => g.skill).slice(0, 3), 
        techStack: gaps.map(g => g.skill).slice(0, 3),
        deliverable: "Working demo + GitHub repo + README",
        resumeBullet: `Built ${gaps[0]?.skill || 'demo'} proof-of-concept demonstrating [specific capability]`,
        salaryImpact: 12000,
        steps: ["Day 1: Set up project, implement core functionality", "Day 2: Polish, document, deploy"] 
      },
      { 
        timeframe: "7 days", 
        name: `Production-Ready ${gaps[0]?.skill || 'Full Stack'} Project`, 
        description: `Comprehensive project demonstrating ${gapSkills} with production-grade architecture, testing, and deployment.`, 
        extendsExperience: profile.experiences?.[0] ? `${profile.experiences[0].role} at ${profile.experiences[0].company}` : "Your existing experience",
        targetGaps: gaps.map(g => g.skill), 
        techStack: [...gaps.map(g => g.skill).slice(0, 3), "Docker", "CI/CD"],
        deliverable: "Deployed app + architecture doc + case study",
        resumeBullet: `Architected and deployed [project] using ${gaps[0]?.skill || 'modern stack'} serving [X users/requests]`,
        salaryImpact: 20000,
        steps: ["Days 1-2: Architecture & setup", "Days 3-5: Core implementation", "Days 6-7: Polish, deploy, document"] 
      }
    ];
    
    // Set fallback immediately so we always have something
    setSuggestedProjects(fallbackProjects);
    setJobLedger(prev => prev.map(j =>
      j.id === targetJobId ? { ...j, suggestedProjects: fallbackProjects } : j
    ));
    // Persist fallback projects to database (don't await - UI already updated)
    updateJobInDb(targetJobId, { suggestedProjects: fallbackProjects })
      .catch(err => console.error('Failed to save fallback projects to DB:', err));
    
    // Try to get better suggestions from API
    try {
      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          messages: [{
            role: "user",
            content: `The candidate was deferred for this role due to skill gaps. Suggest HIGH-LEVERAGE projects that BUILD ON their existing experience.

ROLE: ${analysis.role} at ${analysis.company}
CRITICAL GAPS: ${gaps.map(g => g.skill).join(', ')}

CANDIDATE'S CURRENT SKILLS & STRENGTHS:
${profile.skills?.join(', ')}

CANDIDATE'S EXPERIENCE (build projects that extend this work):
${profile.experiences?.slice(0, 3).map(e => `- ${e.role} at ${e.company}: ${e.bullets?.[0]}`).join('\n')}

CANDIDATE'S ADDITIONAL CONTEXT:
${JSON.stringify(answers, null, 2)}

CANDIDATE'S FEEDBACK/CORRECTIONS:
${currentFeedback || 'None provided'}

ALL DISCOVERED INSIGHTS ABOUT THE CANDIDATE:
${JSON.stringify(discoveredInsights, null, 2)}

Generate exactly 2 projects:
1. A 2-DAY SPRINT: Quick, focused project for immediate skill demonstration
2. A 7-DAY DEEP DIVE: Comprehensive project for thorough skill development

Both should:
- EXTEND their existing work (e.g., add Kubernetes to their options platform, add LLM to their data pipelines)
- Be completable in the timeframe
- Result in something deployable/demo-able for interviews
- Can be added to their resume with quantifiable impact

Respond in JSON:
{
  "projects": [
    {
      "timeframe": "2 days",
      "name": "project name that extends existing work",
      "description": "what to build - be specific about how it connects to their experience",
      "extendsExperience": "Which of their existing projects/roles this builds on",
      "targetGaps": ["gap1", "gap2"],
      "techStack": ["tech1"],
      "deliverable": "what they'll have at the end",
      "resumeBullet": "How to describe this on resume with metrics",
      "salaryImpact": 15000,
      "steps": ["day 1: ...", "day 2: ..."]
    }
  ]
}`
          }]
        })
      });
      
      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const result = JSON.parse(jsonMatch[0]);
        const projects = result.projects || [];
        if (projects.length > 0) {
          setSuggestedProjects(projects);
          setJobLedger(prev => prev.map(j =>
            j.id === targetJobId ? { ...j, suggestedProjects: projects } : j
          ));
          // Persist AI-generated projects to database (don't await - UI already updated)
          updateJobInDb(targetJobId, { suggestedProjects: projects })
            .then(() => console.log('✅ Suggested projects persisted to database'))
            .catch(err => console.error('Failed to save projects to DB:', err));
        }
      }
    } catch (e) {
      console.log('Using fallback projects (API unavailable)');
      // Fallback already set above, no action needed
    }
  };

  // Mark job as applied
  const markAsApplied = async (jobId) => {
    setJobLedger(prev => prev.map(j =>
      j.id === jobId ? { ...j, status: 'applied', appliedAt: new Date().toISOString() } : j
    ));
    // Persist to database
    await updateJobInDb(jobId, { status: 'applied' });
    console.log('✅ Job marked as applied:', jobId);
  };

  // Update job to any funnel status (used by StatusDropdown)
  const updateJobFunnelStatus = async (jobId, newStatus) => {
    const updates = { status: newStatus };
    // If moving to 'applied' for the first time, set appliedAt
    if (newStatus === 'applied') {
      updates.appliedAt = new Date().toISOString();
    }
    // If rejected, capture the stage it was rejected at
    if (newStatus === 'rejected') {
      const job = jobLedger.find(j => j.id === jobId);
      if (job) updates.rejectedAtStage = job.status;
    }
    setJobLedger(prev => prev.map(j =>
      j.id === jobId ? { ...j, ...updates } : j
    ));
    await updateJobInDb(jobId, updates);
    console.log(`✅ Job ${jobId} status updated to: ${newStatus}`);
  };

  // Delete a job from ledger
  const deleteJob = (jobId) => {
    setJobLedger(prev => prev.filter(j => j.id !== jobId));
    if (currentJobId === jobId) {
      startNewAnalysis();
    }
  };

  // Toggle target job status (max 5)
  const toggleTargetJob = (jobId) => {
    setJobLedger(prev => {
      const job = prev.find(j => j.id === jobId);
      const currentTargets = prev.filter(j => j.isTarget).length;
      
      // If already target, remove it
      if (job?.isTarget) {
        return prev.map(j => j.id === jobId ? { ...j, isTarget: false } : j);
      }
      
      // If not target and under limit, add it
      if (currentTargets < 5) {
        return prev.map(j => j.id === jobId ? { ...j, isTarget: true } : j);
      }
      
      // At limit - don't add
      return prev;
    });
  };

  // Check if profile is complete enough for job analysis
  const isProfileComplete = () => {
    return profile.name && profile.name.trim() !== '' &&
           profile.targetRole && profile.targetRole.trim() !== '';
  };

  // Check if user has uploaded a resume (has work experiences)
  const hasResume = () => {
    return profile.experiences && profile.experiences.length > 0;
  };

  // Reset for new analysis
  const startNewAnalysis = () => {
    // Gate: require profile to be complete before analyzing jobs
    if (!isProfileComplete()) {
      setShowProfile(true);
      setSettingsSection('profile');
      return;
    }

    // Gate: require resume upload before analyzing jobs
    if (!hasResume()) {
      setShowProfile(true);
      setSettingsSection('resume');
      return;
    }

    setCurrentJobId(null);
    setCurrentJD('');
    setCurrentJDUrl('');
    setCurrentJobPostedDate('');
    setCurrentFeedback('');
    setFeedbackSubmitted(false);
    setAnalysisStep('input');
    setCurrentAnalysis(null);
    setGapQuestions([]);
    setGapAnswers({});
    setCurrentQuestionIndex(0);
    setAnswerInput('');
    setGeneratedResume(null);
    setSuggestedProjects([]);
    setActiveView('opportunities');
    // Reset URL parsing state
    setUrlParseStatus(null);
    setShowManualPaste(false);
    setParsedJobTitle('');
    setParsedCompany('');
  };

  // Resume analysis for an existing job
  const resumeAnalysis = async (job) => {
    if (job.status === 'applied') return; // Can't resume completed jobs

    // If this is a summary job, lazy load the full details first
    let fullJob = job;
    if (job._isSummary) {
      console.log('📦 Lazy loading job details for:', job.company, job.role);
      setAnalysisStep('input'); // Show loading state
      setActiveView('opportunities');

      const { data, error } = await getJobDetails(job.id);
      if (error || !data) {
        console.error('Failed to load job details:', error);
        return;
      }

      // Convert and merge with existing summary data
      fullJob = { ...job, ...dbJobToAppJob(data), _isSummary: false };

      // Update the job in the ledger so we don't fetch again
      setJobLedger(prev => prev.map(j => j.id === job.id ? fullJob : j));
      console.log('📦 Job details loaded:', fullJob.company);
    }

    setCurrentJobId(fullJob.id);
    setCurrentJD(fullJob.rawJd || '');
    setCurrentJDUrl(fullJob.url || '');
    setCurrentJobPostedDate(fullJob.jobPostedDate || '');
    setCurrentFeedback(fullJob.feedback || '');
    setFeedbackSubmitted(!!fullJob.feedback);

    // Recompute salaryFit when loading (in case it was incorrectly computed by AI)
    const analysis = fullJob.analysis ? { ...fullJob.analysis } : null;
    if (analysis?.salaryRange) {
      analysis.salaryFit = computeSalaryFit(analysis.salaryRange, profile.targetSalaryMin, profile.targetSalaryMax);
    }
    setCurrentAnalysis(analysis);

    // Restore gap questions from analysis
    const questions = fullJob.analysis?.gaps
      ?.filter(g => g.importance !== 'nice-to-have')
      ?.map(g => ({
        skill: g.skill,
        importance: g.importance,
        question: g.question
      })) || [];
    setGapQuestions(questions);
    setGapAnswers(fullJob.gapAnswers || {});

    // Figure out where they left off
    const answeredCount = Object.keys(fullJob.gapAnswers || {}).length;
    setCurrentQuestionIndex(Math.min(answeredCount, questions.length - 1));
    setAnswerInput('');

    // Restore results if they got that far
    setGeneratedResume(fullJob.generatedResume || null);

    // Load or generate suggested projects for deferred jobs
    if (fullJob.suggestedProjects && fullJob.suggestedProjects.length > 0) {
      setSuggestedProjects(fullJob.suggestedProjects);
    } else if (fullJob.status === 'deferred' && fullJob.analysis) {
      // Generate projects on the fly if missing - pass fullJob.id explicitly
      setSuggestedProjects([]); // Clear first
      generateDeferredProjects(fullJob.analysis, fullJob.gapAnswers || {}, fullJob.id);
    } else {
      setSuggestedProjects([]);
    }

    // Set the right step based on status
    if (fullJob.status === 'ready' || fullJob.status === 'deferred') {
      setAnalysisStep('result');
    } else if (questions.length > 0 && answeredCount < questions.length) {
      setAnalysisStep('questions');
    } else if (fullJob.analysis) {
      setAnalysisStep('questions');
    } else {
      setAnalysisStep('input');
    }

    setActiveView('opportunities');
  };

  // Load more jobs (pagination)
  const loadMoreJobs = async () => {
    if (loadingMoreJobs || !hasMoreJobs) return;

    setLoadingMoreJobs(true);
    console.log('📦 Loading more jobs, offset:', jobLedger.length);

    try {
      const { data, error, totalCount } = await getJobsSummary(userId, 10, jobLedger.length);

      if (error) {
        console.error('Failed to load more jobs:', error);
        setLoadingMoreJobs(false);
        return;
      }

      const newJobs = data.map(dbJobSummaryToApp);
      setJobLedger(prev => [...prev, ...newJobs]);
      setTotalJobCount(totalCount);
      console.log('📦 Loaded', newJobs.length, 'more jobs. Total now:', jobLedger.length + newJobs.length);
    } catch (err) {
      console.error('loadMoreJobs error:', err);
    } finally {
      setLoadingMoreJobs(false);
    }
  };

  // Export all data as JSON
  const exportData = () => {
    const data = {
      exportedAt: new Date().toISOString(),
      version: "1.0",
      profile,
      jobLedger,
      discoveredInsights,
      skillAssessments
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `career-agent-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import data from JSON file
  const importData = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result);
        if (data.jobLedger) setJobLedger(data.jobLedger);
        if (data.discoveredInsights) setDiscoveredInsights(data.discoveredInsights);
        if (data.skillAssessments) setSkillAssessments(data.skillAssessments);
        // Only import preferences, not resume data
        if (data.targetRole) setProfile(p => ({ ...p, targetRole: data.targetRole }));
        if (data.targetSalaryMin) setProfile(p => ({ ...p, targetSalaryMin: data.targetSalaryMin }));
        if (data.targetSalaryMax) setProfile(p => ({ ...p, targetSalaryMax: data.targetSalaryMax }));
        alert(`✓ Imported successfully!\n\n${data.jobLedger?.length || 0} jobs\n${data.discoveredInsights?.length || 0} insights`);
      } catch (err) {
        alert('Error importing file. Make sure it\'s a valid Career Agent export.');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Resume upload: PDF text extraction
  const extractTextFromPDF = async (file) => {
    const pdfjsLib = await import('pdfjs-dist');
    // Use unpkg which mirrors npm packages exactly (CSP allows unpkg.com)
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }

    return fullText;
  };

  // Resume upload: DOCX text extraction
  const extractTextFromDOCX = async (file) => {
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  };

  // Resume upload: TXT file reading
  const extractTextFromTXT = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Resume upload handler
  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setResumeUploadError(null);
    setParsedResumeData(null);

    const extension = file.name.split('.').pop().toLowerCase();

    if (!['pdf', 'txt', 'docx'].includes(extension)) {
      setResumeUploadError('Please upload a PDF, TXT, or DOCX file');
      setResumeUploadStep('error');
      return;
    }

    try {
      setResumeUploadStep('extracting');

      let text = '';
      if (extension === 'pdf') {
        text = await extractTextFromPDF(file);
      } else if (extension === 'docx') {
        text = await extractTextFromDOCX(file);
      } else {
        text = await extractTextFromTXT(file);
      }

      if (!text.trim()) {
        throw new Error('Could not extract text from file. The file may be empty or contain only images.');
      }

      setResumeUploadStep('parsing');

      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `Parse this resume and extract structured data. Be thorough and accurate.

RESUME TEXT:
${text.substring(0, 15000)}

Extract the following and respond in JSON only:
{
  "experiences": [
    {
      "role": "Job Title",
      "company": "Company Name",
      "period": "Start Date - End Date (e.g., Jan 2020 - Present)",
      "achievements": ["Key achievement 1", "Key achievement 2"]
    }
  ],
  "education": [
    {
      "institution": "School/University name",
      "degree": "Degree type and field",
      "year": "Graduation year or date range"
    }
  ],
  "activities": "Volunteer work, board positions, advisorships, memberships (as a single string, comma-separated if multiple)",
  "skills": ["skill1", "skill2", "skill3"],
  "name": "Full Name (if found)",
  "title": "Current/Most Recent Title (if clear)",
  "email": "email@example.com (if found)"
}

IMPORTANT:
- List experiences in STRICT reverse chronological order (most recent first)
- Extract ALL education entries (degrees, certifications, bootcamps)
- Extract activities like board positions, advisorships, volunteer work
- For skills, extract both technical and soft skills mentioned
- Normalize skill names (e.g., "JavaScript" not "JS", "React" not "ReactJS")
- Include 10-20 most relevant skills
- If dates are unclear, use "Unknown" for the period
- Achievements should be concise, action-oriented statements`
          }]
        })
      });

      const data = await response.json();
      const responseText = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);

      if (!jsonMatch) {
        throw new Error('Could not parse resume data');
      }

      const parsed = JSON.parse(jsonMatch[0]);
      setParsedResumeData(parsed);
      setResumeUploadStep('preview');

    } catch (error) {
      console.error('Resume upload failed:', error);
      setResumeUploadError(error.message || 'Failed to process resume');
      setResumeUploadStep('error');
    }

    event.target.value = '';
  };

  // Confirm parsed resume data and update profile
  const confirmResumeData = async () => {
    if (!parsedResumeData) return;

    // Format education from parsed data (convert from array of objects to array of strings for display)
    // IMPORTANT: Only update education/activities if the parser actually extracted them
    // Otherwise we'd wipe existing data with empty arrays
    const newProfile = {
      ...profile,
      experiences: parsedResumeData.experiences || profile.experiences,
      skills: parsedResumeData.skills || profile.skills,
      name: profile.name || parsedResumeData.name || '',
      title: profile.title || parsedResumeData.title || '',
      email: profile.email || parsedResumeData.email || '',
    };

    // Only update education if parser found education data
    if (parsedResumeData.education?.length > 0) {
      newProfile.education = parsedResumeData.education.map(edu => {
        if (typeof edu === 'string') return edu;
        const parts = [];
        if (edu.institution) parts.push(edu.institution);
        if (edu.degree) parts.push(edu.degree);
        if (edu.year) parts.push(edu.year);
        return parts.join(' — ');
      });
    }

    // Only update activities if parser found activities data
    if (parsedResumeData.activities) {
      newProfile.activities = parsedResumeData.activities;
    }

    setProfile(newProfile);

    // Explicitly save to Supabase immediately
    console.log('💾 Saving resume data to Supabase...');
    await saveProfile(newProfile);
    console.log('✅ Resume data saved!');

    setResumeUploadStep(null);
    setParsedResumeData(null);
    setProfileImportMethod(null);

    // Close the profile panel after successful save
    setShowProfile(false);
  };

  // LinkedIn URL parsing handler (uses Gemini Flash)
  const handleLinkedInUrlParse = async () => {
    if (!linkedInUrl.trim()) return;

    setLinkedInError(null);
    setParsedLinkedInData(null);
    setLinkedInStep('parsing');

    try {
      const response = await fetch('/api/parse-linkedin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: linkedInUrl.trim() }),
      });

      const data = await response.json();

      if (data.success && data.profile) {
        setParsedLinkedInData(data.profile);
        setLinkedInStep('preview');
        setShowManualLinkedIn(false);
      } else {
        // URL parsing failed - show manual input option
        setLinkedInError(data.error || 'Could not fetch LinkedIn profile. Try pasting your profile content instead.');
        setShowManualLinkedIn(true);
        setLinkedInStep('input');
      }
    } catch (error) {
      console.error('LinkedIn URL parse error:', error);
      setLinkedInError('Failed to fetch profile. Try pasting your profile content instead.');
      setShowManualLinkedIn(true);
      setLinkedInStep('input');
    }
  };

  // LinkedIn manual text parsing handler (fallback)
  const handleLinkedInTextParse = async () => {
    if (!linkedInText.trim()) return;

    setLinkedInError(null);
    setParsedLinkedInData(null);
    setLinkedInStep('parsing');

    try {
      const response = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 3000,
          messages: [{
            role: "user",
            content: `Parse this LinkedIn profile content and extract structured data. Be thorough and accurate.

LINKEDIN PROFILE CONTENT:
${linkedInText.substring(0, 15000)}

Extract the following and respond in JSON only:
{
  "experiences": [
    {
      "role": "Job Title",
      "company": "Company Name",
      "period": "Start Date - End Date (e.g., Jan 2020 - Present)",
      "achievements": ["Key achievement or responsibility 1", "Key achievement 2"]
    }
  ],
  "skills": ["skill1", "skill2", "skill3"],
  "name": "Full Name",
  "title": "Current Title/Headline",
  "summary": "Professional summary if present"
}

IMPORTANT:
- List experiences in reverse chronological order (most recent first)
- Extract skills from the Skills section and from experience descriptions
- Normalize skill names (e.g., "JavaScript" not "JS")
- Include 10-20 most relevant skills
- For achievements, extract bullet points or key responsibilities mentioned
- If the profile has Featured sections or About section, use that for the summary`
          }]
        })
      });

      const data = await response.json();
      const responseText = data.content?.map(c => c.text || '').join('') || '';
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);

      if (!jsonMatch) {
        throw new Error('Could not parse LinkedIn data');
      }

      const parsed = JSON.parse(jsonMatch[0]);
      setParsedLinkedInData(parsed);
      setLinkedInStep('preview');

    } catch (error) {
      console.error('LinkedIn text parse failed:', error);
      setLinkedInError(error.message || 'Failed to parse LinkedIn profile');
      setLinkedInStep('error');
    }
  };

  // Confirm parsed LinkedIn data and update profile
  const confirmLinkedInData = async () => {
    if (!parsedLinkedInData) return;

    // Format education from parsed data (convert from array of objects to array of strings for display)
    const formattedEducation = parsedLinkedInData.education?.map(edu => {
      if (typeof edu === 'string') return edu;
      const parts = [];
      if (edu.institution) parts.push(edu.institution);
      if (edu.degree) parts.push(edu.degree);
      if (edu.year) parts.push(edu.year);
      return parts.join(' — ');
    }) || profile.education || [];

    const newProfile = {
      ...profile,
      experiences: parsedLinkedInData.experiences || profile.experiences,
      skills: [...new Set([...(profile.skills || []), ...(parsedLinkedInData.skills || [])])],
      name: profile.name || parsedLinkedInData.name || '',
      title: profile.title || parsedLinkedInData.title || '',
      summary: parsedLinkedInData.summary || profile.summary || '',
      education: formattedEducation,
      activities: parsedLinkedInData.activities || profile.activities || ''
    };

    setProfile(newProfile);

    // Explicitly save to Supabase immediately
    console.log('💾 Saving LinkedIn data to Supabase...');
    await saveProfile(newProfile);
    console.log('✅ LinkedIn data saved!');

    setLinkedInStep(null);
    setLinkedInUrl('');
    setLinkedInText('');
    setParsedLinkedInData(null);
    setProfileImportMethod(null);
    setShowManualLinkedIn(false);
  };

  const confidence = currentAnalysis ? calculateConfidence(currentAnalysis, gapAnswers) : 0;

  // Pencil palette
  const pencil = {
    yellow: '#f7c948',
    yellowLight: '#fdf4d7',
    pink: '#d4736a',
    pinkLight: '#f5e1de',
    silver: '#b8b8b8',
    silverLight: '#e8e8e8',
    wood: '#c9a87c',
    woodLight: '#f0e6d8',
    graphite: '#3d3d3d',
    paper: '#fffef9'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: pencil.paper,
      color: pencil.graphite,
      fontFamily: "'IBM Plex Sans', 'Inter', -apple-system, sans-serif",
    }}>
      {/* Confetti celebration when onboarding completes */}
      {showConfetti && (
        <Confetti
          duration={4000}
          onComplete={() => setShowConfetti(false)}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Playfair+Display:wght@600;700&display=swap');
        
        * { box-sizing: border-box; }
        
        /* Career blueprint background - very subtle grid */
        .blueprint-bg {
          background-color: #fffef9;
          background-image:
            /* Main grid lines - very subtle */
            linear-gradient(rgba(201, 168, 124, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201, 168, 124, 0.05) 1px, transparent 1px);
          background-size: 32px 32px;
          background-position: 0 0;
          position: relative;
        }

        /* Decorative blueprint elements - behind content */
        .blueprint-bg::before {
          content: '';
          position: fixed;
          top: 180px;
          right: 60px;
          width: 120px;
          height: 120px;
          border: 2px dashed rgba(201, 168, 124, 0.12);
          border-radius: 50%;
          pointer-events: none;
          z-index: -1;
        }

        .blueprint-bg::after {
          content: '';
          position: fixed;
          bottom: 120px;
          left: 50px;
          width: 80px;
          height: 80px;
          border: 2px dashed rgba(247, 201, 72, 0.1);
          transform: rotate(45deg);
          pointer-events: none;
          z-index: -1;
        }
        
        @media print {
          body * { visibility: hidden; }
          .no-print { display: none !important; }
          #print-preview-modal { background: white !important; padding: 0 !important; position: static !important; }
          #printable-resume, #printable-resume * { visibility: visible; }
          #printable-resume {
            position: absolute;
            left: 0;
            top: 0;
            width: 8.5in;
            min-height: auto !important;
            max-height: 11in !important;
            box-shadow: none !important;
            padding: 0.5in 0.6in !important;
            overflow: hidden !important;
          }
          @page {
            size: letter;
            margin: 0;
          }
        }
        
        .card {
          background: #ffffff;
          border: 1px solid #e8e8e8;
          border-radius: 3px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }
        
        .card-pencil {
          background: #ffffff;
          border: 1px solid #c9a87c;
          border-radius: 3px;
          border-left: 4px solid #f7c948;
        }
        
        .btn {
          padding: 10px 20px;
          border-radius: 3px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 12px;
          border: none;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .btn-primary {
          background: #f7c948;
          color: #3d3d3d;
          border: 2px solid #d4a832;
        }
        .btn-primary:hover { background: #f0bc2d; }
        .btn-primary:disabled { background: #fdf4d7; color: #9a9a9a; cursor: not-allowed; }
        
        .btn-secondary {
          background: #ffffff;
          color: #3d3d3d;
          border: 1px solid #b8b8b8;
        }
        .btn-secondary:hover { background: #f5f5f5; border-color: #9a9a9a; }
        
        .btn-success {
          background: #5a8f5a;
          color: white;
        }
        .btn-success:hover { background: #4a7a4a; }
        
        .btn-pink {
          background: #d4736a;
          color: white;
        }
        .btn-pink:hover { background: #c4635a; }
        
        .input {
          background: #ffffff;
          border: 1px solid #b8b8b8;
          border-radius: 3px;
          padding: 12px 16px;
          color: #3d3d3d;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 14px;
          width: 100%;
        }
        .input:focus {
          outline: none;
          border-color: #f7c948;
          box-shadow: 0 0 0 3px rgba(247, 201, 72, 0.2);
        }
        
        textarea.input {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          line-height: 1.6;
          background: #fffef9;
        }
        
        .tag {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 3px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .tag-yellow { background: #fdf4d7; color: #8b6914; border: 1px solid #f7c948; }
        .tag-pink { background: #f5e1de; color: #943c33; border: 1px solid #d4736a; }
        .tag-silver { background: #f0f0f0; color: #5a5a5a; border: 1px solid #b8b8b8; }
        .tag-wood { background: #f0e6d8; color: #7a5c2e; border: 1px solid #c9a87c; }
        .tag-green { background: #e0efe0; color: #3d6b3d; border: 1px solid #5a8f5a; }
        .tag-red { background: #fce8e8; color: #943c33; }
        .tag-gray { background: #f5f5f5; color: #5a5a5a; }
        
        .tag-blue { background: #fdf4d7; color: #8b6914; border: 1px solid #f7c948; }
        .tag-purple { background: #f5e1de; color: #943c33; border: 1px solid #d4736a; }
        
        .status-applied { background: #3b82f6; color: white; }
        .status-ready { background: #f7c948; color: #3d3d3d; font-weight: 700; }
        .status-deferred { background: #d4736a; color: white; }
        .status-response_received { background: #8b5cf6; color: white; }
        .status-phone_screen { background: #a855f7; color: white; }
        .status-interview { background: #f59e0b; color: white; }
        .status-final_round { background: #f97316; color: white; }
        .status-offer { background: #22c55e; color: white; }
        .status-rejected { background: #ef4444; color: white; }
        .status-withdrawn { background: #6b7280; color: white; }
        .status-analyzing { background: #c9a87c; color: white; }
        
        .nav-btn {
          padding: 12px 20px;
          background: transparent;
          border: none;
          color: #7a7a7a;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          border-bottom: 3px solid transparent;
          transition: all 0.15s;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'IBM Plex Sans', sans-serif;
        }
        .nav-btn:hover { color: #3d3d3d; }
        .nav-btn.active { 
          color: #3d3d3d; 
          border-bottom-color: #f7c948;
        }
        
        .section-title {
          font-family: 'Playfair Display', serif;
          font-weight: 600;
          font-size: 18px;
          color: #3d3d3d;
          margin: 0;
          padding-bottom: 8px;
          border-bottom: 3px solid #f7c948;
          display: inline-block;
        }
        
        .section-subtitle {
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 11px;
          color: #7a7a7a;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 4px;
        }
        
        .progress-ring {
          transform: rotate(-90deg);
        }
        
        .fade-in {
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        pre {
          background: #fffef9;
          padding: 16px;
          border-radius: 3px;
          overflow-x: auto;
          font-family: 'IBM Plex Mono', monospace;
          font-size: 12px;
          line-height: 1.6;
          border: 1px solid #c9a87c;
          border-left: 4px solid #f7c948;
        }
        
        .ledger-row {
          display: grid;
          grid-template-columns: 28px 1fr 70px 45px 50px 100px;
          gap: 8px;
          padding: 12px 16px;
          align-items: center;
          border-bottom: 1px solid #e8e8e8;
          font-size: 13px;
        }
        .ledger-row:last-child { border-bottom: none; }
        .ledger-row:hover { background: #fdf4d7; }

        /* Intermediate desktop breakpoint - narrower desktop but not mobile */
        @media (max-width: 1000px) and (min-width: 769px) {
          .mobile-header-container {
            padding: 20px 16px 24px !important;
            min-height: 120px !important;
          }
          .mobile-header-wrapper {
            min-height: 80px !important;
          }
          .mobile-header-top {
            position: absolute !important;
            right: 0 !important;
            top: 0 !important;
            bottom: 8px !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            align-items: flex-end !important;
            gap: 8px !important;
          }
          .mobile-header-logo {
            gap: 6px !important;
          }
          .mobile-header-logo svg {
            width: 44px !important;
            height: 44px !important;
          }
          .mobile-header-logo .logo-text {
            font-size: 16px !important;
          }
          .mobile-header-logo .logo-subtitle {
            font-size: 9px !important;
            letter-spacing: 0.5px !important;
          }
          .mobile-header-center h1 {
            font-size: 28px !important;
          }
          .mobile-header-center .target-role {
            font-size: 12px !important;
            letter-spacing: 2px !important;
          }
          .mobile-header-center .target-salary {
            font-size: 11px !important;
          }
          .mobile-header-actions {
            margin-top: auto !important;
          }
          .mobile-header-actions button {
            padding: 5px 10px !important;
            font-size: 10px !important;
          }
        }

        /* Narrower intermediate - buttons get smaller to avoid overlap */
        @media (max-width: 900px) and (min-width: 769px) {
          .mobile-header-actions button {
            padding: 4px 8px !important;
            font-size: 9px !important;
          }
          .mobile-header-center h1 {
            font-size: 24px !important;
          }
        }

        @media (max-width: 850px) and (min-width: 769px) {
          .mobile-header-actions button {
            padding: 3px 6px !important;
            font-size: 8px !important;
          }
          .mobile-header-logo svg {
            width: 36px !important;
            height: 36px !important;
          }
          .mobile-header-logo .logo-text {
            font-size: 14px !important;
          }
          .mobile-header-logo .logo-subtitle {
            font-size: 8px !important;
          }
          .mobile-header-center h1 {
            font-size: 22px !important;
          }
          .mobile-header-center .target-role {
            font-size: 10px !important;
            letter-spacing: 1.5px !important;
          }
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          /* Header mobile layout - keep same structure, just smaller */
          .mobile-header-container {
            padding: 16px 12px 20px !important;
            min-height: 100px !important;
          }
          .mobile-header-wrapper {
            min-height: 70px !important;
          }
          /* Logo section - stays right-aligned, column layout with buttons below */
          .mobile-header-top {
            position: absolute !important;
            right: 0 !important;
            top: 0 !important;
            bottom: 4px !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            align-items: flex-end !important;
            gap: 6px !important;
          }
          .mobile-header-logo {
            gap: 6px !important;
          }
          .mobile-header-logo svg {
            width: 36px !important;
            height: 36px !important;
          }
          .mobile-header-logo .logo-text {
            font-size: 14px !important;
          }
          .mobile-header-logo .logo-subtitle {
            font-size: 8px !important;
            letter-spacing: 0.3px !important;
            gap: 4px !important;
          }
          .mobile-header-logo .logo-subtitle span {
            font-size: 7px !important;
            padding: 1px 4px !important;
          }
          /* Center section: name, role, salary - stays centered */
          .mobile-header-center {
            position: absolute !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            text-align: center !important;
          }
          .mobile-header-center h1 {
            font-size: 24px !important;
            margin-bottom: 2px !important;
          }
          .mobile-header-center .target-role {
            font-size: 10px !important;
            letter-spacing: 1.5px !important;
          }
          .mobile-header-center .target-salary {
            font-size: 10px !important;
            margin-top: 4px !important;
          }
          /* Actions stay at bottom of right column */
          .mobile-header-actions {
            gap: 4px !important;
          }
          .mobile-header-actions button {
            padding: 4px 6px !important;
            font-size: 9px !important;
            white-space: nowrap !important;
          }

          /* Navigation tabs mobile */
          .nav-btn {
            padding: 10px 6px !important;
            font-size: 9px !important;
            letter-spacing: 0.2px !important;
            flex: 1 !important;
            text-align: center !important;
          }
          .mobile-nav-container {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }

          /* Job cards - compact single row on mobile */
          .ledger-header {
            display: grid !important;
            grid-template-columns: 24px 1fr 50px 36px 36px 60px !important;
            font-size: 8px !important;
            padding: 8px 10px !important;
          }
          .ledger-row {
            display: grid !important;
            grid-template-columns: 24px 1fr 50px 36px 36px 60px !important;
            gap: 4px !important;
            padding: 10px !important;
            font-size: 11px !important;
            flex-wrap: nowrap !important;
            margin-bottom: 0 !important;
            border-radius: 0 !important;
            border: none !important;
            border-bottom: 1px solid #e8e8e8 !important;
            background: transparent !important;
          }
          .ledger-row:last-child { border-bottom: none !important; margin-bottom: 0 !important; }
          .ledger-row > div:nth-child(1) {
            width: auto !important;
            flex-shrink: 0 !important;
            font-size: 12px !important;
          }
          .ledger-row > div:nth-child(2) {
            flex: 1 !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }
          .ledger-row > div:nth-child(2) > div:first-child {
            font-size: 11px !important;
            display: flex !important;
            align-items: center !important;
            gap: 3px !important;
          }
          .ledger-row > div:nth-child(2) > div:first-child > span:first-child {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
          }
          .ledger-row > div:nth-child(2) > div:first-child > a,
          .ledger-row > div:nth-child(2) > div:first-child > span:not(:first-child) {
            flex-shrink: 0 !important;
          }
          .ledger-row > div:nth-child(2) > div:last-child {
            font-size: 9px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .ledger-row > div:nth-child(3),
          .ledger-row > div:nth-child(4),
          .ledger-row > div:nth-child(5) {
            display: block !important;
            font-size: 10px !important;
            text-align: center !important;
          }
          .ledger-row > div:nth-child(6) {
            width: auto !important;
            margin-top: 0 !important;
            padding-top: 0 !important;
            border-top: none !important;
            justify-content: center !important;
          }
          .mobile-job-meta {
            display: none !important;
          }

          /* Job list scroll container */
          .mobile-job-scroll-container {
            max-height: 400px !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }

          /* Stats on one line */
          .stats-grid-4 { grid-template-columns: repeat(4, 1fr) !important; gap: 8px !important; }
          .stats-grid-4 .card { padding: 8px 4px !important; }
          .stats-grid-4 .card > div:first-child { font-size: 9px !important; }
          .stats-grid-4 .card > div:last-child { font-size: 18px !important; }
        }

        @media (max-width: 480px) {
          .card { padding: 12px !important; }
          .mobile-header-container {
            padding: 12px 10px 16px !important;
            min-height: 90px !important;
          }
          .mobile-header-wrapper {
            min-height: 60px !important;
          }
          .mobile-header-logo svg {
            width: 28px !important;
            height: 28px !important;
          }
          .ledger-header {
            grid-template-columns: 20px 1fr 44px 32px 32px 50px !important;
            font-size: 7px !important;
            padding: 6px 8px !important;
          }
          .ledger-row {
            grid-template-columns: 20px 1fr 44px 32px 32px 50px !important;
            padding: 8px !important;
            font-size: 10px !important;
          }
          .mobile-header-center h1 {
            font-size: 18px !important;
          }
          .mobile-header-center .target-role {
            font-size: 8px !important;
            letter-spacing: 1px !important;
          }
          .mobile-header-center .target-salary {
            font-size: 8px !important;
          }
          .mobile-header-actions button {
            padding: 3px 5px !important;
            font-size: 8px !important;
          }
          .mobile-header-logo .logo-text {
            font-size: 12px !important;
          }
          .mobile-header-logo .logo-subtitle {
            font-size: 7px !important;
            letter-spacing: 0.2px !important;
            gap: 3px !important;
          }
          .mobile-header-logo .logo-subtitle span {
            font-size: 6px !important;
            padding: 1px 3px !important;
          }
          .stats-grid-4 .card > div:last-child { font-size: 16px !important; }
          /* Gap analysis mobile adjustments */
          .match-score-banner {
            padding: 16px !important;
          }
          .match-score-banner .score {
            font-size: 40px !important;
          }
          .match-score-banner .label {
            font-size: 12px !important;
          }
          .question-card {
            padding: 16px !important;
          }
        }

        .mobile-job-meta { display: none; }

        .job-link {
          color: #8b6914;
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
        }
        .job-link:hover { 
          color: #6b5210;
          text-decoration: underline;
        }
        
        .question-card {
          background: linear-gradient(135deg, #fffef9 0%, #fdf4d7 100%);
          border: 1px solid #c9a87c;
          border-left: 4px solid #f7c948;
          border-radius: 3px;
          padding: 24px;
        }
        
        .hr-pencil {
          border: none;
          height: 2px;
          background: linear-gradient(90deg, #f7c948, #c9a87c, transparent);
          margin: 16px 0;
        }
        
        /* Ferrule divider - the silver band */
        .ferrule {
          height: 4px;
          background: linear-gradient(180deg, #d0d0d0, #b8b8b8, #a0a0a0, #b8b8b8, #d0d0d0);
          margin: 0;
        }
      `}</style>
      
      {/* Header - Resume centered layout */}
      <div className="mobile-header-container" style={{
        background: '#ffffff',
        borderBottom: '3px solid #3d3d3d',
        padding: '32px 20px 40px',
        minHeight: '140px'
      }}>
        <div className="mobile-header-wrapper" style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', minHeight: '90px' }}>

          {/* Top row: Logo on left, tier badge on right - for mobile */}
          <div className="mobile-header-top" style={{
            position: 'absolute',
            right: 0,
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '12px'
          }}>
            <div className="mobile-header-logo" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Pencil + Manifold combined logo - animates when loading */}
              {dataLoading && (
                <style>{`
                  @keyframes logoDrawLine {
                    0% { stroke-dashoffset: 50; }
                    50% { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -50; }
                  }
                  @keyframes logoPencilBob {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-3px); }
                  }
                  @keyframes logoDotPulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                  }
                `}</style>
              )}
              <svg width="60" height="60" viewBox="0 0 52 52" fill="none">
                {/* Manifold curves - animate when loading */}
                <path
                  d="M8 38 Q16 28, 22 32 T34 26"
                  stroke="#3d3d3d"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.8"
                  {...(dataLoading && {
                    strokeDasharray: "50",
                    strokeDashoffset: "0",
                    style: { animation: 'logoDrawLine 2s ease-in-out infinite' }
                  })}
                />
                <path
                  d="M6 44 Q14 32, 20 38 T32 30"
                  stroke="#3d3d3d"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.4"
                  {...(dataLoading && {
                    strokeDasharray: "50",
                    strokeDashoffset: "0",
                    style: { animation: 'logoDrawLine 2s ease-in-out infinite 0.3s' }
                  })}
                />
                {/* Small dot at curve start */}
                <circle
                  cx="8"
                  cy="38"
                  r="2.5"
                  fill="#d4736a"
                  style={dataLoading ? { animation: 'logoDotPulse 1s ease-in-out infinite' } : undefined}
                />

                {/* Pencil - angled with bob animation when loading */}
                <g style={dataLoading ? { animation: 'logoPencilBob 1.5s ease-in-out infinite', transformOrigin: '38px 14px' } : undefined}>
                  <g transform="rotate(-45 38 14)">
                    <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
                    <rect x="44" y="11" width="4" height="6" fill="url(#ferruleGrad2)"/>
                    <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
                    <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
                    <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
                  </g>
                </g>

                <defs>
                  <linearGradient id="ferruleGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d0d0d0"/>
                    <stop offset="50%" stopColor="#909090"/>
                    <stop offset="100%" stopColor="#d0d0d0"/>
                  </linearGradient>
                </defs>
              </svg>
              <div style={{ textAlign: 'right' }}>
                <div className="logo-text" style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  fontFamily: "'Playfair Display', serif",
                  color: '#3d3d3d'
                }}>
                  Manifold
                </div>
                <div className="logo-subtitle" style={{
                  fontSize: '11px',
                  color: '#7a7a7a',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  Career Architecture
                  <span style={{
                    fontSize: '9px',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    background: effectiveTier === 'pro' ? '#f7c948' : '#e5e5e5',
                    color: effectiveTier === 'pro' ? '#000' : '#666',
                    fontWeight: '600'
                  }}>
                    {effectiveTier === 'pro' ? 'PRO' : 'FREE'}
                  </span>
                  {dataLoading && (
                    <span style={{
                      fontSize: '9px',
                      color: '#d4736a',
                      fontWeight: '500',
                      animation: 'logoDotPulse 1s ease-in-out infinite'
                    }}>
                      syncing...
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mobile-header-actions" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button
                onClick={startNewAnalysis}
                className="btn btn-primary"
                style={{ padding: '6px 12px', fontSize: '11px' }}
              >
                + Analyze
              </button>
              <button
                onClick={() => setShowProfile(true)}
                className="btn btn-secondary"
                style={{ padding: '6px 12px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <SettingsIcon size={14} /> Settings
              </button>
              {onSignOut && (
                <button
                  onClick={() => { console.log('Logout button clicked'); onSignOut(); }}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '11px' }}
                >
                  Logout
                </button>
              )}
            </div>
          </div>

          {/* Center - Resume header style (true center, ignoring logo) */}
          <div className="mobile-header-center" style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center'
          }}>
            <h1 style={{
              margin: 0,
              fontSize: '38px',
              fontWeight: '700',
              fontFamily: "'Playfair Display', serif",
              color: '#3d3d3d',
              letterSpacing: '-0.5px'
            }}>
              {profile.name}
            </h1>
            {/* Track toggle dropdown */}
            <div style={{ marginTop: '6px', position: 'relative' }}>
              <button
                onClick={() => profile.roleTracks.length > 1 && setShowTrackSwitcher(!showTrackSwitcher)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: profile.roleTracks.length > 1 ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                }}
              >
                {activeTrack?.label && profile.roleTracks.length > 1 && (
                  <span style={{
                    fontSize: '10px',
                    color: '#9ca3af',
                    fontWeight: '400',
                    letterSpacing: '1px',
                  }}>
                    {activeTrack.label}
                  </span>
                )}
                <span style={{
                  fontSize: '14px',
                  color: '#3d3d3d',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  {profile.targetRole || 'Your Target Role'}
                  {profile.roleTracks.length > 1 && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: showTrackSwitcher ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      <path d="M2 4L5 7L8 4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  )}
                </span>
              </button>
              {(profile.targetSalaryMin > 0 || profile.targetSalaryMax > 0) && (
                <div className="target-salary" style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#7a7a7a',
                  fontFamily: "'IBM Plex Mono', monospace"
                }}>
                  ${(profile.targetSalaryMin/1000).toFixed(0)}K – ${(profile.targetSalaryMax/1000).toFixed(0)}K
                </div>
              )}

              {/* Track switcher dropdown */}
              {showTrackSwitcher && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#fff',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  padding: '8px',
                  zIndex: 100,
                  minWidth: '260px',
                  marginTop: '8px',
                }}>
                  {profile.roleTracks.map(track => (
                    <button
                      key={track.id}
                      onClick={() => {
                        switchTrack(track.id);
                        setShowTrackSwitcher(false);
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        padding: '10px 12px',
                        background: track.id === profile.activeTrackId
                          ? 'rgba(247, 201, 72, 0.15)'
                          : 'transparent',
                        border: track.id === profile.activeTrackId
                          ? '1px solid rgba(247, 201, 72, 0.4)'
                          : '1px solid transparent',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ fontSize: '11px', color: '#7a7a7a' }}>
                        {track.label}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#3d3d3d' }}>
                        {track.targetRole}
                      </span>
                      {(track.targetSalaryMin > 0 || track.targetSalaryMax > 0) && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: "'IBM Plex Mono', monospace" }}>
                          ${(track.targetSalaryMin/1000).toFixed(0)}K – ${(track.targetSalaryMax/1000).toFixed(0)}K
                        </span>
                      )}
                    </button>
                  ))}
                  {profile.roleTracks.length < maxTracks && (
                    <button
                      onClick={() => {
                        setShowTrackSwitcher(false);
                        setShowAddTrackModal(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '12px',
                        color: '#7a7a7a',
                        background: '#f9fafb',
                        border: '1px dashed #d1d5db',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginTop: '4px',
                      }}
                    >
                      + Add Track ({profile.roleTracks.length}/{maxTracks})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation - Blueprint style tabs */}
      <div className="mobile-nav-container" style={{ background: '#ffffff', borderBottom: '1px solid #e5e5e5' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex' }}>
          <button className={`nav-btn ${activeView === 'opportunities' ? 'active' : ''}`} onClick={() => setActiveView('opportunities')}>
            Opportunities ({totalJobCount || jobLedger.length})
          </button>
          <button className={`nav-btn ${activeView === 'interviews' ? 'active' : ''}`} onClick={() => setActiveView('interviews')}>
            Interviews ({interviews.length})
          </button>
{/* Development tab - unlocked for specific users */}
          {(profile?.email === 'loricorpuz@gmail.com' || effectiveTier === 'pro') ? (
            <button className={`nav-btn ${activeView === 'development' ? 'active' : ''}`} onClick={() => setActiveView('development')}>
              Development
            </button>
          ) : (
            <button className="nav-btn" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              Development <span style={{ fontSize: '9px', color: '#7a7a7a', marginLeft: '2px' }}>(Coming Soon!)</span>
            </button>
          )}
        </div>
      </div>
      
      {/* Main Content - Blueprint grid background */}
      <div className="blueprint-bg" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
        
        {/* OPPORTUNITIES VIEW */}
        {activeView === 'opportunities' && (
          <div className="fade-in">

            {/* Subtabs for Tracker, Daily Match, and Job Radar */}
            {analysisStep === null && (
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
                borderBottom: '1px solid #e5e5e5',
                paddingBottom: '12px'
              }}>
                <button
                  onClick={() => setOpportunitiesSubTab('tracker')}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: opportunitiesSubTab === 'tracker' ? '600' : '400',
                    color: opportunitiesSubTab === 'tracker' ? '#d4736a' : '#7a7a7a',
                    background: opportunitiesSubTab === 'tracker' ? 'rgba(212, 115, 106, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Tracker ({totalJobCount || jobLedger.length})
                </button>
                <button
                  onClick={() => setOpportunitiesSubTab('dailymatch')}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: opportunitiesSubTab === 'dailymatch' ? '600' : '400',
                    color: opportunitiesSubTab === 'dailymatch' ? '#22c55e' : '#7a7a7a',
                    background: opportunitiesSubTab === 'dailymatch' ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <SmallSparkleIcon size={14} /> Daily Match
                  <span style={{ fontSize: '9px', color: '#22c55e', fontWeight: '500' }}>NEW</span>
                </button>
                <button
                  onClick={async () => {
                    const { canAccess } = await canAccessJobRadar(userId);
                    if (!canAccess) {
                      setPaywallFeature('job_radar');
                      setShowPaywall(true);
                      return;
                    }
                    setOpportunitiesSubTab('radar');
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: opportunitiesSubTab === 'radar' ? '600' : '400',
                    color: opportunitiesSubTab === 'radar' ? '#f7c948' : '#7a7a7a',
                    background: opportunitiesSubTab === 'radar' ? 'rgba(247, 201, 72, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Job Radar
                  {unreadAlertCount > 0 && (
                    <span style={{
                      background: '#d4736a',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: '600',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      minWidth: '18px',
                      textAlign: 'center',
                    }}>
                      {unreadAlertCount}
                    </span>
                  )}
                  <span style={{ fontSize: '9px', color: '#d4a832', fontWeight: '500' }}>PRO</span>
                </button>
                <button
                  onClick={() => setOpportunitiesSubTab('pipeline')}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: opportunitiesSubTab === 'pipeline' ? '600' : '400',
                    color: opportunitiesSubTab === 'pipeline' ? '#8b5cf6' : '#7a7a7a',
                    background: opportunitiesSubTab === 'pipeline' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  Pipeline
                  <span style={{ fontSize: '9px', color: '#8b5cf6', fontWeight: '500' }}>NEW</span>
                </button>
              </div>
            )}

            {/* JOB TRACKER / DAILY MATCH */}
            {analysisStep === null && (
              <>
                {/* Onboarding checklist - show ABOVE the tracker when profile incomplete */}
                <OnboardingChecklist
                  profile={profile}
                  onOpenSettings={(section) => {
                    setSettingsSection(section);
                    setShowProfile(true);
                  }}
                />

                {/* DAILY MATCH - Swipe interface */}
                {opportunitiesSubTab === 'dailymatch' && (
                  <div className="card" style={{ marginBottom: '20px', minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
                    <SwipeView
                      userId={user?.id}
                      profile={profile}
                      effectiveTier={effectiveTier}
                      onGenerateResume={async (queueItem, jobData) => {
                        try {
                          // First, add the job to jobs table if it's external
                          let jobId = queueItem.jobId;
                          if (!jobId && queueItem.externalJobData) {
                            const { data: newJob } = await addJob(user?.id, {
                              company: jobData.company,
                              role: jobData.title || jobData.role,
                              url: queueItem.sourceUrl,
                              status: 'applied',
                              matchScore: queueItem.preMatchScore,
                              confidence: queueItem.preMatchScore,
                              analysis: {
                                company: jobData.company,
                                role: jobData.title || jobData.role,
                                matchScore: queueItem.preMatchScore,
                                requiredSkills: [],
                                strengths: [],
                                gaps: [],
                              },
                              rawJd: jobData.description,
                            });
                            jobId = newJob?.id;
                          }

                          // Lazy-load education data
                          let educationData = profile.education;
                          if (!educationData || educationData.length === 0) {
                            const { data: eduData } = await getProfileEducation(userId);
                            if (eduData && eduData.length > 0) {
                              educationData = eduData;
                              setProfile(prev => ({ ...prev, education: eduData }));
                            }
                          }
                          const educationStr = Array.isArray(educationData) && educationData.length > 0
                            ? educationData.join(', ')
                            : (typeof educationData === 'string' && educationData ? educationData : 'Not provided');

                          const roleName = jobData.title || jobData.role || profile.targetRole;
                          const companyName = jobData.company || 'the company';
                          const jdSnippet = (jobData.description || '').slice(0, 1500);

                          // Real AI resume generation via /api/anthropic
                          const response = await fetch("/api/anthropic", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              model: "claude-sonnet-4-20250514",
                              max_tokens: 3000,
                              messages: [{
                                role: "user",
                                content: `Generate a tailored 1-page resume for this role. Output ONLY valid JSON, no markdown.

TARGET ROLE: ${roleName} at ${companyName}
JOB DESCRIPTION EXCERPT:
${jdSnippet}

CANDIDATE'S WORK HISTORY (${profile.experiences?.length || 0} total - ALL MUST appear):
${JSON.stringify(profile.experiences || [], null, 2)}

CANDIDATE'S EDUCATION: ${educationStr}
CANDIDATE'S ACTIVITIES: ${profile.activities || 'Not provided'}

CRITICAL RULES:
- Output EXACTLY ${profile.experiences?.length || 0} experiences. NEVER drop any.
- NEVER fabricate experience or transfer bullets between companies.
- Tailor summary and bullet phrasing to highlight skills relevant to this role.
- QUANTIFY achievements where possible ($ amounts, %, users, time saved).
- MUST FIT ONE PAGE: 2-4 impactful bullets per role.
- Include a "customizations" array listing the specific changes made.

Output JSON:
{
  "name": "${profile.name || 'Candidate'}",
  "summary": "Tailored 2-3 sentence summary...",
  "customizations": ["Change 1", "Change 2"],
  "experiences": [{ "company": "...", "role": "...", "period": "...", "location": "...", "bullets": ["..."] }],
  "skills": ["Skill 1", "Skill 2"],
  "education": "${educationStr}",
  "activities": "${profile.activities || ''}"
}`
                              }]
                            })
                          });

                          const data = await response.json();
                          const text = data.content?.map(c => c.text || '').join('') || '';
                          const jsonMatch = text.match(/\{[\s\S]*\}/);

                          let resume;
                          if (jsonMatch) {
                            resume = JSON.parse(jsonMatch[0]);
                          } else {
                            // Fallback to profile data
                            resume = {
                              name: profile.name,
                              summary: profile.summary || `Experienced professional targeting ${profile.targetRole} roles.`,
                              experiences: profile.experiences || [],
                              skills: profile.skills || [],
                              education: educationStr,
                              activities: profile.activities || '',
                              customizations: [`Tailored for ${companyName}`, `Highlighted relevant experience for ${roleName}`],
                            };
                          }

                          // Track resume usage
                          await incrementResumesUsed(user?.id);

                          // Save to job if we have one
                          if (jobId) {
                            updateJobInDb(jobId, { generatedResume: resume }).catch(err =>
                              console.error('Failed to save swipe resume to DB:', err)
                            );
                          }

                          return resume;
                        } catch (err) {
                          console.error('Resume generation error:', err);
                          throw err;
                        }
                      }}
                      onShowPaywall={(feature) => {
                        setPaywallFeature(feature);
                        setShowPaywall(true);
                      }}
                    />
                  </div>
                )}

                {/* PIPELINE - Conversion funnel + weekly learnings */}
                {opportunitiesSubTab === 'pipeline' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <ConversionFunnel jobs={jobLedger} />
                    <WeeklyLearnings jobs={jobLedger} />
                  </div>
                )}

                {/* JOB TRACKER */}
                {opportunitiesSubTab === 'tracker' && (
                  <>
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>Application Tracker</h2>
                      <p className="section-subtitle">Track and manage your job applications</p>
                    </div>
                    <button className="btn btn-primary" onClick={startNewAnalysis} style={{ padding: '8px 16px', fontSize: '11px' }}>
                      + Analyze Job
                    </button>
                  </div>

                  {jobLedger.length === 0 ? (
                    <div style={{ padding: '20px' }}>
                      {/* Simple empty state for users with complete profiles */}
                      {(profile?.name && profile?.targetRole && (profile?.targetSalaryMin > 0 || profile?.targetSalaryMax > 0) && profile?.experiences?.length > 0) && (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', opacity: 0.5 }}><TrackerIcon size={48} /></div>
                          <p style={{ margin: 0, fontSize: '13px' }}>No jobs tracked yet. Paste a job URL above to get started!</p>
                        </div>
                      )}
                    </div>
                  ) : (
                <>
                  <div className="mobile-job-scroll-container">
                  <div className="ledger-row ledger-header" style={{ fontWeight: '500', fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e5e5e5' }}>
                    <div>★</div>
                    <div>Role</div>
                    <div>Date</div>
                    <div>Fit</div>
                    <div>Conf</div>
                    <div></div>
                  </div>
                  {jobLedger.map(job => (
                    <div key={job.id} className="ledger-row" style={{ background: job.isTarget ? 'rgba(212, 115, 106, 0.05)' : 'transparent' }}>
                      <div>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleTargetJob(job.id); }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: 0,
                            color: job.isTarget ? '#d4736a' : '#d1d5db',
                            transition: 'color 0.15s'
                          }}
                          onMouseOver={(e) => !job.isTarget && (e.target.style.color = '#6b7280')}
                          onMouseOut={(e) => !job.isTarget && (e.target.style.color = '#d1d5db')}
                          title={job.isTarget ? 'Remove from targets' : (jobLedger.filter(j => j.isTarget).length >= 5 ? 'Max 5 targets' : 'Add to targets')}
                        >
                          {job.isTarget ? '★' : '☆'}
                        </button>
                      </div>
                      <div style={{ minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontWeight: '500', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: '1 1 auto'
                          }}>
                            {job.role}
                          </span>
                          {job.url && (
                            <a
                              href={job.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                color: '#a5b4fc',
                                fontSize: '12px',
                                textDecoration: 'none',
                                flexShrink: 0,
                                padding: '0 2px'
                              }}
                              title="Open job posting"
                            >
                              ↗
                            </a>
                          )}
                          {job.feedback && (
                            <span style={{ color: '#5a8f5a', cursor: 'help', flexShrink: 0 }} title={job.feedback}>
                              <SmallChatIcon size={12} />
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.company}</div>
                        {/* Mobile-only metadata */}
                        <div className="mobile-job-meta">
                          <div className="mobile-stat">
                            <span className="mobile-stat-label">Date</span>
                            <span className="mobile-stat-value" style={{ color: '#6b7280' }}>
                              {new Date(job.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <div className="mobile-stat">
                            <span className="mobile-stat-label">Fit</span>
                            <span className="mobile-stat-value" style={{ color: job.matchScore >= 80 ? '#5a8f5a' : job.matchScore >= 60 ? '#d4736a' : '#f87171' }}>
                              {job.matchScore}%
                            </span>
                          </div>
                          <div className="mobile-stat">
                            <span className="mobile-stat-label">Conf</span>
                            <span className="mobile-stat-value" style={{ color: job.confidence >= 80 ? '#5a8f5a' : job.confidence >= 60 ? '#d4736a' : '#f87171' }}>
                              {job.confidence ? `${job.confidence}%` : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {new Date(job.addedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div>
                        <span style={{
                          color: job.matchScore >= 80 ? '#5a8f5a' : job.matchScore >= 60 ? '#d4736a' : '#f87171',
                          fontWeight: '500',
                          fontSize: '12px'
                        }}>{job.matchScore}%</span>
                      </div>
                      <div>
                        {job.confidence ? (
                          <span style={{ 
                            color: job.confidence >= 80 ? '#5a8f5a' : job.confidence >= 60 ? '#d4736a' : '#f87171',
                            fontWeight: '500',
                            fontSize: '12px'
                          }}>{job.confidence}%</span>
                        ) : <span style={{ color: '#4b5563' }}>—</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {['applied', 'response_received', 'phone_screen', 'interview', 'final_round', 'offer', 'rejected', 'withdrawn'].includes(job.status) ? (
                          <StatusDropdown
                            status={job.status}
                            onStatusChange={(newStatus) => updateJobFunnelStatus(job.id, newStatus)}
                            compact
                          />
                        ) : (
                          <>
                            <span
                              className={`tag status-${job.status}`}
                              onClick={() => resumeAnalysis(job)}
                              style={{
                                cursor: 'pointer',
                                fontSize: '10px',
                                padding: '2px 6px'
                              }}
                              title="Click to continue"
                            >
                              {job.status === 'ready' ? '→' :
                               job.status === 'deferred' ? '⏸' : '→'}
                            </span>
                            {job.status === 'ready' && (
                              <button
                                className="btn btn-success"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                onClick={(e) => { e.stopPropagation(); markAsApplied(job.id); }}
                              >
                                ✓
                              </button>
                            )}
                          </>
                        )}
                        {job.generatedResume && (
                          <button
                            onClick={(e) => { e.stopPropagation(); resumeAnalysis(job); }}
                            style={{
                              background: 'transparent',
                              border: '1px solid #d1d5db',
                              color: '#a5b4fc',
                              cursor: 'pointer',
                              padding: '2px 5px',
                              borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="View resume"
                          >
                            <ResumeIcon size={12} />
                          </button>
                        )}
                        <button 
                          onClick={(e) => { e.stopPropagation(); deleteJob(job.id); }}
                          style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            color: '#4b5563', 
                            cursor: 'pointer',
                            padding: '2px',
                            fontSize: '10px'
                          }}
                          onMouseOver={(e) => e.target.style.color = '#f87171'}
                          onMouseOut={(e) => e.target.style.color = '#4b5563'}
                          title="Delete"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Load More button */}
                  {hasMoreJobs && (
                    <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid #e5e5e5' }}>
                      <button
                        onClick={loadMoreJobs}
                        disabled={loadingMoreJobs}
                        style={{
                          background: 'transparent',
                          border: '1px solid #d1d5db',
                          color: loadingMoreJobs ? '#9ca3af' : '#6b7280',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: loadingMoreJobs ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {loadingMoreJobs ? 'Loading...' : `Load more (${jobLedger.length} of ${totalJobCount})`}
                      </button>
                    </div>
                  )}
                  </div>
                </>
              )}
            </div>

            {/* Stats */}
            <div className="stats-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginTop: '16px' }}>
              {[
                { label: 'Opportunities', value: totalJobCount || jobLedger.length, color: '#3d3d3d' },
                { label: 'Ready', value: jobLedger.filter(j => j.status === 'ready').length, color: '#f7c948' },
                { label: 'Applied', value: jobLedger.filter(j => j.status === 'applied').length, color: '#5a8f5a' },
                { label: 'Below 80%', value: jobLedger.filter(j => j.status === 'deferred').length, color: '#d4736a' },
              ].map((stat, i) => (
                <div key={i} className="card" style={{ padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>{stat.label}</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: stat.color }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
                  </>
                )}
              </>
            )}


            {/* JOB RADAR SUBTAB (Pro feature) - HIDDEN for now */}
            {false && (
              <div className="fade-in">
                {/* Add Company Form */}
                {showAddCompany ? (
                  <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#2d2d2d' }}>
                      Track a New Company
                    </h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '4px' }}>
                          Company Name *
                        </label>
                        <input
                          type="text"
                          className="input"
                          value={companyForm.companyName}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, companyName: e.target.value }))}
                          placeholder="e.g., Stripe"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '4px' }}>
                          Careers Page URL *
                        </label>
                        <input
                          type="url"
                          className="input"
                          value={companyForm.careersUrl}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, careersUrl: e.target.value }))}
                          placeholder="https://stripe.com/jobs"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#7a7a7a', marginBottom: '4px' }}>
                          Target Roles (optional, comma-separated)
                        </label>
                        <input
                          type="text"
                          className="input"
                          value={companyForm.targetRoles.join(', ')}
                          onChange={(e) => setCompanyForm(prev => ({
                            ...prev,
                            targetRoles: e.target.value.split(',').map(r => r.trim()).filter(Boolean)
                          }))}
                          placeholder="e.g., Software Engineer, Product Manager"
                        />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#5a5a5a' }}>
                        <input
                          type="checkbox"
                          checked={companyForm.trackAllJobs}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, trackAllJobs: e.target.checked }))}
                        />
                        Also notify me about all new job postings
                      </label>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button className="btn btn-primary" onClick={handleAddCompany}>
                          Add Company
                        </button>
                        <button
                          className="btn"
                          onClick={() => {
                            setShowAddCompany(false);
                            setCompanyForm({ companyName: '', careersUrl: '', targetRoles: [], trackAllJobs: true });
                          }}
                          style={{ background: '#f5f5f5', color: '#5a5a5a' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <div style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #e5e5e5',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                          Job Radar
                        </h2>
                        <p className="section-subtitle">
                          Track companies and get notified when they post new jobs
                        </p>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowAddCompany(true)}
                        style={{ padding: '8px 16px', fontSize: '11px' }}
                      >
                        + Track Company
                      </button>
                    </div>

                    {loadingRadar ? (
                      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                        <div style={{ fontSize: '14px' }}>Loading...</div>
                      </div>
                    ) : trackedCompanies.length === 0 ? (
                      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', opacity: 0.5 }}><RadarIcon size={48} /></div>
                        <p style={{ margin: 0, fontSize: '13px' }}>
                          No companies tracked yet. Add a company to get job alerts.
                        </p>
                      </div>
                    ) : (
                      <div style={{ padding: '12px 16px' }}>
                        {trackedCompanies.map(company => (
                          <div
                            key={company.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 0',
                              borderBottom: '1px solid #f0f0f0'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: '500', fontSize: '14px', color: '#2d2d2d' }}>
                                  {company.companyName}
                                </span>
                                {!company.isActive && (
                                  <span style={{
                                    fontSize: '10px',
                                    color: '#9ca3af',
                                    background: '#f5f5f5',
                                    padding: '2px 6px',
                                    borderRadius: '4px'
                                  }}>
                                    Paused
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                {company.targetRoles.length > 0
                                  ? `Watching: ${company.targetRoles.join(', ')}`
                                  : 'Watching all jobs'}
                                {company.lastCheckedAt && (
                                  <> · Last checked: {new Date(company.lastCheckedAt).toLocaleDateString()}</>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <a
                                href={company.careersUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#d4736a', fontSize: '12px', textDecoration: 'none' }}
                              >
                                View Jobs ↗
                              </a>
                              <button
                                onClick={() => handleToggleCompanyActive(company.id, company.isActive)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#9ca3af',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                {company.isActive ? 'Pause' : 'Resume'}
                              </button>
                              <button
                                onClick={() => handleRemoveCompany(company.id)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#d4736a',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Job Alerts Section */}
                <div className="card">
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '15px', color: '#2d2d2d' }}>
                        Recent Job Alerts
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        New openings from your tracked companies
                      </p>
                    </div>
                    {unreadAlertCount > 0 && (
                      <span style={{
                        background: '#d4736a',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '12px'
                      }}>
                        {unreadAlertCount} new
                      </span>
                    )}
                  </div>

                  {jobAlerts.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                      <p style={{ margin: 0, fontSize: '13px' }}>
                        No job alerts yet. We'll notify you when tracked companies post new jobs.
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: '11px' }}>
                        Jobs are checked once daily.
                      </p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {jobAlerts.filter(a => a.status !== 'dismissed').map(alert => (
                        <div
                          key={alert.id}
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            background: alert.isRead ? 'transparent' : 'rgba(212, 115, 106, 0.03)'
                          }}
                          onClick={() => handleMarkAlertRead(alert.id)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {!alert.isRead && (
                                  <span style={{
                                    width: '6px',
                                    height: '6px',
                                    background: '#d4736a',
                                    borderRadius: '50%',
                                    flexShrink: 0
                                  }} />
                                )}
                                <span style={{ fontWeight: '500', fontSize: '13px', color: '#2d2d2d' }}>
                                  {alert.jobTitle}
                                </span>
                                {alert.isTargetMatch && (
                                  <span style={{
                                    fontSize: '9px',
                                    color: '#f7c948',
                                    background: '#fffef0',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                  }}>
                                    TARGET MATCH
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: '#7a7a7a', marginTop: '2px' }}>
                                {alert.companyName}
                                {alert.location && <> · {alert.location}</>}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                Discovered {new Date(alert.discoveredAt).toLocaleDateString()}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {alert.jobUrl && (
                                <a
                                  href={alert.jobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    color: '#d4736a',
                                    fontSize: '11px',
                                    textDecoration: 'none',
                                    padding: '4px 8px',
                                    background: 'rgba(212, 115, 106, 0.1)',
                                    borderRadius: '4px'
                                  }}
                                >
                                  Apply ↗
                                </a>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDismissAlert(alert.id);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#9ca3af',
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                  padding: '0'
                                }}
                                title="Dismiss"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* How it works info */}
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  background: 'rgba(247, 201, 72, 0.1)',
                  borderRadius: '8px',
                  border: '1px dashed rgba(247, 201, 72, 0.3)'
                }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#2d2d2d' }}>
                    How Job Radar Works
                  </h4>
                  <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '12px', color: '#5a5a5a', lineHeight: '1.6' }}>
                    <li>Add company careers pages you want to monitor</li>
                    <li>We check for new job postings once daily</li>
                    <li>Get notified when matching roles are posted</li>
                    <li>Jobs matching your target roles are highlighted</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Analyze UI */}
            {analysisStep !== null && (
              <>
                {/* Back to Ledger */}
                <button 
                  onClick={() => setAnalysisStep(null)} 
                  style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    color: '#9ca3af', 
                    cursor: 'pointer',
                    padding: '0 0 12px',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ← Back to Tracker
                </button>
            
            {/* Step 1: Input JD */}
            {analysisStep === 'input' && (
              <div className="card" style={{ padding: '20px' }}>
                <h2 style={{ margin: '0 0 8px', fontSize: '16px' }}>Source Job Requirements</h2>
                <p style={{ margin: '0 0 20px', color: '#9ca3af', fontSize: '14px' }}>
                  Paste a job URL and I'll analyze your fit, then generate a tailored resume or skill-building plan.
                </p>

                {/* Job Link with Match % button */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#4b5563', marginBottom: '6px' }}>
                    Job Posting URL
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="url"
                      className="input"
                      value={currentJDUrl}
                      onChange={(e) => setCurrentJDUrl(e.target.value)}
                      placeholder="https://jobs.lever.co/company/..."
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={async () => {
                        if (currentJDUrl.trim()) {
                          // Fetch and auto-analyze
                          setUrlParseStatus('parsing');
                          try {
                            const response = await fetch('/api/parse-job-url', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ url: currentJDUrl })
                            });
                            const data = await response.json();
                            if (data.success && data.job) {
                              const jd = `${data.job.title || ''}\n${data.job.company || ''}\n${data.job.location || ''}\n\n${data.job.description || ''}`;
                              setCurrentJD(jd);
                              setParsedCompany(data.job.company || '');
                              if (effectiveTier !== 'pro') {
                                setRemainingFetches(prev => Math.max(0, prev - 1));
                              }
                              setUrlParseStatus('success');
                              // Auto-analyze after fetch - pass JD directly to avoid state timing issues
                              analyzeJobDescription(jd);
                            } else {
                              setUrlParseStatus('failed');
                              setShowManualPaste(true);
                            }
                          } catch (err) {
                            console.error('URL parse error:', err);
                            setUrlParseStatus('failed');
                            setShowManualPaste(true);
                          }
                        } else if (currentJD.trim()) {
                          // If no URL but has pasted JD, analyze directly
                          analyzeJobDescription();
                        }
                      }}
                      disabled={(!currentJDUrl.trim() && !currentJD.trim()) || loading || urlParseStatus === 'parsing'}
                      style={{ minWidth: '100px' }}
                    >
                      {urlParseStatus === 'parsing' ? 'Loading...' : loading ? 'Analyzing...' : 'Match %'}
                    </button>
                  </div>
                  {/* Show remaining fetches for non-Pro users */}
                  {effectiveTier !== 'pro' && remainingFetches > 0 && (
                    <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#7a7a7a' }}>
                      {remainingFetches} auto-fetch{remainingFetches === 1 ? '' : 'es'} remaining this month
                      {effectiveTier === 'free' && (
                        <span> · <span onClick={() => setShowPaywall(true)} style={{ color: '#d4736a', cursor: 'pointer', textDecoration: 'underline' }}>Upgrade for more</span></span>
                      )}
                    </p>
                  )}
                </div>

                {/* Fallback: Paste option - only shows if fetch failed or limit reached */}
                {(urlParseStatus === 'failed' || (effectiveTier !== 'pro' && remainingFetches === 0) || showManualPaste) && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e5e5'
                  }}>
                    <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                      {urlParseStatus === 'failed'
                        ? "Couldn't fetch that URL. Paste the job description instead:"
                        : "Paste the job description manually:"}
                    </p>
                    <textarea
                      className="input"
                      rows={8}
                      value={currentJD}
                      onChange={(e) => setCurrentJD(e.target.value)}
                      placeholder="Paste the full job description here..."
                      style={{ resize: 'vertical', marginBottom: '12px' }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={() => analyzeJobDescription()}
                      disabled={!currentJD.trim() || loading}
                    >
                      {loading ? 'Analyzing...' : 'Match %'}
                    </button>
                  </div>
                )}

                {/* Toggle to show paste option */}
                {!showManualPaste && urlParseStatus !== 'failed' && !(effectiveTier !== 'pro' && remainingFetches === 0) && (
                  <button
                    onClick={() => setShowManualPaste(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      fontSize: '12px',
                      cursor: 'pointer',
                      padding: '8px 0',
                      textDecoration: 'underline'
                    }}
                  >
                    Or paste job description instead
                  </button>
                )}

                {/* Show upgrade prompt for free users */}
                {effectiveTier === 'free' && (
                  <p style={{
                    fontSize: '12px',
                    color: '#7a7a7a',
                    marginTop: '12px',
                    marginBottom: 0
                  }}>
                    <span
                      onClick={() => setShowPaywall(true)}
                      style={{ color: '#d4736a', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Upgrade for interview prep & more
                    </span>
                  </p>
                )}
              </div>
            )}
            
            {/* Step 2: Analyzing */}
            {analysisStep === 'analyzing' && (
              <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{
                  width: '48px', height: '48px',
                  border: '3px solid #e5e5e5',
                  borderTopColor: '#f7c948',
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <h3 style={{ margin: '0 0 8px' }}>Analyzing Job Description...</h3>
                <p style={{ margin: 0, color: '#9ca3af' }}>Extracting requirements and comparing to your profile</p>
              </div>
            )}

            {/* Step 3b: Generating Resume/Projects */}
            {analysisStep === 'generating' && (
              <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{
                  width: '48px', height: '48px',
                  border: '3px solid #e5e5e5',
                  borderTopColor: '#5a8f5a',
                  borderRadius: '50%',
                  margin: '0 auto 20px',
                  animation: 'spin 1s linear infinite'
                }} />
                <h3 style={{ margin: '0 0 8px' }}>Generating Your Results...</h3>
                <p style={{ margin: 0, color: '#9ca3af' }}>Creating your tailored resume and recommendations</p>
              </div>
            )}

            {/* Step 3: Gap Questions */}
            {analysisStep === 'questions' && currentAnalysis && (
              <div>
                {/* Prominent Match Score Banner */}
                <div className="match-score-banner" style={{
                  background: currentAnalysis.matchScore >= 90 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                              currentAnalysis.matchScore >= 80 ? 'linear-gradient(135deg, #5a8f5a 0%, #3d6b3d 100%)' :
                              currentAnalysis.matchScore >= 60 ? 'linear-gradient(135deg, #f7c948 0%, #d4a832 100%)' :
                              'linear-gradient(135deg, #d4736a 0%, #b85450 100%)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '20px',
                  color: 'white',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                    {currentAnalysis.role} at {currentAnalysis.company}
                  </div>
                  <div className="score" style={{ fontSize: '56px', fontWeight: '700', lineHeight: 1 }}>
                    {currentAnalysis.matchScore}%
                  </div>
                  <div className="label" style={{ fontSize: '16px', fontWeight: '600', marginTop: '8px' }}>
                    {currentAnalysis.matchScore >= 90 ? 'Strong Hire Candidate' :
                     currentAnalysis.matchScore >= 80 ? 'Interview Ready' :
                     currentAnalysis.matchScore >= 60 ? 'Good Foundation' : 'Needs Development'}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '12px' }}>
                    {currentAnalysis.matchScore >= 80
                      ? "You're ready to apply! Generate a tailored resume below."
                      : "Let's boost your score! Answer a few questions to see your true match."}
                  </div>
                  {currentAnalysis.matchScore < 80 && (
                    <div style={{
                      marginTop: '12px',
                      fontSize: '11px',
                      opacity: 0.7,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '16px'
                    }}>
                      <span>80% = Interview Ready</span>
                      <span>90% = Strong Hire</span>
                    </div>
                  )}
                </div>

                {/* Analysis Details */}
                <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h2 style={{ margin: '0 0 4px', fontSize: '18px' }}>{currentAnalysis.role}</h2>
                      <p style={{ margin: 0, color: '#9ca3af' }}>
                        {currentAnalysis.company}
                        {currentJDUrl && (
                          <>
                            {' · '}
                            <a
                              href={currentJDUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="job-link"
                            >
                              View Posting ↗
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                    {feedbackSubmitted && (
                      <span className="tag tag-green">Match Updated</span>
                    )}
                  </div>
                  
                  {/* Salary Info - only show if actually provided in JD (must contain $ or numbers) */}
                  {currentAnalysis.salaryRange && 
                   typeof currentAnalysis.salaryRange === 'string' &&
                   /\$|\d{2,}/.test(currentAnalysis.salaryRange) && (
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px 16px', 
                      background: currentAnalysis.salaryFit === 'in-range' ? 'rgba(52, 211, 153, 0.1)' : 
                                  currentAnalysis.salaryFit === 'below-range' ? 'rgba(251, 191, 36, 0.1)' : 
                                  currentAnalysis.salaryFit === 'above-range' ? 'rgba(96, 165, 250, 0.1)' : '#f9fafb',
                      borderRadius: '8px',
                      border: `1px solid ${currentAnalysis.salaryFit === 'in-range' ? 'rgba(52, 211, 153, 0.3)' : 
                                           currentAnalysis.salaryFit === 'below-range' ? 'rgba(251, 191, 36, 0.3)' : 
                                           currentAnalysis.salaryFit === 'above-range' ? 'rgba(96, 165, 250, 0.3)' : '#e5e5e5'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <span style={{ fontSize: '12px', color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><InsightsIcon size={12} /> Listed Salary: </span>
                        <span style={{ fontWeight: '500' }}>{currentAnalysis.salaryRange}</span>
                      </div>
                      {currentAnalysis.salaryFit && currentAnalysis.salaryFit !== 'unknown' && (
                        <span className={`tag ${currentAnalysis.salaryFit === 'in-range' ? 'tag-green' : 
                                               currentAnalysis.salaryFit === 'below-range' ? 'tag-yellow' : 'tag-blue'}`}>
                          {currentAnalysis.salaryFit === 'in-range' ? '✓ In Range' :
                           currentAnalysis.salaryFit === 'below-range' ? '↓ Below Target' : '↑ Above Target'}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div style={{ marginTop: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ fontSize: '13px', color: '#4b5563' }}>{currentAnalysis.overallAssessment}</div>
                  </div>
                  
                  {/* Side-by-Side Comparison */}
                  {(currentAnalysis.strengths?.length > 0 || currentAnalysis.gaps?.length > 0) && (
                    <div style={{
                      marginTop: '16px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '16px'
                    }}>
                      {/* Left Column: Job Requirements */}
                      <div style={{
                        background: '#f9fafb',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #e5e5e5'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#6b7280',
                          marginBottom: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <SmallClipboardIcon size={14} /> Job Requirements
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {/* Show all required skills from both strengths and gaps */}
                          {[
                            ...(currentAnalysis.strengths || []).map(s => ({ skill: s.skill, matched: true })),
                            ...(currentAnalysis.gaps || []).map(g => ({ skill: g.skill, matched: false, importance: g.importance }))
                          ].map((item, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: '#374151'
                            }}>
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: item.matched ? '#10b981' : item.importance === 'critical' ? '#ef4444' : '#f59e0b',
                                flexShrink: 0
                              }} />
                              {item.skill}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Right Column: Your Match */}
                      <div style={{
                        background: '#f0fdf4',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '1px solid #bbf7d0'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#166534',
                          marginBottom: '12px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <SmallProfileIcon size={14} /> Your Profile Match
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {/* Matched Skills */}
                          {currentAnalysis.strengths?.map((s, i) => (
                            <div key={`s-${i}`} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: '#166534'
                            }}>
                              <span style={{ color: '#10b981' }}>✓</span>
                              {s.skill}
                            </div>
                          ))}
                          {/* Gaps */}
                          {currentAnalysis.gaps?.map((g, i) => (
                            <div key={`g-${i}`} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: g.importance === 'critical' ? '#dc2626' : '#d97706'
                            }}>
                              <span>{g.importance === 'critical' ? '✗' : '~'}</span>
                              <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>{g.skill}</span>
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: g.importance === 'critical' ? '#fef2f2' : '#fffbeb',
                                color: g.importance === 'critical' ? '#dc2626' : '#d97706'
                              }}>
                                {g.importance === 'critical' ? 'Gap' : 'Partial'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Relevant Current Projects */}
                  {currentAnalysis.relevantProjects?.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}><SmallRocketIcon size={12} /> RELEVANT CURRENT PROJECTS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {currentAnalysis.relevantProjects.map((p, i) => (
                          <span key={i} className="tag tag-blue">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Human Feedback Box */}
                  <div style={{ marginTop: '20px', padding: '16px', background: feedbackSubmitted ? 'rgba(52, 211, 153, 0.1)' : '#f9fafb', borderRadius: '8px', border: `1px solid ${feedbackSubmitted ? 'rgba(52, 211, 153, 0.3)' : '#e5e5e5'}` }}>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <SmallChatIcon size={14} /> {feedbackSubmitted ? 'FEEDBACK APPLIED' : 'ADD CONTEXT & FEEDBACK'}
                      </span>
                      {feedbackSubmitted && <span className="tag tag-green">✓ Match Updated</span>}
                    </div>
                    <textarea
                      className="input"
                      rows={3}
                      value={currentFeedback}
                      onChange={(e) => { setCurrentFeedback(e.target.value); setFeedbackSubmitted(false); }}
                      placeholder="Add context about gaps, corrections, or things I should know... (e.g., 'I have Kubernetes experience from a side project')"
                      style={{ fontSize: '13px' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        Submit to recalculate your match score
                      </div>
                      <button 
                        className="btn btn-primary" 
                        onClick={submitFeedback}
                        disabled={!currentFeedback.trim() || loading || feedbackSubmitted}
                        style={{ padding: '8px 16px', fontSize: '13px' }}
                      >
                        {loading ? 'Updating...' : feedbackSubmitted ? '✓ Applied' : 'Submit & Recalculate'}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Question Card */}
                <div className="question-card">
                  {/* Skip All Section */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '16px',
                    paddingBottom: '12px',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <button
                      className="btn btn-secondary"
                      onClick={skipAllQuestions}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      Skip All → {confidence >= 80 ? 'Generate Resume' : 'See Increase Match % Plan'}
                    </button>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <span className="tag tag-blue">Question {currentQuestionIndex + 1} of {gapQuestions.length}</span>
                    <span style={{ fontSize: '13px', color: '#4b5563' }}>
                      Gap: <strong>{gapQuestions[currentQuestionIndex]?.skill}</strong>
                    </span>
                  </div>
                  
                  <h3 style={{ margin: '0 0 20px', fontSize: '18px', lineHeight: '1.5' }}>
                    {gapQuestions[currentQuestionIndex]?.question}
                  </h3>
                  
                  <textarea
                    className="input"
                    rows={4}
                    value={answerInput}
                    onChange={(e) => setAnswerInput(e.target.value)}
                    placeholder="Share any relevant experience, even if it's not on your resume..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) submitAnswer();
                    }}
                  />
                  
                  <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>⌘ + Enter to submit</span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={skipQuestion}
                        style={{ opacity: 0.7 }}
                      >
                        Skip
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setAnswerInput("I don't have direct experience with this.");
                        }}
                      >
                        No experience
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={submitAnswer}
                        disabled={!answerInput.trim()}
                      >
                        {currentQuestionIndex < gapQuestions.length - 1 ? 'Next Question →' : 'Finish & Evaluate →'}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Progress */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {gapQuestions.map((_, i) => (
                      <div 
                        key={i}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: i < currentQuestionIndex ? '#f7c948' : i === currentQuestionIndex ? '#a5b4fc' : '#e5e5e5'
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 4: Result */}
            {analysisStep === 'result' && currentAnalysis && (
              <div>
                {/* Confidence Result - Compact for deferred, full for ready */}
                {confidence >= 80 ? (
                  <div className="card" style={{ padding: '24px', textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '72px', height: '72px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #5a8f5a, #6ba36b)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px',
                      boxShadow: '0 0 24px rgba(16, 185, 129, 0.2)'
                    }}>
                      <span style={{ fontSize: '22px', fontWeight: '700', color: 'white' }}>{confidence}%</span>
                    </div>
                    <h2 style={{ margin: '0 0 6px', fontSize: '18px' }}>✓ Ready to Apply!</h2>
                    <p style={{ margin: 0, color: '#4b5563', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', fontSize: '13px' }}>
                      Your match score indicates a strong fit. Your tailored resume is ready below.
                    </p>
                    <div style={{ marginTop: '12px' }}>
                      <span className="tag status-ready" style={{ fontSize: '11px', padding: '4px 10px' }}>READY</span>
                    </div>
                  </div>
                ) : (
                  /* Compact deferred header with option to generate resume */
                  <div className="card" style={{ padding: '12px 16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #d4736a, #e8a0a0)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: 'white' }}>{confidence}%</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallChartIcon size={16} /> Below 80% Match</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>Build skills or generate a resume anyway</div>
                      </div>
                      <span className="tag status-deferred" style={{ fontSize: '11px', padding: '4px 10px' }}>BELOW 80%</span>
                    </div>
                    {/* Generate Resume Anyway option */}
                    {!generatedResume && (
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e5e5' }}>
                        <button
                          className="btn btn-primary"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            background: loading ? '#e8e8e8' : '#d4736a',
                            color: loading ? '#7a7a7a' : 'white',
                            border: 'none',
                            borderRadius: '8px',
                          }}
                          onClick={() => generateResume(currentAnalysis, gapAnswers)}
                          disabled={loading}
                        >
                          {loading ? (
                            <>Generating your tailored resume...</>
                          ) : (
                            <>
                              <SmallResumeIcon /> Generate Tailored Resume Anyway
                            </>
                          )}
                        </button>
                        <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                          Creates an AI-optimized resume tailored to this role
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Resume (if generated - available at any confidence level) */}
                {generatedResume && typeof generatedResume === 'object' && (
                  <div className="card" style={{ marginBottom: '24px', overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><ResumeIcon size={16} /> Resume for {currentAnalysis?.company}</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn btn-primary"
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => setShowPrintPreview(true)}
                        >
                          Print / PDF
                        </button>
                        <button 
                          className="btn btn-success"
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                          onClick={() => {
                            markAsApplied(currentJobId);
                            setAnalysisStep(null);
                          }}
                        >
                          ✓ Applied
                        </button>
                      </div>
                    </div>
                    
                    {/* Customizations Panel */}
                    {generatedResume.customizations && generatedResume.customizations.length > 0 && (
                      <div style={{
                        padding: '12px 16px',
                        background: currentAnalysis?.matchScore >= 80 ? 'rgba(30, 58, 138, 0.08)' : 'rgba(96, 165, 250, 0.1)',
                        borderBottom: '1px solid #e5e5e5'
                      }}>
                        <div style={{ fontSize: '11px', color: currentAnalysis?.matchScore >= 80 ? '#1e3a8a' : '#a5b4fc', marginBottom: '6px', fontWeight: '600' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><SmallSparkleIcon size={12} /> CUSTOMIZED FOR THIS ROLE</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {generatedResume.customizations.map((c, i) => (
                            <span key={i} style={{
                              background: currentAnalysis?.matchScore >= 80 ? 'rgba(30, 58, 138, 0.12)' : 'rgba(96, 165, 250, 0.15)',
                              color: currentAnalysis?.matchScore >= 80 ? '#1e3a8a' : '#93c5fd',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontSize: '11px'
                            }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Resume Preview */}
                    <div style={{ 
                      padding: '30px', 
                      background: 'white', 
                      color: '#000',
                      fontFamily: "'Libre Franklin', Arial, sans-serif",
                      fontSize: '10px',
                      lineHeight: '1.35',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}>
                      {/* Name */}
                      <div style={{ fontSize: '16px', fontWeight: '700', textAlign: 'center', marginBottom: '8px' }}>
                        {generatedResume.name}
                      </div>
                      
                      {/* Summary */}
                      <div style={{ textAlign: 'justify', marginBottom: '10px', fontSize: '9px' }}>
                        {generatedResume.summary}
                      </div>
                      
                      {/* Experience Header */}
                      <div style={{ fontWeight: '700', borderBottom: '1px solid #000', marginBottom: '8px', paddingBottom: '2px', fontSize: '9px' }}>
                        RELEVANT EXPERIENCE
                      </div>
                      
                      {/* Jobs */}
                      {generatedResume.experiences?.map((exp, i) => (
                        <div key={i} style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '9px' }}>
                            <span>
                              <strong>{exp.company}</strong>
                              {exp.type && ` (${exp.type})`}
                              {exp.location && `, ${exp.location}`}
                              {' — '}<em>{exp.role}</em>
                            </span>
                            <span style={{ whiteSpace: 'nowrap', marginLeft: '8px' }}>{exp.period}</span>
                          </div>
                          <div style={{ paddingLeft: '12px' }}>
                            {exp.bullets?.map((bullet, j) => (
                              <div key={j} style={{ marginBottom: '1px', display: 'flex', gap: '6px', fontSize: '9px' }}>
                                <span style={{ fontSize: '5px', marginTop: '3px' }}>●</span>
                                <span style={{ flex: 1 }}>{bullet}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      
                      {/* Skills */}
                      <div style={{ marginTop: '8px', fontSize: '9px' }}>
                        <strong>SKILLS:</strong> {Array.isArray(generatedResume.skills) ? generatedResume.skills.join(', ') : generatedResume.skills}
                      </div>
                      
                      {/* Activities */}
                      {generatedResume.activities && (
                        <div style={{ marginTop: '4px', fontSize: '9px' }}>
                          <strong>ACTIVITIES:</strong> {generatedResume.activities}
                        </div>
                      )}
                      
                      {/* Education */}
                      <div style={{ marginTop: '4px', fontSize: '9px' }}>
                        <strong>EDUCATION:</strong> {generatedResume.education}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Projects (if deferred) - Expandable summaries */}
                {confidence < 80 && (
                  <div className="card" style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <SmallLightbulbIcon size={14} /> Skill-Building Projects
                      </h3>
                      {suggestedProjects.length === 0 && (
                        <button
                          className="btn btn-primary"
                          style={{ padding: '4px 10px', fontSize: '11px' }}
                          onClick={() => generateDeferredProjects(currentAnalysis, gapAnswers)}
                          disabled={loading}
                        >
                          {loading ? '...' : 'Generate Projects'}
                        </button>
                      )}
                    </div>
                    {suggestedProjects.length > 0 && (() => {
                      // Get one 2-day and one 7-day project
                      const quickProject = suggestedProjects.find(p => p.timeframe === '2 days') || suggestedProjects[0];
                      const deepProject = suggestedProjects.find(p => p.timeframe === '7 days') || suggestedProjects[1];
                      const projectsToShow = [quickProject, deepProject].filter(Boolean);

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {projectsToShow.map((project, i) => {
                            const isExpanded = expandedProjectIndex === i;
                            const isQuick = project.timeframe === '2 days';
                            return (
                              <div key={i} style={{
                                border: '1px solid #e5e5e5',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: isExpanded ? '#fafafa' : 'white'
                              }}>
                                {/* Summary Row - Always visible */}
                                <div
                                  onClick={() => setExpandedProjectIndex(isExpanded ? null : i)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '12px 14px',
                                    cursor: 'pointer',
                                    background: isQuick ? 'rgba(59, 130, 246, 0.05)' : 'rgba(34, 197, 94, 0.05)'
                                  }}
                                >
                                  <span className={`tag ${isQuick ? 'tag-blue' : 'tag-green'}`} style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '600' }}>
                                    {project.timeframe}
                                  </span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>{project.name}</div>
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                      {isQuick ? 'Quick win' : 'Deep skill build'} → {project.targetGaps?.[0] || 'Core skills'}
                                    </div>
                                  </div>
                                  <span style={{ color: '#9ca3af', fontSize: '18px', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                                    ▾
                                  </span>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                  <div style={{ padding: '14px', borderTop: '1px solid #e5e5e5', fontSize: '12px' }}>
                                    {project.description && (
                                      <p style={{ margin: '0 0 12px', color: '#4b5563', lineHeight: '1.5' }}>{project.description}</p>
                                    )}

                                    {project.steps && project.steps.length > 0 && (
                                      <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontWeight: '600', fontSize: '11px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>Steps</div>
                                        <ol style={{ margin: 0, paddingLeft: '18px', color: '#374151' }}>
                                          {project.steps.map((step, j) => (
                                            <li key={j} style={{ marginBottom: '4px', lineHeight: '1.4' }}>{step}</li>
                                          ))}
                                        </ol>
                                      </div>
                                    )}

                                    {project.targetGaps && project.targetGaps.length > 0 && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        <span style={{ fontSize: '11px', color: '#6b7280' }}>Skills:</span>
                                        {project.targetGaps.map((gap, j) => (
                                          <span key={j} className="tag" style={{ fontSize: '10px', padding: '2px 6px', background: '#e5e7eb' }}>{gap}</span>
                                        ))}
                                      </div>
                                    )}

                                    {project.resumeBullet && (
                                      <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(90, 143, 90, 0.1)', borderRadius: '6px', borderLeft: '3px solid #5a8f5a' }}>
                                        <div style={{ fontSize: '10px', color: '#5a8f5a', fontWeight: '600', marginBottom: '4px' }}>RESUME BULLET</div>
                                        <div style={{ color: '#374151', fontStyle: 'italic' }}>{project.resumeBullet}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* Action buttons */}
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                  <button className="btn btn-secondary" onClick={() => setAnalysisStep(null)}>
                    ← Back to Tracker
                  </button>
                  <button className="btn btn-primary" onClick={startNewAnalysis}>
                    + Analyze Another Job
                  </button>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* INTERVIEWS VIEW */}
        {activeView === 'interviews' && (
          <div className="fade-in">
            {/* Subtabs for Interviews and Brag Book */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '16px',
              borderBottom: '1px solid #e5e5e5',
              paddingBottom: '12px'
            }}>
              <button
                onClick={() => setInterviewsSubTab('interviews')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: interviewsSubTab === 'interviews' ? '600' : '400',
                  color: interviewsSubTab === 'interviews' ? '#d4736a' : '#7a7a7a',
                  background: interviewsSubTab === 'interviews' ? 'rgba(212, 115, 106, 0.1)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Interviews ({interviews.length})
              </button>
              <button
                onClick={() => setInterviewsSubTab('brag_book')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: interviewsSubTab === 'brag_book' ? '600' : '400',
                  color: interviewsSubTab === 'brag_book' ? '#f7c948' : '#7a7a7a',
                  background: interviewsSubTab === 'brag_book' ? 'rgba(247, 201, 72, 0.1)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Brag Book ({bragBookEntries.length})
              </button>
              <button
                onClick={() => setInterviewsSubTab('emails')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: interviewsSubTab === 'emails' ? '600' : '400',
                  color: interviewsSubTab === 'emails' ? '#0288d1' : '#7a7a7a',
                  background: interviewsSubTab === 'emails' ? 'rgba(2, 136, 209, 0.1)' : 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Emails ({applicationEmails.length})
                {unreadEmailCount > 0 && (
                  <span style={{
                    background: '#0288d1',
                    color: 'white',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}>
                    {unreadEmailCount}
                  </span>
                )}
              </button>
            </div>

            {/* INTERVIEWS SUBTAB */}
            {interviewsSubTab === 'interviews' && (
              <>
            {/* Interview List View */}
            {interviewView === 'list' && !showScheduleInterview && (
              <>
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>Interview Tracker</h2>
                      <p className="section-subtitle">Track interviews by company</p>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowScheduleInterview(true)}
                      style={{ padding: '8px 16px', fontSize: '11px' }}
                    >
                      + Add Interview
                    </button>
                  </div>

                  {interviews.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', opacity: 0.5 }}><InterviewIcon size={48} /></div>
                      <p style={{ margin: 0, fontSize: '13px' }}>No interviews yet. Add one to start preparing.</p>
                    </div>
                  ) : (
                    <div style={{ padding: '0' }}>
                      {/* Group interviews by company */}
                      {Object.entries(
                        interviews.reduce((acc, interview) => {
                          const company = interview.company || 'Unknown Company';
                          if (!acc[company]) {
                            acc[company] = {
                              interviews: [],
                              job: jobLedger.find(j => String(j.id) === String(interview.jobId)),
                              role: interview.role
                            };
                          }
                          acc[company].interviews.push(interview);
                          return acc;
                        }, {})
                      ).map(([company, data]) => {
                        const isExpanded = expandedCompanies[company];
                        const interviewTypes = [...new Set(data.interviews.map(i => i.interviewType))];
                        const latestInterview = data.interviews.sort((a, b) =>
                          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                        )[0];

                        return (
                          <div key={company} style={{ borderBottom: '1px solid #e5e5e5' }}>
                            {/* Company Header - Clickable to expand */}
                            <div
                              onClick={() => setExpandedCompanies(prev => ({ ...prev, [company]: !prev[company] }))}
                              style={{
                                padding: '16px 20px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: isExpanded ? '#fafafa' : 'transparent',
                                transition: 'background 0.15s'
                              }}
                              onMouseOver={(e) => { if (!isExpanded) e.currentTarget.style.background = '#fafafa'; }}
                              onMouseOut={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                  <span style={{ fontWeight: '600', fontSize: '15px' }}>{company}</span>
                                  <span style={{ fontSize: '12px', color: '#7a7a7a' }}>({data.interviews.length} interview{data.interviews.length > 1 ? 's' : ''})</span>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {interviewTypes.map(type => (
                                    <span
                                      key={type}
                                      className="tag"
                                      style={{
                                        background: '#f7c948',
                                        color: '#3d3d3d',
                                        fontSize: '10px',
                                        padding: '2px 8px'
                                      }}
                                    >
                                      {type?.replace('_', ' ')}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '18px', color: '#7a7a7a', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                                  ▼
                                </span>
                              </div>
                            </div>

                            {/* Expanded Content - Interview Prep */}
                            {isExpanded && (
                              <div style={{ padding: '0 20px 20px', background: '#fafafa' }}>
                                {/* Role info */}
                                <div style={{ fontSize: '13px', color: '#5a5a5a', marginBottom: '16px' }}>
                                  <strong>Role:</strong> {data.role || 'Not specified'}
                                </div>

                                {/* Interview stages list */}
                                <div style={{ marginBottom: '16px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Interview Stages
                                  </div>
                                  {data.interviews.map(interview => (
                                    <div
                                      key={interview.id}
                                      onClick={() => { setSelectedInterview(interview); setInterviewView('detail'); }}
                                      style={{
                                        padding: '10px 12px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        marginBottom: '8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        border: '1px solid #e5e5e5'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span className="tag" style={{ background: '#f7c948', color: '#3d3d3d', fontSize: '11px' }}>
                                          {interview.interviewType?.replace('_', ' ')}
                                        </span>
                                        {interview.scheduledAt && (
                                          <span style={{ fontSize: '12px', color: '#7a7a7a' }}>
                                            {new Date(interview.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {interview.prepDocument && <span title="Prep ready" style={{ color: '#5a8f5a' }}><SmallClipboardIcon size={14} /></span>}
                                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>→</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Prep Actions */}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <button
                                    className="btn btn-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedInterview(latestInterview);
                                      setInterviewView('detail');
                                    }}
                                    style={{ fontSize: '12px', padding: '8px 14px' }}
                                  >
                                    View Prep Materials
                                  </button>
                                  <button
                                    className="btn btn-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedJobForInterview(data.job);
                                      setShowScheduleInterview(true);
                                    }}
                                    style={{ fontSize: '12px', padding: '8px 14px' }}
                                  >
                                    + Add Stage
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Add Interview Form */}
            {showScheduleInterview && (
              <div className="card" style={{ marginBottom: '20px' }}>
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e5e5e5',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>Add Interview</h2>
                  <button
                    onClick={() => { setShowScheduleInterview(false); setSelectedJobForInterview(null); }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#7a7a7a' }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ padding: '20px' }}>
                  {/* Select Job */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Select Job *
                    </label>
                    <select
                      className="input"
                      value={selectedJobForInterview?.id || ''}
                      onChange={(e) => {
                        // Compare as strings to handle both UUID strings and temp number IDs
                        const job = jobLedger.find(j => String(j.id) === e.target.value);
                        setSelectedJobForInterview(job);
                      }}
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      <option value="">Choose a job...</option>
                      {jobLedger.filter(j => j.status === 'applied' || j.status === 'ready' || j.status === 'deferred' || j.analysis).map(job => (
                        <option key={job.id} value={job.id}>
                          {job.role} at {job.company} {job.status === 'ready' ? '✓' : job.status === 'applied' ? '📤' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Interview Type */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7a7a7a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Interview Type *
                    </label>
                    <select
                      className="input"
                      value={interviewForm.interviewType}
                      onChange={(e) => setInterviewForm(prev => ({ ...prev, interviewType: e.target.value }))}
                      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                    >
                      <option value="phone_screen">Phone Screen</option>
                      <option value="recruiter">Recruiter Call</option>
                      <option value="technical">Technical</option>
                      <option value="behavioral">Behavioral</option>
                      <option value="system_design">System Design</option>
                      <option value="hiring_manager">Hiring Manager</option>
                      <option value="team_fit">Team Fit / Culture</option>
                      <option value="onsite">Onsite</option>
                      <option value="final">Final Round</option>
                      <option value="offer">Offer Discussion</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => { setShowScheduleInterview(false); setSelectedJobForInterview(null); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={scheduleInterview}
                      disabled={!selectedJobForInterview}
                    >
                      Add Interview
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Interview Detail View */}
            {interviewView === 'detail' && selectedInterview && (
              <div className="fade-in">
                {/* Back button */}
                <button
                  onClick={() => { setInterviewView('list'); setSelectedInterview(null); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#7a7a7a',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  ← Back to Interviews
                </button>

                {/* Interview Header Card */}
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>{selectedInterview.company}</h2>
                          <span className="tag" style={{
                            background: selectedInterview.status === 'completed' ?
                              (selectedInterview.outcome === 'passed' ? '#5a8f5a' :
                               selectedInterview.outcome === 'rejected' ? '#d4736a' :
                               selectedInterview.outcome === 'offer' ? '#f7c948' : '#e5e5e5') :
                              '#f7c948',
                            color: selectedInterview.status === 'completed' && selectedInterview.outcome === 'offer' ? '#3d3d3d' :
                                   selectedInterview.status === 'completed' && selectedInterview.outcome ? 'white' : '#3d3d3d'
                          }}>
                            {selectedInterview.status === 'completed' ? selectedInterview.outcome : selectedInterview.interviewType?.replace('_', ' ')}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: '#7a7a7a', fontSize: '14px' }}>{selectedInterview.role}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>
                          {new Date(selectedInterview.scheduledAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </div>
                        <div style={{ color: '#7a7a7a', fontSize: '14px' }}>
                          {new Date(selectedInterview.scheduledAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {selectedInterview.durationMinutes && ` (${selectedInterview.durationMinutes} min)`}
                        </div>
                        {selectedInterview.meetingLink && (
                          <a
                            href={selectedInterview.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary"
                            style={{ marginTop: '8px', display: 'inline-block', padding: '6px 12px', fontSize: '11px', textDecoration: 'none' }}
                          >
                            Join Meeting
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Contact Info */}
                    {(selectedInterview.recruiterName || selectedInterview.recruiterEmail) && (
                      <div style={{ marginTop: '16px', padding: '12px', background: '#fafafa', borderRadius: '3px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase' }}>Recruiter</div>
                        <div style={{ fontSize: '13px' }}>
                          {selectedInterview.recruiterName}
                          {selectedInterview.recruiterEmail && (
                            <span style={{ color: '#7a7a7a' }}> · <a href={`mailto:${selectedInterview.recruiterEmail}`} style={{ color: '#3d3d3d' }}>{selectedInterview.recruiterEmail}</a></span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Prep Actions */}
                {selectedInterview.status !== 'completed' && (
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Interview Preparation</h3>
                      {effectiveTier === 'pro' && (
                        <span style={{ fontSize: '10px', color: '#d4a832', background: '#fdf4d7', padding: '2px 8px', borderRadius: '10px' }}>
                          Pro: One-sheet + AI Coaching
                        </span>
                      )}
                    </div>
                    <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: effectiveTier === 'pro' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '16px' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => generateSTARExamples(selectedInterview)}
                        disabled={generatingSTAR}
                        style={{ padding: '16px', height: 'auto', flexDirection: 'column', display: 'flex', alignItems: 'flex-start', textAlign: 'left' }}
                      >
                        <div style={{ marginBottom: '8px', opacity: 0.8 }}><SubscriptionIcon size={24} /></div>
                        <div style={{ fontWeight: '600', marginBottom: '4px', color: generatingSTAR ? '#f7c948' : undefined }}>
                          {generatingSTAR ? 'Generating...' : selectedInterview.starExamples?.length > 0 ? 'Regenerate STAR Examples' : 'Generate STAR Examples'}
                        </div>
                        <div style={{ fontSize: '11px', textTransform: 'none', letterSpacing: 'normal', color: '#7a7a7a' }}>
                          5 tailored stories mapped to job requirements
                        </div>
                      </button>

                      {/* Only show generate button if no prep document exists */}
                      {!selectedInterview.prepDocument && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => generatePrepDocument(selectedInterview)}
                          disabled={generatingPrep}
                          style={{ padding: '16px', height: 'auto', flexDirection: 'column', display: 'flex', alignItems: 'flex-start', textAlign: 'left' }}
                        >
                          <div style={{ marginBottom: '8px', opacity: 0.8 }}><TrackerIcon size={24} /></div>
                          <div style={{ fontWeight: '600', marginBottom: '4px', color: generatingPrep ? '#f7c948' : undefined }}>
                            {generatingPrep ? 'Generating...' : 'Generate Prep Document'}
                          </div>
                          <div style={{ fontSize: '11px', textTransform: 'none', letterSpacing: 'normal', color: '#7a7a7a' }}>
                            One-sheet with company info, questions, and strategy
                          </div>
                        </button>
                      )}

                      {/* Pro-only: AI Coaching button */}
                      {effectiveTier === 'pro' && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => startCoachingSession(selectedInterview)}
                          style={{
                            padding: '16px',
                            height: 'auto',
                            flexDirection: 'column',
                            display: 'flex',
                            alignItems: 'flex-start',
                            textAlign: 'left',
                            background: showCoaching ? '#fdf4d7' : undefined,
                            border: showCoaching ? '2px solid #f7c948' : undefined
                          }}
                        >
                          <div style={{ marginBottom: '8px', opacity: 0.8 }}><AnalysisIcon size={24} /></div>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {showCoaching ? 'Coaching Active' : 'Start AI Coaching'}
                          </div>
                          <div style={{ fontSize: '11px', textTransform: 'none', letterSpacing: 'normal', color: '#7a7a7a' }}>
                            Practice with interactive AI coach
                          </div>
                        </button>
                      )}
                    </div>

                  </div>
                )}

                {/* Pro-only: AI Coaching Chat Panel */}
                {showCoaching && effectiveTier === 'pro' && selectedInterview && (
                  <div className="card" style={{ marginBottom: '20px', border: '2px solid #f7c948' }}>
                    <div style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #e5e5e5',
                      background: 'linear-gradient(135deg, #fdf4d7 0%, #fffef9 100%)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallTargetIcon size={16} /> AI Interview Coach</h3>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#7a7a7a' }}>
                          Practice answers, get feedback, and build confidence
                        </p>
                      </div>
                      <button
                        onClick={() => setShowCoaching(false)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          fontSize: '18px',
                          cursor: 'pointer',
                          color: '#7a7a7a'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Chat messages */}
                    <div style={{
                      padding: '16px 20px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      background: '#fafafa'
                    }}>
                      {/* Welcome message */}
                      {coachingMessages.length === 0 && (
                        <div style={{
                          padding: '16px',
                          background: 'white',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          border: '1px solid #e5e5e5'
                        }}>
                          <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#3d3d3d' }}>
                            <p style={{ margin: '0 0 12px', fontWeight: '600' }}>
                              I'm your interview coach! I've reviewed your profile and the role you're preparing for.
                            </p>
                            <p style={{ margin: '0 0 12px' }}>How can I help you get ready? I can:</p>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#5a5a5a' }}>
                              <li>Practice answering likely questions with feedback</li>
                              <li>Help refine your STAR stories</li>
                              <li>Run mock interview scenarios</li>
                              <li>Address concerns about gaps or weak areas</li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Chat history */}
                      {coachingMessages.map((msg, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: '12px',
                            display: 'flex',
                            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div style={{
                            maxWidth: '80%',
                            padding: '12px 16px',
                            borderRadius: '12px',
                            background: msg.role === 'user' ? '#d4736a' : 'white',
                            color: msg.role === 'user' ? 'white' : '#3d3d3d',
                            border: msg.role === 'assistant' ? '1px solid #e5e5e5' : 'none',
                            fontSize: '13px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {msg.content}
                          </div>
                        </div>
                      ))}

                      {/* Loading indicator */}
                      {coachingLoading && (
                        <div style={{
                          marginBottom: '12px',
                          display: 'flex',
                          justifyContent: 'flex-start'
                        }}>
                          <div style={{
                            padding: '12px 16px',
                            borderRadius: '12px',
                            background: 'white',
                            border: '1px solid #e5e5e5',
                            fontSize: '13px',
                            color: '#7a7a7a'
                          }}>
                            Thinking...
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Chat input */}
                    <div style={{
                      padding: '16px 20px',
                      borderTop: '1px solid #e5e5e5',
                      display: 'flex',
                      gap: '12px'
                    }}>
                      <input
                        type="text"
                        value={coachingInput}
                        onChange={(e) => setCoachingInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendCoachingMessage(selectedInterview, coachingInput);
                          }
                        }}
                        placeholder="Ask a question or practice an answer..."
                        disabled={coachingLoading}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0',
                          fontSize: '13px',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={() => sendCoachingMessage(selectedInterview, coachingInput)}
                        disabled={coachingLoading || !coachingInput.trim()}
                        style={{
                          padding: '12px 20px',
                          background: coachingLoading || !coachingInput.trim() ? '#e5e5e5' : '#d4736a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: coachingLoading || !coachingInput.trim() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Send
                      </button>
                    </div>

                    {/* Quick prompts */}
                    <div style={{
                      padding: '0 20px 16px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {[
                        "Practice 'Tell me about yourself'",
                        "Help me with my biggest weakness",
                        "Mock behavioral question",
                        "Why should we hire you?"
                      ].map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => sendCoachingMessage(selectedInterview, prompt)}
                          disabled={coachingLoading}
                          style={{
                            padding: '6px 12px',
                            background: '#f5f5f5',
                            border: '1px solid #e0e0e0',
                            borderRadius: '16px',
                            fontSize: '11px',
                            color: '#5a5a5a',
                            cursor: coachingLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STAR Examples */}
                {selectedInterview.starExamples?.length > 0 && (
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallStarIcon size={16} /> STAR Examples</h3>
                    </div>
                    <div style={{ padding: '0' }}>
                      {selectedInterview.starExamples.map((star, idx) => (
                        <details key={idx} style={{ borderBottom: idx < selectedInterview.starExamples.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <summary style={{
                            padding: '16px 20px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}>
                            <span>{star.skill}</span>
                            <span style={{ fontSize: '11px', color: '#7a7a7a' }}>Click to expand</span>
                          </summary>
                          <div style={{ padding: '0 20px 20px', background: '#fafafa' }}>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#d4736a', marginBottom: '4px', textTransform: 'uppercase' }}>Situation</div>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>{star.situation}</p>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#f7c948', marginBottom: '4px', textTransform: 'uppercase' }}>Task</div>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>{star.task}</p>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#5a8f5a', marginBottom: '4px', textTransform: 'uppercase' }}>Action</div>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>{star.action}</p>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px', textTransform: 'uppercase' }}>Result</div>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>{star.result}</p>
                            </div>
                            {star.followUpQuestions?.length > 0 && (
                              <div style={{ marginTop: '16px', padding: '12px', background: 'white', borderRadius: '3px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: '#7a7a7a', marginBottom: '8px', textTransform: 'uppercase' }}>Likely Follow-up Questions</div>
                                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#5a5a5a' }}>
                                  {star.followUpQuestions.map((q, i) => <li key={i} style={{ marginBottom: '4px' }}>{q}</li>)}
                                </ul>
                              </div>
                            )}
                            {star.talkingPoints?.length > 0 && (
                              <div style={{ marginTop: '12px', padding: '12px', background: '#fdf4d7', borderRadius: '3px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '600', color: '#7a7a7a', marginBottom: '8px', textTransform: 'uppercase' }}>Key Talking Points</div>
                                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#5a5a5a' }}>
                                  {star.talkingPoints.map((p, i) => <li key={i} style={{ marginBottom: '4px' }}>{p}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prep Document - Enhanced One-Pager (Collapsible) */}
                {selectedInterview.prepDocument && (
                  <details className="card" style={{ marginBottom: '20px' }} open>
                    <summary style={{
                      padding: '16px 20px',
                      borderBottom: '1px solid #e5e5e5',
                      cursor: 'pointer',
                      listStyle: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#f8fff8'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <SmallClipboardIcon size={16} /> Interview Prep One-Pager
                        <span style={{ fontSize: '11px', fontWeight: '500', color: '#5a8f5a', marginLeft: '8px' }}>✓ Ready</span>
                      </h3>
                      <span style={{ fontSize: '12px', color: '#7a7a7a' }}>▼</span>
                    </summary>
                    <div style={{ padding: '20px' }}>

                      {/* MARKET ANALYSIS */}
                      {selectedInterview.prepDocument.marketAnalysis && (
                        <div style={{ marginBottom: '28px', padding: '16px', background: '#f8f9fa', borderRadius: '6px', borderLeft: '4px solid #6c757d' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: '700', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '1px' }}>Market Analysis</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div><span style={{ fontSize: '11px', color: '#7a7a7a' }}>Industry:</span> <span style={{ fontSize: '13px', fontWeight: '500' }}>{selectedInterview.prepDocument.marketAnalysis.industry}</span></div>
                            <div><span style={{ fontSize: '11px', color: '#7a7a7a' }}>Market Size:</span> <span style={{ fontSize: '13px', fontWeight: '500' }}>{selectedInterview.prepDocument.marketAnalysis.marketSize}</span></div>
                            <div><span style={{ fontSize: '11px', color: '#7a7a7a' }}>Growth:</span> <span style={{ fontSize: '13px', fontWeight: '500' }}>{selectedInterview.prepDocument.marketAnalysis.growthRate}</span></div>
                          </div>
                          {selectedInterview.prepDocument.marketAnalysis.keyTrends?.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#7a7a7a' }}>Key Trends:</span>
                              <ul style={{ margin: '4px 0 0', paddingLeft: '16px', fontSize: '12px' }}>
                                {selectedInterview.prepDocument.marketAnalysis.keyTrends.map((t, i) => <li key={i}>{t}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* COMPANY BRIEF (VC-Style) */}
                      {selectedInterview.prepDocument.companyBrief && (
                        <div style={{ marginBottom: '28px', padding: '16px', background: '#fff8e6', borderRadius: '6px', borderLeft: '4px solid #f7c948' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: '700', color: '#b8860b', textTransform: 'uppercase', letterSpacing: '1px' }}>Company Brief (VC Analysis)</h4>
                          <p style={{ margin: '0 0 12px', fontSize: '13px', lineHeight: '1.6' }}>{selectedInterview.prepDocument.companyBrief.overview}</p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px', fontSize: '12px' }}>
                            <div><span style={{ color: '#7a7a7a' }}>Stage:</span> <strong>{selectedInterview.prepDocument.companyBrief.stage}</strong></div>
                            {selectedInterview.prepDocument.companyBrief.fundingEstimate && <div><span style={{ color: '#7a7a7a' }}>Funding:</span> <strong>{selectedInterview.prepDocument.companyBrief.fundingEstimate}</strong></div>}
                            {selectedInterview.prepDocument.companyBrief.headcount && <div><span style={{ color: '#7a7a7a' }}>Team:</span> <strong>{selectedInterview.prepDocument.companyBrief.headcount}</strong></div>}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                            <div style={{ background: '#e8f5e9', padding: '10px', borderRadius: '4px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#2e7d32', marginBottom: '4px' }}>BULL CASE</div>
                              <div style={{ fontSize: '12px' }}>{selectedInterview.prepDocument.companyBrief.bullCase}</div>
                            </div>
                            <div style={{ background: '#ffebee', padding: '10px', borderRadius: '4px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', color: '#c62828', marginBottom: '4px' }}>BEAR CASE</div>
                              <div style={{ fontSize: '12px' }}>{selectedInterview.prepDocument.companyBrief.bearCase}</div>
                            </div>
                          </div>
                          {selectedInterview.prepDocument.companyBrief.vcVerdict && (
                            <div style={{ marginTop: '12px', padding: '10px', background: '#f7c948', borderRadius: '4px' }}>
                              <div style={{ fontSize: '10px', fontWeight: '600', marginBottom: '4px' }}>VC VERDICT</div>
                              <div style={{ fontSize: '13px', fontWeight: '500' }}>{selectedInterview.prepDocument.companyBrief.vcVerdict}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Legacy: Company Overview (backwards compatibility) */}
                      {!selectedInterview.prepDocument.companyBrief && selectedInterview.prepDocument.companyOverview && (
                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#d4736a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company Overview</h4>
                          <p style={{ margin: '0 0 8px', fontSize: '13px', lineHeight: '1.6' }}>{selectedInterview.prepDocument.companyOverview.whatTheyDo}</p>
                        </div>
                      )}

                      {/* ROLE IMPACT */}
                      {selectedInterview.prepDocument.roleImpact && (
                        <div style={{ marginBottom: '28px', padding: '16px', background: '#e8f4f8', borderRadius: '6px', borderLeft: '4px solid #0288d1' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: '700', color: '#0277bd', textTransform: 'uppercase', letterSpacing: '1px' }}>Role Impact & Growth</h4>
                          <p style={{ margin: '0 0 8px', fontSize: '13px' }}><strong>Why This Role Matters:</strong> {selectedInterview.prepDocument.roleImpact.whyThisRoleMatters}</p>
                          <p style={{ margin: '0 0 8px', fontSize: '13px' }}><strong>Growth Path:</strong> {selectedInterview.prepDocument.roleImpact.growthTrajectory}</p>
                          <p style={{ margin: '0 0 8px', fontSize: '13px' }}><strong>Scope:</strong> {selectedInterview.prepDocument.roleImpact.scopeOfInfluence}</p>
                          {selectedInterview.prepDocument.roleImpact.skillsDeveloped?.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <span style={{ fontSize: '12px', fontWeight: '500' }}>Skills You'll Develop:</span>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                {selectedInterview.prepDocument.roleImpact.skillsDeveloped.map((s, i) => (
                                  <span key={i} className="tag" style={{ background: '#0288d1', color: 'white', fontSize: '10px' }}>{s}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* EQUITY ANALYSIS */}
                      {selectedInterview.prepDocument.equityAnalysis && (
                        <div style={{ marginBottom: '28px', padding: '16px', background: '#f3e5f5', borderRadius: '6px', borderLeft: '4px solid #7b1fa2' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: '700', color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: '1px' }}>Equity Analysis</h4>
                          {selectedInterview.prepDocument.equityAnalysis.evaluationFramework && (
                            <p style={{ margin: '0 0 12px', fontSize: '13px', lineHeight: '1.5' }}>{selectedInterview.prepDocument.equityAnalysis.evaluationFramework}</p>
                          )}
                          {selectedInterview.prepDocument.equityAnalysis.questionsToAsk?.length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '6px' }}>Questions to Ask About Equity:</div>
                              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px' }}>
                                {selectedInterview.prepDocument.equityAnalysis.questionsToAsk.map((q, i) => <li key={i} style={{ marginBottom: '4px' }}>{q}</li>)}
                              </ul>
                            </div>
                          )}
                          {selectedInterview.prepDocument.equityAnalysis.scenarios && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
                              <div style={{ background: 'white', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#7a7a7a' }}>Conservative (1x)</div>
                                <div style={{ fontSize: '11px' }}>{selectedInterview.prepDocument.equityAnalysis.scenarios.conservative}</div>
                              </div>
                              <div style={{ background: 'white', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#7a7a7a' }}>Base (3x)</div>
                                <div style={{ fontSize: '11px' }}>{selectedInterview.prepDocument.equityAnalysis.scenarios.base}</div>
                              </div>
                              <div style={{ background: 'white', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                                <div style={{ fontSize: '10px', color: '#7a7a7a' }}>Optimistic (10x)</div>
                                <div style={{ fontSize: '11px' }}>{selectedInterview.prepDocument.equityAnalysis.scenarios.optimistic}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* YOUR TALKING POINTS (Brag Book Integration) */}
                      {selectedInterview.prepDocument.talkingPoints && (
                        <div style={{ marginBottom: '28px', padding: '16px', background: '#e8f5e9', borderRadius: '6px', borderLeft: '4px solid #43a047' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: '700', color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Talking Points</h4>
                          {selectedInterview.prepDocument.talkingPoints.topStories?.map((story, i) => (
                            <div key={i} style={{ marginBottom: '12px', padding: '12px', background: 'white', borderRadius: '4px' }}>
                              <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{story.achievement}</div>
                              <div style={{ fontSize: '12px', color: '#5a5a5a', marginBottom: '4px' }}>{story.howToFrame}</div>
                              {story.relevantSkill && <span className="tag" style={{ background: '#43a047', color: 'white', fontSize: '10px' }}>{story.relevantSkill}</span>}
                            </div>
                          ))}
                          {selectedInterview.prepDocument.talkingPoints.suggestedAdditional?.length > 0 && (
                            <div style={{ marginTop: '12px', padding: '10px', background: '#fff8e1', borderRadius: '4px' }}>
                              <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>Suggested Additional Stories to Prepare:</div>
                              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px' }}>
                                {selectedInterview.prepDocument.talkingPoints.suggestedAdditional.map((s, i) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* INTERVIEW STRATEGY */}
                      {selectedInterview.prepDocument.interviewStrategy && (
                        <>
                          {/* Questions to Ask */}
                          {selectedInterview.prepDocument.interviewStrategy.questionsToAsk?.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#f7c948', textTransform: 'uppercase', letterSpacing: '0.5px' }}>High-Leverage Questions to Ask</h4>
                              {selectedInterview.prepDocument.interviewStrategy.questionsToAsk.map((q, i) => (
                                <div key={i} style={{ marginBottom: '12px', padding: '12px', background: '#fdf4d7', borderRadius: '3px' }}>
                                  <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '4px' }}>{q.question}</div>
                                  <div style={{ fontSize: '11px', color: '#7a7a7a' }}>{q.why}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Likely Questions */}
                          {selectedInterview.prepDocument.interviewStrategy.likelyQuestions?.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#5a8f5a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Questions You'll Likely Face</h4>
                              {selectedInterview.prepDocument.interviewStrategy.likelyQuestions.map((q, i) => (
                                <div key={i} style={{ marginBottom: '12px', padding: '12px', background: '#f0f7f0', borderRadius: '3px' }}>
                                  <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '4px' }}>{q.question}</div>
                                  <div style={{ fontSize: '11px', color: '#7a7a7a' }}>{q.approach}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Red Flags */}
                          {selectedInterview.prepDocument.interviewStrategy.redFlags?.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                              <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#d4736a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gaps to Address</h4>
                              {selectedInterview.prepDocument.interviewStrategy.redFlags.map((rf, i) => (
                                <div key={i} style={{ marginBottom: '12px', padding: '12px', background: '#fdf0ef', borderRadius: '3px' }}>
                                  <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '4px' }}>{rf.gap}</div>
                                  <div style={{ fontSize: '11px', color: '#7a7a7a' }}>{rf.strategy}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Closing Strategy */}
                          {selectedInterview.prepDocument.interviewStrategy.closingStrategy && (
                            <div style={{ padding: '16px', background: '#f7c948', borderRadius: '3px' }}>
                              <h4 style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Closing Strategy</h4>
                              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>{selectedInterview.prepDocument.interviewStrategy.closingStrategy}</p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Legacy fields for backwards compatibility */}
                      {!selectedInterview.prepDocument.interviewStrategy && selectedInterview.prepDocument.questionsToAsk?.length > 0 && (
                        <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: '600', color: '#f7c948', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Questions to Ask</h4>
                          {selectedInterview.prepDocument.questionsToAsk.map((q, i) => (
                            <div key={i} style={{ marginBottom: '12px', padding: '12px', background: '#fdf4d7', borderRadius: '3px' }}>
                              <div style={{ fontWeight: '500', fontSize: '13px', marginBottom: '4px' }}>{q.question}</div>
                              <div style={{ fontSize: '11px', color: '#7a7a7a' }}>{q.why}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {!selectedInterview.prepDocument.interviewStrategy && selectedInterview.prepDocument.closingStrategy && (
                        <div style={{ padding: '16px', background: '#f7c948', borderRadius: '3px' }}>
                          <h4 style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Closing Strategy</h4>
                          <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>{selectedInterview.prepDocument.closingStrategy}</p>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {/* Record Outcome (for past interviews) */}
                {selectedInterview.status !== 'completed' && new Date(selectedInterview.scheduledAt) < new Date() && (
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5' }}>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Record Outcome</h3>
                    </div>
                    <div style={{ padding: '20px' }}>
                      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#7a7a7a' }}>
                        How did the interview go? Recording outcomes helps improve future prep.
                      </p>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {['passed', 'rejected', 'pending', 'offer'].map(outcome => (
                          <button
                            key={outcome}
                            className="btn btn-secondary"
                            onClick={() => recordInterviewOutcome(selectedInterview, outcome, '', {})}
                            style={{
                              background: outcome === 'passed' ? '#5a8f5a' :
                                outcome === 'rejected' ? '#d4736a' :
                                outcome === 'offer' ? '#f7c948' : '#e5e5e5',
                              color: outcome === 'offer' || outcome === 'pending' ? '#3d3d3d' : 'white',
                              border: 'none'
                            }}
                          >
                            {outcome.charAt(0).toUpperCase() + outcome.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Interview */}
                <div style={{ textAlign: 'right', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      if (confirm('Delete this interview?')) {
                        handleDeleteInterview(selectedInterview.id);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#d4736a',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete Interview
                  </button>
                </div>
              </div>
            )}
              </>
            )}

            {/* BRAG BOOK SUBTAB */}
            {interviewsSubTab === 'brag_book' && (
              <div>
                <div className="card" style={{ marginBottom: '20px' }}>
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>Brag Book</h2>
                      <p className="section-subtitle">Build your narrative of professional achievements using STAR format</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {prepModeCompany && (
                        <button
                          onClick={() => setPrepModeCompany(null)}
                          className="btn"
                          style={{ padding: '8px 16px', fontSize: '11px', background: '#f5f5f5', color: '#3d3d3d' }}
                        >
                          Exit Prep Mode
                        </button>
                      )}
                      {showStarGenerator && (
                        <button
                          onClick={() => {
                            setShowStarGenerator(false);
                            setDraftStars([]);
                            setSelectedDraftStar(null);
                            setAmplificationAnswers({});
                            setAdditionalContext('');
                          }}
                          className="btn"
                          style={{ padding: '8px 16px', fontSize: '11px', background: '#f5f5f5', color: '#3d3d3d' }}
                        >
                          Back to List
                        </button>
                      )}
                      <button
                        className="btn"
                        onClick={async () => {
                          if (!profile.experiences || profile.experiences.length === 0) {
                            alert('Add work experiences to your profile first to generate STAR achievements.');
                            return;
                          }
                          setShowStarGenerator(true);
                          setShowBragBookForm(false);
                          setSelectedBragEntry(null);
                          setGeneratingStars(true);
                          setDraftStars([]);
                          try {
                            const response = await fetch('/api/generate-stars', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                experiences: profile.experiences,
                                skills: profile.skills,
                                currentProjects: profile.currentProjects
                              })
                            });
                            const data = await response.json();
                            if (data.success && data.stars) {
                              setDraftStars(data.stars);
                            } else {
                              console.error('Failed to generate STARs:', data.error);
                              alert('Failed to generate achievements. Please try again.');
                              setShowStarGenerator(false);
                            }
                          } catch (error) {
                            console.error('Error generating STARs:', error);
                            alert('Failed to generate achievements. Please try again.');
                            setShowStarGenerator(false);
                          } finally {
                            setGeneratingStars(false);
                          }
                        }}
                        disabled={generatingStars}
                        style={{ padding: '8px 16px', fontSize: '11px', background: '#e8f4e8', color: '#2d5a2d', border: '1px solid #b8d4b8' }}
                      >
                        {generatingStars ? 'Generating...' : 'Generate STARs'}
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setEditingBragEntry(null);
                          setBragBookForm({
                            title: '',
                            situation: '',
                            task: '',
                            action: '',
                            result: '',
                            skills: [],
                            date: '',
                            company: '',
                            impact: ''
                          });
                          setShowBragBookForm(true);
                          setShowStarGenerator(false);
                        }}
                        style={{ padding: '8px 16px', fontSize: '11px' }}
                      >
                        + Add Achievement
                      </button>
                    </div>
                  </div>

                  {/* Interview Prep Mode Selector */}
                  {!showBragBookForm && !selectedBragEntry && !showStarGenerator && (
                    <div style={{
                      padding: '12px 20px',
                      background: '#fdf4d7',
                      borderBottom: '1px solid #e5e5e5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <SmallTargetIcon size={14} />
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#3d3d3d' }}>Interview Prep Mode:</span>
                      <select
                        value={prepModeCompany || ''}
                        onChange={(e) => setPrepModeCompany(e.target.value || null)}
                        style={{
                          padding: '6px 10px',
                          fontSize: '12px',
                          border: '1px solid #d8d8d8',
                          borderRadius: '4px',
                          background: 'white'
                        }}
                      >
                        <option value="">Select a company...</option>
                        {[...new Set(jobLedger.map(j => j.company).filter(Boolean))].map(company => (
                          <option key={company} value={company}>{company}</option>
                        ))}
                      </select>
                      {prepModeCompany && (
                        <span style={{ fontSize: '11px', color: '#7a7a7a' }}>
                          Showing relevant achievements for {prepModeCompany}
                        </span>
                      )}
                    </div>
                  )}

                  {/* STAR Generator Views */}
                  {showStarGenerator && (
                    <div style={{ padding: '20px' }}>
                      {/* Loading State */}
                      {generatingStars && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                          <div className="spinner" style={{ width: '32px', height: '32px', margin: '0 auto 16px' }}></div>
                          <p style={{ fontSize: '14px', color: '#3d3d3d', fontWeight: '500' }}>Analyzing your experience...</p>
                          <p style={{ fontSize: '12px', color: '#7a7a7a', marginTop: '8px' }}>Generating STAR achievement stories</p>
                        </div>
                      )}

                      {/* Draft STAR Cards */}
                      {!generatingStars && draftStars.length > 0 && !selectedDraftStar && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#7a7a7a', marginBottom: '16px' }}>
                            We found {draftStars.length} potential achievements from your profile. Select one to refine and amplify.
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {draftStars.map((star, index) => (
                              <div
                                key={index}
                                style={{
                                  padding: '16px',
                                  border: '1px solid #e5e5e5',
                                  borderRadius: '8px',
                                  background: '#fafafa',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.background = '#fafafa'; }}
                                onClick={() => {
                                  setSelectedDraftStar(star);
                                  setAmplificationAnswers({});
                                  setAdditionalContext('');
                                  setBragBookForm({
                                    title: star.title || '',
                                    situation: star.situation || '',
                                    task: star.task || '',
                                    action: star.action || '',
                                    result: star.result || '',
                                    skills: star.skills || [],
                                    date: star.date || '',
                                    company: star.company || '',
                                    impact: star.impact || ''
                                  });
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', margin: 0 }}>{star.title}</h4>
                                  {star.company && (
                                    <span style={{ fontSize: '11px', color: '#7a7a7a', background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>
                                      {star.company}
                                    </span>
                                  )}
                                </div>
                                <p style={{ fontSize: '12px', color: '#5a5a5a', marginBottom: '8px', lineHeight: '1.5' }}>
                                  {star.result?.substring(0, 150)}{star.result?.length > 150 ? '...' : ''}
                                </p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {star.skills?.slice(0, 4).map((skill, i) => (
                                    <span key={i} style={{ fontSize: '10px', background: '#e8f4e8', color: '#2d5a2d', padding: '2px 6px', borderRadius: '3px' }}>
                                      {skill}
                                    </span>
                                  ))}
                                  {star.skills?.length > 4 && (
                                    <span style={{ fontSize: '10px', color: '#7a7a7a' }}>+{star.skills.length - 4} more</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected STAR Editor with Amplification */}
                      {!generatingStars && selectedDraftStar && (
                        <div>
                          <div style={{ marginBottom: '20px' }}>
                            <button
                              onClick={() => {
                                setSelectedDraftStar(null);
                                setAmplificationAnswers({});
                                setAdditionalContext('');
                              }}
                              style={{ fontSize: '12px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              &larr; Back to drafts
                            </button>
                          </div>

                          {/* STAR Form Fields */}
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                              Achievement Title
                            </label>
                            <input
                              type="text"
                              value={bragBookForm.title}
                              onChange={(e) => setBragBookForm(prev => ({ ...prev, title: e.target.value }))}
                              style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px' }}
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>Company</label>
                              <input
                                type="text"
                                value={bragBookForm.company}
                                onChange={(e) => setBragBookForm(prev => ({ ...prev, company: e.target.value }))}
                                style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>Date/Timeframe</label>
                              <input
                                type="text"
                                value={bragBookForm.date}
                                onChange={(e) => setBragBookForm(prev => ({ ...prev, date: e.target.value }))}
                                style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px' }}
                              />
                            </div>
                          </div>

                          <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#3d3d3d', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <SmallStarIcon size={14} /> STAR Format
                            </div>

                            {[
                              { key: 'situation', label: 'Situation - What was the context?' },
                              { key: 'task', label: 'Task - What was your responsibility?' },
                              { key: 'action', label: 'Action - What did you do?' },
                              { key: 'result', label: 'Result - What was the outcome?' }
                            ].map(field => (
                              <div key={field.key} style={{ marginBottom: '12px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  {field.label}
                                </label>
                                <textarea
                                  value={bragBookForm[field.key]}
                                  onChange={(e) => setBragBookForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                                  rows={field.key === 'action' ? 3 : 2}
                                  style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px', resize: 'vertical' }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Amplification Questions Section */}
                          {selectedDraftStar.amplificationQuestions && selectedDraftStar.amplificationQuestions.length > 0 && (
                            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>&#10024;</span> Amplify Your Impact
                              </div>
                              <p style={{ fontSize: '12px', color: '#78716c', marginBottom: '16px' }}>
                                Answer these questions to add specific metrics and make your achievement more impressive.
                              </p>

                              {selectedDraftStar.amplificationQuestions.map((question, index) => (
                                <div key={index} style={{ marginBottom: '12px' }}>
                                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#44403c', marginBottom: '6px' }}>
                                    {index + 1}. {question}
                                  </label>
                                  <input
                                    type="text"
                                    value={amplificationAnswers[index] || ''}
                                    onChange={(e) => setAmplificationAnswers(prev => ({ ...prev, [index]: e.target.value }))}
                                    placeholder="Your answer..."
                                    style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px', background: 'white' }}
                                  />
                                </div>
                              ))}

                              <div style={{ marginTop: '16px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#44403c', marginBottom: '6px' }}>
                                  Additional context (optional)
                                </label>
                                <textarea
                                  value={additionalContext}
                                  onChange={(e) => setAdditionalContext(e.target.value)}
                                  placeholder="Any other details that would help amplify this achievement..."
                                  rows={2}
                                  style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px', background: 'white', resize: 'vertical' }}
                                />
                              </div>

                              <button
                                onClick={async () => {
                                  const hasAnswers = Object.values(amplificationAnswers).some(a => a && a.trim());
                                  if (!hasAnswers && !additionalContext.trim()) {
                                    alert('Please answer at least one question or add additional context.');
                                    return;
                                  }
                                  setAmplifyingStars(true);
                                  try {
                                    const response = await fetch('/api/amplify-star', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        star: { ...bragBookForm, amplificationQuestions: selectedDraftStar.amplificationQuestions },
                                        answers: Object.values(amplificationAnswers),
                                        additionalContext
                                      })
                                    });
                                    const data = await response.json();
                                    if (data.success && data.star) {
                                      setBragBookForm({
                                        title: data.star.title || bragBookForm.title,
                                        situation: data.star.situation || bragBookForm.situation,
                                        task: data.star.task || bragBookForm.task,
                                        action: data.star.action || bragBookForm.action,
                                        result: data.star.result || bragBookForm.result,
                                        skills: data.star.skills || bragBookForm.skills,
                                        date: data.star.date || bragBookForm.date,
                                        company: data.star.company || bragBookForm.company,
                                        impact: data.star.impact || bragBookForm.impact
                                      });
                                    } else {
                                      console.error('Failed to amplify STAR:', data.error);
                                      alert('Failed to amplify achievement. Please try again.');
                                    }
                                  } catch (error) {
                                    console.error('Error amplifying STAR:', error);
                                    alert('Failed to amplify achievement. Please try again.');
                                  } finally {
                                    setAmplifyingStars(false);
                                  }
                                }}
                                disabled={amplifyingStars}
                                className="btn"
                                style={{ marginTop: '12px', padding: '10px 20px', fontSize: '12px', background: '#f59e0b', color: 'white', border: 'none' }}
                              >
                                {amplifyingStars ? 'Amplifying...' : '&#10024; Amplify Achievement'}
                              </button>
                            </div>
                          )}

                          {/* Skills and Impact */}
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                              Skills Demonstrated (comma-separated)
                            </label>
                            <input
                              type="text"
                              value={bragBookForm.skills.join(', ')}
                              onChange={(e) => setBragBookForm(prev => ({ ...prev, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                              style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px' }}
                            />
                          </div>

                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                              Business Impact
                            </label>
                            <input
                              type="text"
                              value={bragBookForm.impact}
                              onChange={(e) => setBragBookForm(prev => ({ ...prev, impact: e.target.value }))}
                              placeholder="e.g., $2M annual savings, 40% faster deployments"
                              style={{ width: '100%', padding: '10px 12px', fontSize: '13px', border: '1px solid #d8d8d8', borderRadius: '6px' }}
                            />
                          </div>

                          {/* Save Buttons */}
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => {
                                setSelectedDraftStar(null);
                                setAmplificationAnswers({});
                                setAdditionalContext('');
                              }}
                              className="btn"
                              style={{ padding: '10px 20px', fontSize: '12px', background: '#f5f5f5', color: '#3d3d3d' }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={async () => {
                                if (!bragBookForm.title) {
                                  alert('Please enter an achievement title');
                                  return;
                                }
                                try {
                                  if (userId) {
                                    const { data, error } = await addBragBookEntry(userId, bragBookForm);
                                    if (error) throw error;
                                    const appEntry = dbBragBookEntryToApp(data);
                                    setBragBookEntries(prev => [appEntry, ...prev]);
                                  } else {
                                    const newEntry = {
                                      id: Date.now().toString(),
                                      ...bragBookForm,
                                      createdAt: new Date().toISOString(),
                                      updatedAt: new Date().toISOString()
                                    };
                                    setBragBookEntries(prev => [newEntry, ...prev]);
                                  }
                                  setShowStarGenerator(false);
                                  setSelectedDraftStar(null);
                                  setDraftStars([]);
                                  setAmplificationAnswers({});
                                  setAdditionalContext('');
                                } catch (e) {
                                  console.error('Error saving brag book entry:', e);
                                  alert('Failed to save achievement. Please try again.');
                                }
                              }}
                              className="btn btn-primary"
                              style={{ padding: '10px 20px', fontSize: '12px' }}
                            >
                              Save to Brag Book
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Brag Book Form */}
                  {showBragBookForm && (
                    <div style={{ padding: '20px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                          Achievement Title *
                        </label>
                        <input
                          type="text"
                          value={bragBookForm.title}
                          onChange={(e) => setBragBookForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Led migration to microservices architecture"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '13px',
                            border: '1px solid #d8d8d8',
                            borderRadius: '6px'
                          }}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                            Company
                          </label>
                          <input
                            type="text"
                            value={bragBookForm.company}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, company: e.target.value }))}
                            placeholder="e.g., Acme Corp"
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                            Date/Timeframe
                          </label>
                          <input
                            type="text"
                            value={bragBookForm.date}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, date: e.target.value }))}
                            placeholder="e.g., Q3 2024 or Jan-Mar 2024"
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#3d3d3d', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <SmallStarIcon size={14} /> STAR Format
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Situation - What was the context?
                          </label>
                          <textarea
                            value={bragBookForm.situation}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, situation: e.target.value }))}
                            placeholder="Describe the situation or challenge you faced..."
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Task - What was your responsibility?
                          </label>
                          <textarea
                            value={bragBookForm.task}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, task: e.target.value }))}
                            placeholder="What was your specific role or responsibility?"
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Action - What did you do?
                          </label>
                          <textarea
                            value={bragBookForm.action}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, action: e.target.value }))}
                            placeholder="What specific actions did you take?"
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#7a7a7a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Result - What was the outcome?
                          </label>
                          <textarea
                            value={bragBookForm.result}
                            onChange={(e) => setBragBookForm(prev => ({ ...prev, result: e.target.value }))}
                            placeholder="What were the measurable results or impact?"
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              fontSize: '13px',
                              border: '1px solid #d8d8d8',
                              borderRadius: '6px',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                          Skills Demonstrated (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={bragBookForm.skills.join(', ')}
                          onChange={(e) => setBragBookForm(prev => ({ ...prev, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                          placeholder="e.g., Leadership, System Design, Python, Communication"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '13px',
                            border: '1px solid #d8d8d8',
                            borderRadius: '6px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '4px' }}>
                          Business Impact (optional)
                        </label>
                        <input
                          type="text"
                          value={bragBookForm.impact}
                          onChange={(e) => setBragBookForm(prev => ({ ...prev, impact: e.target.value }))}
                          placeholder="e.g., $2M annual savings, 40% faster deployments, 99.9% uptime"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '13px',
                            border: '1px solid #d8d8d8',
                            borderRadius: '6px'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowBragBookForm(false)}
                          className="btn"
                          style={{ padding: '10px 20px', fontSize: '12px', background: '#f5f5f5', color: '#3d3d3d' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!bragBookForm.title) {
                              alert('Please enter an achievement title');
                              return;
                            }
                            try {
                              if (editingBragEntry) {
                                // Update existing entry
                                if (userId) {
                                  const { data, error } = await updateBragBookEntry(editingBragEntry.id, bragBookForm);
                                  if (error) throw error;
                                  const appEntry = dbBragBookEntryToApp(data);
                                  setBragBookEntries(prev => prev.map(e => e.id === editingBragEntry.id ? appEntry : e));
                                } else {
                                  const updatedEntry = { ...editingBragEntry, ...bragBookForm, updatedAt: new Date().toISOString() };
                                  setBragBookEntries(prev => prev.map(e => e.id === editingBragEntry.id ? updatedEntry : e));
                                }
                              } else {
                                // Add new entry
                                if (userId) {
                                  const { data, error } = await addBragBookEntry(userId, bragBookForm);
                                  if (error) throw error;
                                  const appEntry = dbBragBookEntryToApp(data);
                                  setBragBookEntries(prev => [appEntry, ...prev]);
                                } else {
                                  const newEntry = {
                                    id: Date.now().toString(),
                                    ...bragBookForm,
                                    createdAt: new Date().toISOString(),
                                    updatedAt: new Date().toISOString()
                                  };
                                  setBragBookEntries(prev => [newEntry, ...prev]);
                                }
                              }
                              setShowBragBookForm(false);
                              setEditingBragEntry(null);
                            } catch (e) {
                              console.error('Error saving brag book entry:', e);
                              alert('Failed to save achievement. Please try again.');
                            }
                          }}
                          className="btn btn-primary"
                          style={{ padding: '10px 20px', fontSize: '12px' }}
                        >
                          {editingBragEntry ? 'Update Achievement' : 'Save Achievement'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Brag Book Entries List */}
                  {!showBragBookForm && !selectedBragEntry && !showStarGenerator && (
                    <>
                      {bragBookEntries.length === 0 ? (
                        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>
                          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', opacity: 0.5 }}><SubscriptionIcon size={48} /></div>
                          <p style={{ margin: 0, fontSize: '13px', marginBottom: '8px' }}>No achievements recorded yet.</p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#7a7a7a' }}>
                            Start building your professional narrative by adding your accomplishments.
                          </p>
                        </div>
                      ) : (
                        <div>
                          {bragBookEntries
                            .filter(entry => {
                              if (!prepModeCompany) return true;
                              // In prep mode, filter to relevant entries
                              const job = jobLedger.find(j => j.company === prepModeCompany);
                              if (!job?.analysis?.requirements) return true;
                              // Check if entry skills match job requirements
                              const requiredSkills = job.analysis.requirements.map(r => r.skill?.toLowerCase()).filter(Boolean);
                              return entry.skills.some(s => requiredSkills.includes(s.toLowerCase())) || entry.company === prepModeCompany;
                            })
                            .map(entry => (
                              <div
                                key={entry.id}
                                onClick={() => setSelectedBragEntry(entry)}
                                style={{
                                  padding: '16px 20px',
                                  borderBottom: '1px solid #f0f0f0',
                                  cursor: 'pointer',
                                  transition: 'background 0.15s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#fafafa'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#3d3d3d' }}>{entry.title}</div>
                                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                                    {entry.company && (
                                      <span className="tag" style={{ background: '#e8e8e8', color: '#3d3d3d', fontSize: '10px' }}>
                                        {entry.company}
                                      </span>
                                    )}
                                    {entry.date && (
                                      <span className="tag" style={{ background: '#f5f5f5', color: '#7a7a7a', fontSize: '10px' }}>
                                        {entry.date}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {entry.result && (
                                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#5a5a5a', lineHeight: '1.4' }}>
                                    {entry.result.substring(0, 150)}{entry.result.length > 150 ? '...' : ''}
                                  </p>
                                )}
                                {entry.skills.length > 0 && (
                                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                    {entry.skills.slice(0, 4).map((skill, i) => (
                                      <span key={i} className="tag" style={{ background: '#fdf4d7', color: '#3d3d3d', fontSize: '10px' }}>
                                        {skill}
                                      </span>
                                    ))}
                                    {entry.skills.length > 4 && (
                                      <span className="tag" style={{ background: '#f5f5f5', color: '#7a7a7a', fontSize: '10px' }}>
                                        +{entry.skills.length - 4} more
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          {prepModeCompany && bragBookEntries.filter(entry => {
                            const job = jobLedger.find(j => j.company === prepModeCompany);
                            if (!job?.analysis?.requirements) return false;
                            const requiredSkills = job.analysis.requirements.map(r => r.skill?.toLowerCase()).filter(Boolean);
                            return entry.skills.some(s => requiredSkills.includes(s.toLowerCase())) || entry.company === prepModeCompany;
                          }).length === 0 && bragBookEntries.length > 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#7a7a7a', fontSize: '12px' }}>
                              No achievements match the requirements for {prepModeCompany}. Try adding achievements that demonstrate relevant skills.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Brag Entry Detail View */}
                  {selectedBragEntry && !showBragBookForm && (
                    <div style={{ padding: '20px' }}>
                      <button
                        onClick={() => setSelectedBragEntry(null)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'none',
                          border: 'none',
                          color: '#7a7a7a',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginBottom: '16px',
                          padding: 0
                        }}
                      >
                        ← Back to list
                      </button>

                      <div style={{ marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#3d3d3d' }}>{selectedBragEntry.title}</h3>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {selectedBragEntry.company && (
                            <span className="tag" style={{ background: '#e8e8e8', color: '#3d3d3d' }}>{selectedBragEntry.company}</span>
                          )}
                          {selectedBragEntry.date && (
                            <span className="tag" style={{ background: '#f5f5f5', color: '#7a7a7a' }}>{selectedBragEntry.date}</span>
                          )}
                          {selectedBragEntry.impact && (
                            <span className="tag" style={{ background: '#d4edda', color: '#155724' }}>{selectedBragEntry.impact}</span>
                          )}
                        </div>
                      </div>

                      <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px', marginBottom: '16px' }}>
                        {selectedBragEntry.situation && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#d4736a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Situation
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#3d3d3d', lineHeight: '1.5' }}>{selectedBragEntry.situation}</p>
                          </div>
                        )}
                        {selectedBragEntry.task && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#f7c948', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Task
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#3d3d3d', lineHeight: '1.5' }}>{selectedBragEntry.task}</p>
                          </div>
                        )}
                        {selectedBragEntry.action && (
                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#5a8f5a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Action
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#3d3d3d', lineHeight: '1.5' }}>{selectedBragEntry.action}</p>
                          </div>
                        )}
                        {selectedBragEntry.result && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#2563eb', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Result
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: '#3d3d3d', lineHeight: '1.5' }}>{selectedBragEntry.result}</p>
                          </div>
                        )}
                      </div>

                      {selectedBragEntry.skills.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#3d3d3d', marginBottom: '8px' }}>Skills Demonstrated</div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {selectedBragEntry.skills.map((skill, i) => (
                              <span key={i} className="tag" style={{ background: '#fdf4d7', color: '#3d3d3d' }}>{skill}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #e5e5e5', paddingTop: '16px' }}>
                        <button
                          onClick={() => {
                            setBragBookForm({
                              title: selectedBragEntry.title,
                              situation: selectedBragEntry.situation || '',
                              task: selectedBragEntry.task || '',
                              action: selectedBragEntry.action || '',
                              result: selectedBragEntry.result || '',
                              skills: selectedBragEntry.skills || [],
                              date: selectedBragEntry.date || '',
                              company: selectedBragEntry.company || '',
                              impact: selectedBragEntry.impact || ''
                            });
                            setEditingBragEntry(selectedBragEntry);
                            setShowBragBookForm(true);
                            setSelectedBragEntry(null);
                          }}
                          className="btn"
                          style={{ padding: '8px 16px', fontSize: '12px', background: '#f5f5f5', color: '#3d3d3d' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm('Delete this achievement?')) {
                              try {
                                if (userId) {
                                  await deleteBragBookEntry(selectedBragEntry.id);
                                }
                                setBragBookEntries(prev => prev.filter(e => e.id !== selectedBragEntry.id));
                                setSelectedBragEntry(null);
                              } catch (e) {
                                console.error('Error deleting brag book entry:', e);
                                alert('Failed to delete achievement. Please try again.');
                              }
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            fontSize: '12px',
                            background: 'transparent',
                            border: 'none',
                            color: '#d4736a',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* EMAILS SUBTAB */}
            {interviewsSubTab === 'emails' && (
              <div className="fade-in">
                <div className="card">
                  {/* Header */}
                  <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h2 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>Application Emails</h2>
                      <p className="section-subtitle">Track recruiter and application emails</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {gmailConnected && (
                        <button
                          className="btn btn-secondary"
                          onClick={async () => {
                            setEmailSyncing(true);
                            try {
                              // Get the Google access token from Supabase session
                              const { data: { session } } = await import('./supabaseClient').then(m => m.supabase.auth.getSession());
                              const providerToken = session?.provider_token;

                              if (!providerToken) {
                                console.error('No Gmail token available');
                                setEmailSyncing(false);
                                return;
                              }

                              // Get existing message IDs to avoid duplicates
                              const existingIds = applicationEmails.map(e => e.gmailMessageId);

                              const response = await fetch('/api/gmail-sync', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  accessToken: providerToken,
                                  daysAgo: 30,
                                  existingMessageIds: existingIds
                                })
                              });

                              const result = await response.json();

                              if (result.success && result.emails?.length > 0) {
                                // Save new emails to database
                                for (const email of result.emails) {
                                  const { data } = await addApplicationEmail(userId, email);
                                  if (data) {
                                    setApplicationEmails(prev => [dbApplicationEmailToApp(data), ...prev]);
                                  }
                                }
                                // Update last sync time
                                await updateGmailConnection(userId, true, new Date().toISOString());
                              }
                            } catch (error) {
                              console.error('Email sync error:', error);
                            }
                            setEmailSyncing(false);
                          }}
                          disabled={emailSyncing}
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        >
                          {emailSyncing ? 'Syncing...' : 'Refresh'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '20px' }}>
                    {/* Gmail Not Connected State */}
                    {!gmailConnected && (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ marginBottom: '16px', opacity: 0.5 }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#3d3d3d' }}>Connect Gmail</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#7a7a7a', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                          Connect your Gmail account to automatically pull job application and recruiter emails.
                        </p>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            const { error } = await connectGmail();
                            if (error) {
                              console.error('Gmail connect error:', error);
                            }
                          }}
                          style={{ padding: '10px 24px' }}
                        >
                          Connect Gmail
                        </button>
                        <p style={{ marginTop: '12px', fontSize: '11px', color: '#9ca3af' }}>
                          Read-only access. We only fetch job-related emails.
                        </p>
                      </div>
                    )}

                    {/* Gmail Connected - View Mode Toggle & Email List */}
                    {gmailConnected && (
                      <>
                        {/* View Mode Toggle */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', gap: '4px', background: '#f5f5f5', borderRadius: '6px', padding: '4px' }}>
                            <button
                              onClick={() => setEmailsViewMode('chronological')}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                background: emailsViewMode === 'chronological' ? 'white' : 'transparent',
                                color: emailsViewMode === 'chronological' ? '#3d3d3d' : '#7a7a7a',
                                fontWeight: emailsViewMode === 'chronological' ? '500' : '400',
                                boxShadow: emailsViewMode === 'chronological' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                              }}
                            >
                              Chronological
                            </button>
                            <button
                              onClick={() => setEmailsViewMode('company')}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                background: emailsViewMode === 'company' ? 'white' : 'transparent',
                                color: emailsViewMode === 'company' ? '#3d3d3d' : '#7a7a7a',
                                fontWeight: emailsViewMode === 'company' ? '500' : '400',
                                boxShadow: emailsViewMode === 'company' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                              }}
                            >
                              By Company
                            </button>
                          </div>

                          {/* Email Type Filter */}
                          <select
                            value={emailFilter}
                            onChange={(e) => setEmailFilter(e.target.value)}
                            style={{
                              padding: '6px 10px',
                              fontSize: '12px',
                              border: '1px solid #e5e5e5',
                              borderRadius: '6px',
                              background: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="all">All Emails</option>
                            <option value="unread">Unread</option>
                            <option value="interview_invite">Interview Invites</option>
                            <option value="offer">Offers</option>
                            <option value="rejection">Rejections</option>
                            <option value="application_confirmation">Confirmations</option>
                          </select>
                        </div>

                        {/* Email List */}
                        {applicationEmails.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                            <p style={{ margin: 0, fontSize: '13px' }}>
                              {emailSyncing ? 'Syncing emails...' : 'No emails yet. Click Refresh to sync your inbox.'}
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* Chronological View */}
                            {emailsViewMode === 'chronological' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {applicationEmails
                                  .filter(email => {
                                    if (emailFilter === 'all') return true;
                                    if (emailFilter === 'unread') return !email.isRead;
                                    return email.emailType === emailFilter;
                                  })
                                  .map(email => (
                                    <div
                                      key={email.id}
                                      onClick={async () => {
                                        setSelectedEmail(email);
                                        if (!email.isRead) {
                                          await updateEmailRead(email.id, true);
                                          setApplicationEmails(prev => prev.map(e =>
                                            e.id === email.id ? { ...e, isRead: true } : e
                                          ));
                                          setUnreadEmailCount(prev => Math.max(0, prev - 1));
                                        }
                                      }}
                                      style={{
                                        padding: '12px 16px',
                                        background: email.isRead ? '#fafafa' : 'white',
                                        border: '1px solid #e5e5e5',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        borderLeft: email.isRead ? '3px solid transparent' : '3px solid #0288d1'
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0288d1'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.borderLeftColor = email.isRead ? 'transparent' : '#0288d1'; }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ fontWeight: email.isRead ? '400' : '600', fontSize: '13px', color: '#3d3d3d' }}>
                                            {email.fromName || email.fromEmail.split('@')[0]}
                                          </span>
                                          {email.matchedCompany && (
                                            <span style={{ fontSize: '11px', background: '#e8f4fc', color: '#0288d1', padding: '2px 6px', borderRadius: '4px' }}>
                                              {email.matchedCompany}
                                            </span>
                                          )}
                                        </div>
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                          {new Date(email.dateReceived).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                      </div>
                                      <div style={{ fontWeight: email.isRead ? '400' : '500', fontSize: '13px', color: '#3d3d3d', marginBottom: '4px' }}>
                                        {email.subject}
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '12px', color: '#7a7a7a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {email.snippet}
                                        </span>
                                        <span style={{
                                          fontSize: '10px',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          background: email.emailType === 'interview_invite' ? '#d4edda' :
                                                      email.emailType === 'offer' ? '#fff3cd' :
                                                      email.emailType === 'rejection' ? '#f8d7da' :
                                                      email.emailType === 'application_confirmation' ? '#cce5ff' :
                                                      '#f5f5f5',
                                          color: email.emailType === 'interview_invite' ? '#155724' :
                                                 email.emailType === 'offer' ? '#856404' :
                                                 email.emailType === 'rejection' ? '#721c24' :
                                                 email.emailType === 'application_confirmation' ? '#004085' :
                                                 '#6c757d',
                                          textTransform: 'capitalize'
                                        }}>
                                          {email.emailType.replace(/_/g, ' ')}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            )}

                            {/* Company Grouped View */}
                            {emailsViewMode === 'company' && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {Object.entries(
                                  applicationEmails
                                    .filter(email => {
                                      if (emailFilter === 'all') return true;
                                      if (emailFilter === 'unread') return !email.isRead;
                                      return email.emailType === emailFilter;
                                    })
                                    .reduce((acc, email) => {
                                      const company = email.matchedCompany || 'Unknown';
                                      if (!acc[company]) acc[company] = [];
                                      acc[company].push(email);
                                      return acc;
                                    }, {})
                                ).sort((a, b) => {
                                  // Sort by most recent email date
                                  const aLatest = new Date(Math.max(...a[1].map(e => new Date(e.dateReceived))));
                                  const bLatest = new Date(Math.max(...b[1].map(e => new Date(e.dateReceived))));
                                  return bLatest - aLatest;
                                }).map(([company, emails]) => (
                                  <div key={company} style={{ border: '1px solid #e5e5e5', borderRadius: '8px', overflow: 'hidden' }}>
                                    <div style={{
                                      padding: '12px 16px',
                                      background: '#fafafa',
                                      borderBottom: '1px solid #e5e5e5',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#3d3d3d' }}>{company}</span>
                                        <span style={{ fontSize: '12px', color: '#7a7a7a' }}>({emails.length} email{emails.length !== 1 ? 's' : ''})</span>
                                      </div>
                                      {emails.some(e => !e.isRead) && (
                                        <span style={{ fontSize: '10px', background: '#0288d1', color: 'white', padding: '2px 6px', borderRadius: '10px' }}>
                                          {emails.filter(e => !e.isRead).length} new
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      {emails.slice(0, 5).map((email, idx) => (
                                        <div
                                          key={email.id}
                                          onClick={async () => {
                                            setSelectedEmail(email);
                                            if (!email.isRead) {
                                              await updateEmailRead(email.id, true);
                                              setApplicationEmails(prev => prev.map(e =>
                                                e.id === email.id ? { ...e, isRead: true } : e
                                              ));
                                              setUnreadEmailCount(prev => Math.max(0, prev - 1));
                                            }
                                          }}
                                          style={{
                                            padding: '10px 16px',
                                            borderBottom: idx < Math.min(emails.length - 1, 4) ? '1px solid #f0f0f0' : 'none',
                                            cursor: 'pointer',
                                            background: email.isRead ? 'white' : '#f8fbff',
                                            transition: 'background 0.15s'
                                          }}
                                          onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.background = email.isRead ? 'white' : '#f8fbff'; }}
                                        >
                                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                            <span style={{ fontWeight: email.isRead ? '400' : '500', fontSize: '12px', color: '#3d3d3d' }}>
                                              {email.subject.length > 50 ? email.subject.substring(0, 50) + '...' : email.subject}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                              {new Date(email.dateReceived).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                      {emails.length > 5 && (
                                        <div style={{ padding: '8px 16px', fontSize: '12px', color: '#0288d1', textAlign: 'center' }}>
                                          +{emails.length - 5} more emails
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}

                        {/* Disconnect Gmail option */}
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e5e5', textAlign: 'center' }}>
                          <button
                            onClick={async () => {
                              await disconnectGmail(userId);
                              setGmailConnected(false);
                              setApplicationEmails([]);
                              setEmailsLoaded(false);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#9ca3af',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            Disconnect Gmail
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Email Detail Modal */}
                {selectedEmail && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                      padding: '20px'
                    }}
                    onClick={() => setSelectedEmail(null)}
                  >
                    <div
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{
                        padding: '20px',
                        borderBottom: '1px solid #e5e5e5',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div>
                          <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#3d3d3d' }}>{selectedEmail.subject}</h3>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#3d3d3d' }}>
                              {selectedEmail.fromName || selectedEmail.fromEmail}
                            </span>
                            {selectedEmail.matchedCompany && (
                              <span style={{ fontSize: '11px', background: '#e8f4fc', color: '#0288d1', padding: '2px 6px', borderRadius: '4px' }}>
                                {selectedEmail.matchedCompany}
                              </span>
                            )}
                            <span style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: selectedEmail.emailType === 'interview_invite' ? '#d4edda' :
                                          selectedEmail.emailType === 'offer' ? '#fff3cd' :
                                          selectedEmail.emailType === 'rejection' ? '#f8d7da' :
                                          '#f5f5f5',
                              color: selectedEmail.emailType === 'interview_invite' ? '#155724' :
                                     selectedEmail.emailType === 'offer' ? '#856404' :
                                     selectedEmail.emailType === 'rejection' ? '#721c24' :
                                     '#6c757d',
                              textTransform: 'capitalize'
                            }}>
                              {selectedEmail.emailType.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                            {new Date(selectedEmail.dateReceived).toLocaleString('en-US', {
                              weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedEmail(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: '#9ca3af',
                            padding: 0
                          }}
                        >
                          &times;
                        </button>
                      </div>
                      <div style={{ padding: '20px' }}>
                        <div style={{ fontSize: '13px', color: '#3d3d3d', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                          {selectedEmail.bodyPreview || selectedEmail.snippet || 'No preview available'}
                        </div>

                        {/* Link to matching job */}
                        {selectedEmail.matchedJobId && (
                          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e5e5' }}>
                            <span style={{ fontSize: '11px', color: '#7a7a7a' }}>Linked to:</span>
                            <button
                              onClick={() => {
                                const job = jobLedger.find(j => j.id === selectedEmail.matchedJobId);
                                if (job) {
                                  setSelectedEmail(null);
                                  setActiveView('opportunities');
                                  // Would need to select the job - could add this functionality
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#0288d1',
                                cursor: 'pointer',
                                fontSize: '12px',
                                marginLeft: '4px'
                              }}
                            >
                              View Job
                            </button>
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                          <button
                            onClick={async () => {
                              await archiveEmail(selectedEmail.id);
                              setApplicationEmails(prev => prev.filter(e => e.id !== selectedEmail.id));
                              setSelectedEmail(null);
                            }}
                            style={{
                              padding: '8px 16px',
                              fontSize: '12px',
                              background: '#f5f5f5',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: '#7a7a7a'
                            }}
                          >
                            Archive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* DEVELOPMENT VIEW */}
        {activeView === 'development' && (
          <div className="fade-in">
            {/* Pro Feature Gate Banner - bypassed for specific users */}
            {effectiveTier !== 'pro' && profile?.email !== 'loricorpuz@gmail.com' && (devSubTab === 'planning' || devSubTab === 'projects') && (
              <div style={{
                background: 'linear-gradient(135deg, #fdf4d7 0%, #fff5f5 100%)',
                border: '2px solid #f7c948',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center',
              }}>
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><InsightsIcon size={32} /></div>
                <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600', color: '#3d3d3d' }}>
                  Skill Development is a Pro Feature
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#5a5a5a' }}>
                  Get personalized project recommendations and skill development planning to close your gaps faster.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setPaywallFeature('development');
                    setShowPaywall(true);
                  }}
                  style={{ padding: '10px 24px' }}
                >
                  Upgrade to Pro
                </button>
              </div>
            )}

            {/* Sub-tabs */}
            <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '2px solid #e5e5e5', paddingBottom: '0' }}>
              {[
                { id: 'planning', label: '📐 Planning', count: null, pro: true },
                { id: 'skills', label: 'Skills', count: null },
                { id: 'projects', label: 'Projects', count: profile.currentProjects?.filter(p => p.status === 'active').length || 0, pro: true },
                { id: 'targets', label: '★ Targets', count: `${jobLedger.filter(j => j.isTarget).length}/5`, accent: true },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setDevSubTab(tab.id)}
                  style={{
                    padding: '10px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: devSubTab === tab.id ? '2px solid #f7c948' : '2px solid transparent',
                    marginBottom: '-2px',
                    color: devSubTab === tab.id ? '#f7c948' : '#6b7280',
                    fontSize: '13px',
                    fontWeight: devSubTab === tab.id ? '600' : '500',
                    cursor: 'pointer',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    opacity: tab.pro && effectiveTier !== 'pro' && profile?.email !== 'loricorpuz@gmail.com' ? 0.6 : 1,
                  }}
                >
                  {tab.label}{tab.count !== null && ` (${tab.count})`}
                  {tab.pro && effectiveTier !== 'pro' && profile?.email !== 'loricorpuz@gmail.com' && <span style={{ marginLeft: '4px' }}><SmallLockIcon size={12} /></span>}
                </button>
              ))}
            </div>
            
            {/* TARGETS SUB-TAB */}
            {devSubTab === 'targets' && (
              <div className="fade-in">
                {(() => {
                  const targetJobs = jobLedger.filter(j => j.isTarget);
                  const nonTargetJobs = jobLedger.filter(j => !j.isTarget);
                  
                  // Collect gaps from target jobs
                  const targetGapSkills = {};
                  targetJobs.forEach(job => {
                    job.analysis?.gaps?.forEach(gap => {
                      if (!targetGapSkills[gap.skill]) {
                        targetGapSkills[gap.skill] = { count: 0, companies: [], importance: gap.importance };
                      }
                      targetGapSkills[gap.skill].count++;
                      if (!targetGapSkills[gap.skill].companies.includes(job.company)) {
                        targetGapSkills[gap.skill].companies.push(job.company);
                      }
                    });
                  });
                  
                  const sortedGaps = Object.entries(targetGapSkills)
                    .sort((a, b) => b[1].count - a[1].count);
                  
                  return (
                    <>
                      {/* Target Jobs Header */}
                      <div className="card" style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          padding: '16px 20px', 
                          background: 'linear-gradient(135deg, rgba(212, 115, 106, 0.15), rgba(99, 102, 241, 0.05))'
                        }}>
                          <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ color: '#d4736a' }}>★</span> Target Jobs
                          </h2>
                          <p style={{ margin: '6px 0 0', color: '#9ca3af', fontSize: '13px' }}>
                            Your dream roles — projects and planning are weighted to close gaps for these first
                          </p>
                        </div>
                      </div>
                      
                      {/* Current Targets */}
                      <div className="card" style={{ marginBottom: '16px' }}>
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                          <h3 style={{ margin: 0, fontSize: '14px' }}>Current Targets ({targetJobs.length}/5)</h3>
                        </div>
                        <div style={{ padding: '12px' }}>
                          {targetJobs.length === 0 ? (
                            <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '24px' }}>
                              No target jobs selected yet.<br/>
                              <span style={{ fontSize: '12px' }}>Star jobs in the Opportunities tab or select from below.</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {targetJobs.map(job => (
                                <div key={job.id} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '14px 16px',
                                  background: 'rgba(212, 115, 106, 0.1)',
                                  borderRadius: '8px',
                                  borderLeft: '4px solid #d4736a'
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{job.role}</div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>{job.company}</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                                      {(job.analysis?.gaps || []).map((gap, i) => (
                                        <span key={i} className="tag tag-yellow" style={{ fontSize: '10px', padding: '2px 6px' }}>
                                          {gap.skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Match</div>
                                      <div style={{ 
                                        fontSize: '16px', 
                                        fontWeight: '600',
                                        color: job.matchScore >= 80 ? '#5a8f5a' : job.matchScore >= 60 ? '#d4736a' : '#f87171'
                                      }}>
                                        {job.matchScore}%
                                      </div>
                                    </div>
                                    <span className={`tag status-${job.status}`} style={{ fontSize: '10px', padding: '3px 8px' }}>
                                      {job.status}
                                    </span>
                                    <button
                                      onClick={() => toggleTargetJob(job.id)}
                                      style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(212, 115, 106, 0.5)',
                                        color: '#d4736a',
                                        padding: '6px 10px',
                                        fontSize: '11px',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Priority Skills Summary */}
                      {sortedGaps.length > 0 && (
                        <div className="card" style={{ marginBottom: '16px' }}>
                          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                            <h3 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallTargetIcon size={16} /> Skills to Unlock Targets</h3>
                            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '11px' }}>
                              Gaps across your target jobs — sorted by frequency
                            </p>
                          </div>
                          <div style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {sortedGaps.map(([skill, data]) => (
                                <div key={skill} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  padding: '10px 12px',
                                  background: '#f9fafb',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span className="tag tag-purple" style={{ fontSize: '12px', padding: '4px 10px' }}>{skill}</span>
                                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                      {data.companies.join(', ')}
                                    </span>
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    fontWeight: '600',
                                    color: data.count >= 3 ? '#f87171' : data.count >= 2 ? '#d4736a' : '#9ca3af'
                                  }}>
                                    {data.count}/{targetJobs.length} targets
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Add More Targets */}
                      {targetJobs.length < 5 && nonTargetJobs.length > 0 && (
                        <div className="card">
                          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                            <h3 style={{ margin: 0, fontSize: '14px' }}>Add More Targets</h3>
                            <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '11px' }}>
                              Select from your tracked jobs ({5 - targetJobs.length} slots remaining)
                            </p>
                          </div>
                          <div style={{ padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {nonTargetJobs.map(job => (
                                <div key={job.id} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '10px 12px',
                                  background: '#f9fafb',
                                  borderRadius: '6px'
                                }}>
                                  <div>
                                    <div style={{ fontWeight: '500', fontSize: '13px' }}>{job.role}</div>
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{job.company}</div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ 
                                      fontSize: '12px',
                                      color: job.matchScore >= 80 ? '#5a8f5a' : job.matchScore >= 60 ? '#d4736a' : '#f87171'
                                    }}>
                                      {job.matchScore}%
                                    </span>
                                    <button
                                      onClick={() => toggleTargetJob(job.id)}
                                      style={{
                                        background: 'linear-gradient(135deg, #f7c948, #c9a87c)',
                                        border: 'none',
                                        color: 'white',
                                        padding: '6px 12px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      ★ Add
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* PLANNING SUB-TAB */}
            {devSubTab === 'planning' && (
              <>
                {(() => {
                  // Get target jobs
                  const targetJobs = jobLedger.filter(j => j.isTarget);
                  
                  // Collect ALL gaps from target jobs (these are priority)
                  const targetGapSkills = new Set();
                  targetJobs.forEach(job => {
                    job.analysis?.gaps?.forEach(gap => targetGapSkills.add(gap.skill));
                  });
                  
                  // Get suggested projects - prioritize those that address target job gaps
                  const allProjects = jobLedger
                    .filter(j => j.status === 'deferred' && j.suggestedProjects?.length > 0)
                    .flatMap(j => j.suggestedProjects.map(p => ({ 
                      ...p, 
                      fromJob: j.company,
                      isTargetJob: j.isTarget,
                      addressesTargetGaps: (p.targetGaps || []).some(g => targetGapSkills.has(g))
                    })));
                  
                  // Consolidate by primary skill, but track if it addresses target gaps
                  const projectsBySkill = {};
                  allProjects.forEach(p => {
                    const skill = p.targetGaps?.[0] || 'General';
                    if (!projectsBySkill[skill]) {
                      projectsBySkill[skill] = { 
                        ...p, 
                        companies: [p.fromJob], 
                        allGaps: new Set(p.targetGaps || []),
                        addressesTargetGaps: p.addressesTargetGaps,
                        fromTargetJobs: p.isTargetJob ? [p.fromJob] : []
                      };
                    } else {
                      if (!projectsBySkill[skill].companies.includes(p.fromJob)) {
                        projectsBySkill[skill].companies.push(p.fromJob);
                      }
                      if (p.isTargetJob && !projectsBySkill[skill].fromTargetJobs.includes(p.fromJob)) {
                        projectsBySkill[skill].fromTargetJobs.push(p.fromJob);
                      }
                      (p.targetGaps || []).forEach(g => projectsBySkill[skill].allGaps.add(g));
                      if ((p.salaryImpact || 0) > (projectsBySkill[skill].salaryImpact || 0)) {
                        projectsBySkill[skill].salaryImpact = p.salaryImpact;
                      }
                      projectsBySkill[skill].addressesTargetGaps = projectsBySkill[skill].addressesTargetGaps || p.addressesTargetGaps;
                    }
                  });
                  
                  // Create timeline - PRIORITIZE target job gaps, then by timeframe/impact
                  const timeline = Object.entries(projectsBySkill)
                    .map(([skill, p]) => ({ ...p, primarySkill: skill, allGaps: Array.from(p.allGaps) }))
                    .sort((a, b) => {
                      // Target job gaps first
                      if (a.addressesTargetGaps && !b.addressesTargetGaps) return -1;
                      if (!a.addressesTargetGaps && b.addressesTargetGaps) return 1;
                      // Then by timeframe (quick wins first)
                      if (a.timeframe === '2 days' && b.timeframe !== '2 days') return -1;
                      if (a.timeframe !== '2 days' && b.timeframe === '2 days') return 1;
                      // Then by impact
                      return (b.salaryImpact || 0) - (a.salaryImpact || 0);
                    });
                  
                  // Calculate cumulative value and days
                  let cumulativeValue = 0;
                  let cumulativeDays = 0;
                  const timelineWithValue = timeline.map(p => {
                    cumulativeValue += (p.salaryImpact || 0);
                    const days = p.timeframe === '2 days' ? 2 : 7;
                    cumulativeDays += days;
                    return { ...p, cumulativeValue, cumulativeDays, days };
                  });
                  
                  const currentSalary = profile.currentSalary || 187000;
                  const projectedSalary = currentSalary + cumulativeValue;
                  const totalDays = cumulativeDays || 14;
                  
                  // Commit project function
                  const commitProject = (project) => {
                    const newProject = {
                      id: Date.now(),
                      name: project.name,
                      description: project.description,
                      category: project.primarySkill || 'Skill Development',
                      skills: project.allGaps || project.targetGaps || [],
                      status: 'active',
                      impact: `Unlocks: ${project.companies?.join(', ')} (+$${((project.salaryImpact || 0)/1000).toFixed(0)}K)`,
                      startedAt: new Date().toISOString()
                    };
                    setProfile(prev => ({
                      ...prev,
                      currentProjects: [...(prev.currentProjects || []), newProject]
                    }));
                  };
                  
                  return (
                    <>
                      {/* Executive Gantt Chart */}
                      <div className="card" style={{ marginBottom: '16px' }}>
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                          <h3 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><InsightsIcon size={16} /> Skill Development Roadmap</h3>
                          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '11px' }}>
                            {timelineWithValue.length} projects • {totalDays} days to +${(cumulativeValue/1000).toFixed(0)}K
                            {targetJobs.length > 0 && <span style={{ color: '#d4736a' }}> • Weighted for {targetJobs.length} target job{targetJobs.length !== 1 ? 's' : ''}</span>}
                          </p>
                        </div>
                        <div style={{ padding: '16px' }}>
                          {timelineWithValue.length === 0 ? (
                            <div style={{ color: '#6b7280', fontSize: '12px', textAlign: 'center', padding: '24px' }}>
                              Defer some job applications to build your development roadmap
                            </div>
                          ) : (
                            <>
                              {/* Salary Scale */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
                                <div>
                                  <div style={{ color: '#6b7280' }}>Last Salary</div>
                                  <div style={{ color: '#9ca3af', fontWeight: '600', fontSize: '16px' }}>${(currentSalary/1000).toFixed(0)}K</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ color: '#6b7280' }}>Target After Plan</div>
                                  <div style={{ color: '#5a8f5a', fontWeight: '600', fontSize: '16px' }}>${(projectedSalary/1000).toFixed(0)}K</div>
                                </div>
                              </div>
                              
                              {/* Main Gantt Area */}
                              <div style={{ 
                                background: '#f8f7f4', 
                                borderRadius: '8px', 
                                padding: '16px',
                                border: '1px solid #e5e5e5'
                              }}>
                                {/* Timeline header */}
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  marginBottom: '12px',
                                  paddingBottom: '8px',
                                  borderBottom: '1px solid #e5e5e5',
                                  fontSize: '10px',
                                  color: '#6b7280'
                                }}>
                                  <span>Day 1</span>
                                  <span>Day {Math.ceil(totalDays / 2)}</span>
                                  <span>Day {totalDays}</span>
                                </div>
                                
                                {/* Project bars */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {timelineWithValue.map((project, i) => {
                                    const isCommitted = profile.currentProjects?.some(p => p.name === project.name);
                                    const startPct = ((project.cumulativeDays - project.days) / totalDays) * 100;
                                    const widthPct = (project.days / totalDays) * 100;
                                    const isTargetProject = project.addressesTargetGaps;
                                    
                                    return (
                                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {/* Target indicator */}
                                        <div style={{ width: '16px', textAlign: 'center' }}>
                                          {isTargetProject && <span style={{ color: '#d4736a', fontSize: '10px' }}>★</span>}
                                        </div>
                                        
                                        {/* Project name */}
                                        <div style={{ 
                                          width: '130px', 
                                          fontSize: '11px', 
                                          fontWeight: '500',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          color: isCommitted ? '#5a8f5a' : isTargetProject ? '#f7c948' : '#1a1a1a'
                                        }}>
                                          {isCommitted && '✓ '}{project.name.length > 16 ? project.name.slice(0, 16) + '...' : project.name}
                                        </div>
                                        
                                        {/* Bar area */}
                                        <div style={{ flex: 1, height: '28px', position: 'relative' }}>
                                          {/* Bar */}
                                          <div style={{
                                            position: 'absolute',
                                            left: `${startPct}%`,
                                            width: `${widthPct}%`,
                                            height: '100%',
                                            background: isCommitted 
                                              ? 'linear-gradient(90deg, #5a8f5a, #6ba36b)'
                                              : isTargetProject
                                                ? 'linear-gradient(90deg, #d4736a, #e8a0a0)'
                                                : project.timeframe === '2 days' 
                                                  ? 'linear-gradient(90deg, #f7c948, #f9d97a)' 
                                                  : 'linear-gradient(90deg, #c9a87c, #d4b896)',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: isTargetProject || project.timeframe === '2 days' ? '#3d3d3d' : 'white',
                                            boxShadow: isTargetProject ? '0 2px 8px rgba(212, 115, 106, 0.4)' : '0 2px 4px rgba(0,0,0,0.15)'
                                          }}>
                                            {project.timeframe}
                                          </div>
                                          
                                          {/* Milestone marker at end */}
                                          <div style={{
                                            position: 'absolute',
                                            left: `${startPct + widthPct}%`,
                                            top: '-4px',
                                            transform: 'translateX(-50%)',
                                            fontSize: '9px',
                                            color: '#5a8f5a',
                                            fontWeight: '600',
                                            whiteSpace: 'nowrap'
                                          }}>
                                            +${(project.salaryImpact/1000).toFixed(0)}K
                                          </div>
                                        </div>
                                        
                                        {/* Cumulative */}
                                        <div style={{ 
                                          width: '60px', 
                                          textAlign: 'right',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          color: '#5a8f5a'
                                        }}>
                                          ${(project.cumulativeValue/1000).toFixed(0)}K
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {/* Summary bar */}
                                <div style={{ 
                                  marginTop: '16px', 
                                  paddingTop: '12px', 
                                  borderTop: '1px solid #e5e5e5',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                    Complete all {timelineWithValue.length} projects in ~{totalDays} days
                                  </div>
                                  <div style={{ 
                                    padding: '6px 12px', 
                                    background: 'linear-gradient(135deg, #5a8f5a, #6ba36b)',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: 'white'
                                  }}>
                                    Total: +${(cumulativeValue/1000).toFixed(0)}K → ${(projectedSalary/1000).toFixed(0)}K
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Detailed Development Plan */}
                      <div className="card">
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                          <h3 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallClipboardIcon size={16} /> Development Plan Details</h3>
                          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '11px' }}>
                            Click to commit and track in My Projects
                          </p>
                        </div>
                        <div style={{ padding: '12px' }}>
                          {timelineWithValue.length === 0 ? (
                            <div style={{ color: '#6b7280', fontSize: '12px', textAlign: 'center', padding: '16px' }}>
                              No projects to show yet
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {timelineWithValue.map((project, i) => {
                                const isCommitted = profile.currentProjects?.some(p => p.name === project.name);
                                const isTargetProject = project.addressesTargetGaps;
                                return (
                                  <div key={i} style={{ 
                                    padding: '14px', 
                                    background: isCommitted ? 'rgba(16, 185, 129, 0.1)' : isTargetProject ? 'rgba(212, 115, 106, 0.05)' : '#f9fafb',
                                    borderRadius: '8px',
                                    border: isCommitted ? '1px solid rgba(16, 185, 129, 0.3)' : isTargetProject ? '1px solid rgba(212, 115, 106, 0.3)' : '1px solid #e5e5e5'
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ 
                                            width: '24px', 
                                            height: '24px', 
                                            borderRadius: '50%', 
                                            background: isCommitted ? '#6ba36b' : isTargetProject ? '#d4736a' : '#c9a87c',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            color: 'white'
                                          }}>
                                            {isCommitted ? '✓' : isTargetProject ? '★' : (i + 1)}
                                          </span>
                                          <span style={{ fontWeight: '600', fontSize: '14px' }}>{project.name}</span>
                                          {isTargetProject && (
                                            <span style={{ fontSize: '9px', color: '#d4736a', background: 'rgba(212, 115, 106, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>
                                              Target Priority
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '6px', marginLeft: '32px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                          <SmallLockIcon size={10} /> Unlocks: {project.companies?.map((c, ci) => {
                                            const isTargetCompany = project.fromTargetJobs?.includes(c);
                                            return (
                                              <span key={ci} className={isTargetCompany ? 'tag tag-purple' : 'tag tag-gray'} style={{ fontSize: '9px', padding: '1px 5px', marginLeft: ci > 0 ? '4px' : '0' }}>
                                                {isTargetCompany && '★ '}{c}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'right' }}>
                                        <span className={`tag ${project.timeframe === '2 days' ? 'tag-blue' : 'tag-green'}`} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                          {project.timeframe}
                                        </span>
                                        <div style={{ color: '#5a8f5a', fontWeight: '600', fontSize: '16px', marginTop: '4px' }}>
                                          +${(project.salaryImpact/1000).toFixed(0)}K
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <p style={{ margin: '0 0 10px 32px', color: '#9ca3af', fontSize: '12px', lineHeight: '1.5' }}>
                                      {project.description}
                                    </p>
                                    
                                    {/* Skills */}
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '32px', marginBottom: '10px' }}>
                                      {(project.allGaps || project.targetGaps || []).map((gap, j) => (
                                        <span key={j} className="tag tag-yellow" style={{ fontSize: '9px', padding: '2px 5px' }}>{gap}</span>
                                      ))}
                                    </div>
                                    
                                    {/* Commit button */}
                                    <div style={{ marginLeft: '32px' }}>
                                      {!isCommitted ? (
                                        <button
                                          onClick={() => commitProject(project)}
                                          style={{
                                            padding: '8px 16px',
                                            background: 'linear-gradient(135deg, #f7c948, #c9a87c)',
                                            border: 'none',
                                            borderRadius: '6px',
                                            color: 'white',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          ✓ Commit to Project
                                        </button>
                                      ) : (
                                        <span style={{ color: '#5a8f5a', fontSize: '12px', fontWeight: '500' }}>
                                          ✓ Committed — tracking in My Projects
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
            
            {/* SKILLS SUB-TAB */}
            {devSubTab === 'skills' && (
              <>
                {/* AI Research Lab Skill Heatmap - for AI Research focused users */}
                <SkillHeatmap
                  userSkills={profile.skills || []}
                  selectedLabs={['openai', 'anthropic', 'deepmind', 'metaFair']}
                  viewMode="salary"
                />

                {/* AI Research Gap Analysis */}
                <AIResearchGapAnalysis
                  userSkills={profile.skills || []}
                  targetLabs={['openai', 'anthropic', 'deepmind', 'metaFair']}
                  maxSkills={10}
                />

                {(() => {
                  const gapCounts = {};
                  const gapJobs = {};
                  jobLedger.forEach(job => {
                    job.analysis?.gaps?.forEach(gap => {
                      const skill = gap.skill;
                      gapCounts[skill] = (gapCounts[skill] || 0) + 1;
                      if (!gapJobs[skill]) gapJobs[skill] = [];
                      gapJobs[skill].push(job.company);
                    });
                  });
                  const sortedGaps = Object.entries(gapCounts).sort((a, b) => b[1] - a[1]).slice(0, 10);
                  
                  return (
                    <>
                      {/* Your Skills */}
                      <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><ToolsIcon size={16} /> Your Skills</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {profile.skills?.map((skill, i) => (
                            <span key={i} className="tag tag-green" style={{ fontSize: '11px', padding: '4px 8px' }}>{skill}</span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Priority Skills from Job Search */}
                      {sortedGaps.length > 0 && (
                        <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
                          <h3 style={{ margin: '0 0 8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallTargetIcon size={16} /> Priority Skills from Job Search</h3>
                          <p style={{ margin: '0 0 12px', color: '#9ca3af', fontSize: '11px' }}>
                            Skills that appeared as gaps across analyzed jobs
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {sortedGaps.map(([skill, count]) => {
                              const impact = SKILL_SALARY_IMPACT[skill]?.impact || 15000;
                              return (
                                <div key={skill} style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  padding: '10px 12px',
                                  background: count >= 2 ? 'rgba(251, 191, 36, 0.1)' : '#f9fafb',
                                  border: count >= 2 ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid #e5e5e5',
                                  borderRadius: '6px'
                                }}>
                                  <div>
                                    <div style={{ fontWeight: '500', fontSize: '13px' }}>{skill}</div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                      {gapJobs[skill].slice(0, 2).join(', ')}{gapJobs[skill].length > 2 ? ` +${gapJobs[skill].length - 2}` : ''}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: '#5a8f5a', fontWeight: '600', fontSize: '15px' }}>+${(impact/1000).toFixed(0)}K</div>
                                    <div style={{ fontSize: '10px', color: count >= 2 ? '#d4736a' : '#9ca3af' }}>
                                      {count} job{count > 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* High-Value Skills */}
                      <div className="card" style={{ padding: '16px', marginBottom: '12px' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><InsightsIcon size={16} /> High-Value Skills to Learn</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {Object.entries(SKILL_SALARY_IMPACT)
                            .filter(([skill]) => !profile.skills?.includes(skill))
                            .sort((a, b) => b[1].impact - a[1].impact)
                            .slice(0, 8)
                            .map(([skill, data]) => (
                              <div key={skill} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 12px',
                                background: '#f9fafb',
                                borderRadius: '6px'
                              }}>
                                <span style={{ fontSize: '13px' }}>{skill}</span>
                                <span style={{ color: '#5a8f5a', fontWeight: '600' }}>+${(data.impact/1000).toFixed(0)}K</span>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                      
                      {/* Salary by Category */}
                      <div className="card" style={{ padding: '16px' }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><InsightsIcon size={16} /> Salary Impact by Category</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                          {['core', 'infrastructure', 'emerging', 'frontend'].map(category => {
                            const skills = Object.entries(SKILL_SALARY_IMPACT).filter(([_, d]) => d.category === category);
                            const avgImpact = skills.reduce((sum, [_, d]) => sum + d.impact, 0) / skills.length;
                            return (
                              <div key={category} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                                  {category}
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: '600', color: '#a5b4fc' }}>
                                  +${(avgImpact/1000).toFixed(0)}K
                                </div>
                                <div style={{ fontSize: '10px', color: '#9ca3af' }}>avg per skill</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
            
            {/* PROJECTS SUB-TAB */}
            {devSubTab === 'projects' && (
              <>
                {/* Learning Paths Section */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📚</span> Learning Paths
                  </h3>
                  <LearningPathCard
                    course={STANFORD_RL_COURSE}
                    userProgress={{}}
                    onModuleToggle={(courseId, week) => {
                      console.log('Toggle module:', courseId, week);
                      // TODO: Persist progress to database
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr', gap: '12px' }}>
                {/* Left: Suggested Projects (4/5) */}
                <div>
                  {(() => {
                    // Get all projects from deferred jobs
                    const allProjects = jobLedger
                      .filter(j => j.status === 'deferred' && j.suggestedProjects?.length > 0)
                      .flatMap(j => j.suggestedProjects.map(p => ({ ...p, fromJob: j.company })));
                    
                    // Consolidate by primary skill gap + timeframe
                    const consolidated = {};
                    allProjects.forEach(project => {
                      const primaryGap = project.targetGaps?.[0] || 'General';
                      const key = `${primaryGap}-${project.timeframe}`;
                      
                      if (!consolidated[key]) {
                        consolidated[key] = {
                          ...project,
                          companies: [project.fromJob],
                          allGaps: new Set(project.targetGaps || [])
                        };
                      } else {
                        if (!consolidated[key].companies.includes(project.fromJob)) {
                          consolidated[key].companies.push(project.fromJob);
                        }
                        (project.targetGaps || []).forEach(g => consolidated[key].allGaps.add(g));
                        if ((project.salaryImpact || 0) > (consolidated[key].salaryImpact || 0)) {
                          consolidated[key].salaryImpact = project.salaryImpact;
                          consolidated[key].name = project.name;
                          consolidated[key].description = project.description;
                        }
                      }
                    });
                    
                    // Convert to array
                    const projects = Object.values(consolidated)
                      .map(p => ({ ...p, allGaps: Array.from(p.allGaps) }));
                    
                    const quickProjects = projects.filter(p => p.timeframe === '2 days');
                    const deepProjects = projects.filter(p => p.timeframe !== '2 days');
                    const uniqueCompanies = new Set(allProjects.map(p => p.fromJob)).size;
                    
                    return (
                      <div className="card">
                        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e5e5' }}>
                          <h3 style={{ margin: 0, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><SmallLightbulbIcon size={16} /> Suggested Projects</h3>
                          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '11px' }}>
                            {projects.length} unique projects from {uniqueCompanies} below-80% role{uniqueCompanies !== 1 ? 's' : ''}
                          </p>
                        </div>
                        
                        {projects.length === 0 ? (
                          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '12px' }}>
                            No suggested projects yet. Analyze jobs below 80% match to get personalized project recommendations.
                          </div>
                        ) : (
                          <div style={{ padding: '12px' }}>
                            {/* 2-Day Sprints */}
                            {quickProjects.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '10px', color: '#a5b4fc', marginBottom: '10px', textTransform: 'uppercase', fontWeight: '600' }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><SmallLightningIcon size={12} /> 2-Day Sprints ({quickProjects.length})</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  {quickProjects.map((project, i) => (
                                    <div key={i} style={{ 
                                      padding: '14px', 
                                      background: '#f9fafb', 
                                      borderRadius: '8px',
                                      border: project.companies.length > 1 ? '2px solid rgba(251, 191, 36, 0.4)' : '1px solid rgba(96, 165, 250, 0.3)'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>{project.name}</h4>
                                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                            <span style={{ color: project.companies.length > 1 ? '#d4736a' : '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                              {project.companies.length > 1 ? <><SmallLockIcon size={10} /> Unlocks:</> : 'From:'}
                                            </span>
                                            {project.companies.map((c, ci) => (
                                              <span key={ci} className="tag tag-gray" style={{ fontSize: '9px', padding: '1px 5px' }}>{c}</span>
                                            ))}
                                          </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                          <span className="tag tag-blue" style={{ fontSize: '10px', padding: '2px 6px' }}>2 days</span>
                                          {project.salaryImpact && (
                                            <div style={{ color: '#5a8f5a', fontWeight: '600', fontSize: '16px', marginTop: '4px' }}>
                                              +${(project.salaryImpact/1000).toFixed(0)}K
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <p style={{ margin: '0 0 8px', color: '#4b5563', fontSize: '12px', lineHeight: '1.4' }}>{project.description}</p>
                                      {project.extendsExperience && (
                                        <div style={{ padding: '6px 8px', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '4px', border: '1px solid rgba(96, 165, 250, 0.2)', marginBottom: '8px', fontSize: '11px' }}>
                                          <span style={{ color: '#3b82f6', fontWeight: '600' }}>🔗 Builds on:</span> <span style={{ color: '#1e40af' }}>{project.extendsExperience}</span>
                                        </div>
                                      )}
                                      {project.resumeBullet && (
                                        <div style={{ padding: '6px 8px', background: 'rgba(52, 211, 153, 0.1)', borderRadius: '4px', border: '1px solid rgba(52, 211, 153, 0.2)', marginBottom: '8px', fontSize: '11px' }}>
                                          <span style={{ color: '#059669', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><ResumeIcon size={11} /> Resume:</span> <span style={{ color: '#047857', fontStyle: 'italic' }}>"{project.resumeBullet}"</span>
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {project.allGaps?.map((gap, j) => (
                                          <span key={j} className="tag tag-yellow" style={{ fontSize: '10px', padding: '2px 6px' }}>{gap}</span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 7-Day Deep Dives */}
                            {deepProjects.length > 0 && (
                              <div>
                                <div style={{ fontSize: '10px', color: '#5a8f5a', marginBottom: '10px', textTransform: 'uppercase', fontWeight: '600' }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><SmallRocketIcon size={12} /> 7-Day Deep Dives ({deepProjects.length})</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  {deepProjects.map((project, i) => (
                                    <div key={i} style={{
                                      padding: '14px',
                                      background: '#f9fafb',
                                      borderRadius: '8px',
                                      border: project.companies.length > 1 ? '2px solid rgba(251, 191, 36, 0.4)' : '1px solid rgba(52, 211, 153, 0.3)'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>{project.name}</h4>
                                          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                            <span style={{ color: project.companies.length > 1 ? '#d4736a' : '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                              {project.companies.length > 1 ? <><SmallLockIcon size={10} /> Unlocks:</> : 'From:'}
                                            </span>
                                            {project.companies.map((c, ci) => (
                                              <span key={ci} className="tag tag-gray" style={{ fontSize: '9px', padding: '1px 5px' }}>{c}</span>
                                            ))}
                                          </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                          <span className="tag tag-green" style={{ fontSize: '10px', padding: '2px 6px' }}>{project.timeframe}</span>
                                          {project.salaryImpact && (
                                            <div style={{ color: '#5a8f5a', fontWeight: '600', fontSize: '16px', marginTop: '4px' }}>
                                              +${(project.salaryImpact/1000).toFixed(0)}K
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <p style={{ margin: '0 0 8px', color: '#4b5563', fontSize: '12px', lineHeight: '1.4' }}>{project.description}</p>
                                      {project.extendsExperience && (
                                        <div style={{ padding: '6px 8px', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '4px', border: '1px solid rgba(96, 165, 250, 0.2)', marginBottom: '8px', fontSize: '11px' }}>
                                          <span style={{ color: '#3b82f6', fontWeight: '600' }}>🔗 Builds on:</span> <span style={{ color: '#1e40af' }}>{project.extendsExperience}</span>
                                        </div>
                                      )}
                                      {project.resumeBullet && (
                                        <div style={{ padding: '6px 8px', background: 'rgba(52, 211, 153, 0.1)', borderRadius: '4px', border: '1px solid rgba(52, 211, 153, 0.2)', marginBottom: '8px', fontSize: '11px' }}>
                                          <span style={{ color: '#059669', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><ResumeIcon size={11} /> Resume:</span> <span style={{ color: '#047857', fontStyle: 'italic' }}>"{project.resumeBullet}"</span>
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {project.allGaps?.map((gap, j) => (
                                          <span key={j} className="tag tag-yellow" style={{ fontSize: '10px', padding: '2px 6px' }}>{gap}</span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Right: Current Projects (1/5) */}
                <div>
                  <div className="card">
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e5e5' }}>
                      <h3 style={{ margin: 0, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}><SmallRocketIcon size={12} /> My Projects</h3>
                    </div>
                    <div style={{ padding: '10px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {profile.currentProjects?.filter(p => p.status === 'active').map(project => (
                          <div key={project.id} style={{ 
                            padding: '10px', 
                            background: '#f9fafb', 
                            borderRadius: '6px',
                            border: '1px solid #e5e5e5'
                          }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px' }}>{project.name}</div>
                            <span className="tag tag-green" style={{ fontSize: '9px', padding: '1px 4px' }}>Active</span>
                            {project.impact && (
                              <div style={{ fontSize: '9px', color: '#6ee7b7', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                <InsightsIcon size={10} /> {project.impact}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Planned */}
                      {profile.currentProjects?.filter(p => p.status === 'planned').length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '9px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>
                            Planned
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {profile.currentProjects?.filter(p => p.status === 'planned').map(project => (
                              <div key={project.id} style={{ 
                                padding: '8px', 
                                background: '#ffffff', 
                                borderRadius: '4px',
                                border: '1px dashed #d1d5db'
                              }}>
                                <div style={{ fontSize: '10px', color: '#9ca3af' }}>{project.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Profile Modal */}
      {showProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(61, 61, 61, 0.95)',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#ffffff',
              borderRadius: '3px',
              borderLeft: '4px solid #f7c948'
            }}>
              <div style={{ fontWeight: '600', color: '#3d3d3d' }}>Settings</div>
              <button
                onClick={() => setShowProfile(false)}
                style={{
                  background: '#3d3d3d',
                  border: 'none',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Close
              </button>
            </div>

            {/* Two-column layout */}
            <div style={{ display: 'flex', gap: '16px' }}>
              {/* Left sidebar */}
              <div style={{ width: '160px', flexShrink: 0 }}>
                <div style={{
                  background: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'sticky',
                  top: '20px'
                }}>
                  {[
                    { id: 'profile', label: 'Profile', icon: <ProfileIcon size={18} /> },
                    { id: 'resume', label: 'Resume', icon: <ResumeIcon size={18} /> },
                    { id: 'insights', label: 'Insights Log', icon: <InsightsIcon size={18} /> },
                    { id: 'data', label: 'Data', icon: <DataIcon size={18} /> },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSettingsSection(item.id)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '13px',
                        fontWeight: settingsSection === item.id ? '600' : '400',
                        color: settingsSection === item.id ? '#3d3d3d' : '#7a7a7a',
                        background: settingsSection === item.id ? '#f5f5f5' : 'transparent',
                        border: 'none',
                        borderLeft: settingsSection === item.id ? '3px solid #f7c948' : '3px solid transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>

                {/* Save button in sidebar */}
                <button
                  onClick={() => {
                    saveProfile(profile);
                    setShowProfile(false);
                  }}
                  className="btn btn-primary"
                  style={{
                    width: '100%',
                    marginTop: '16px',
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  Save
                </button>
              </div>

              {/* Right content area */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* PROFILE SECTION */}
                {settingsSection === 'profile' && (
                  <div className="fade-in">
                    {/* Profile Completion Prompt */}
                    {(!profile.name || !profile.targetRole) && (
                      <div style={{
                        padding: '16px 20px',
                        marginBottom: '16px',
                        background: 'rgba(247, 201, 72, 0.6)',
                        borderRadius: '8px',
                        border: '1px solid rgba(247, 201, 72, 0.3)'
                      }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', color: '#000000' }}>
                          Complete your profile to get started
                        </div>
                        <div style={{ fontSize: '13px', color: '#4b5563' }}>
                          Add your <strong>name</strong> and <strong>target role</strong> to analyze job matches.
                        </div>
                      </div>
                    )}
                    {/* Basic Info */}
                    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                      <h3 style={{ margin: '0 0 16px', fontSize: '14px' }}>Basic Info</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Name</label>
                          <input
                            type="text"
                            className="input"
                            value={profile.name || ''}
                            onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                            placeholder="Your full name"
                            style={{ padding: '8px 12px', fontSize: '13px' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Email</label>
                          <input
                            type="email"
                            className="input"
                            value={profile.email || ''}
                            onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
                            placeholder="you@example.com"
                            style={{ padding: '8px 12px', fontSize: '13px' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Current Title</label>
                          <input
                            type="text"
                            className="input"
                            value={profile.title || ''}
                            onChange={(e) => setProfile(p => ({ ...p, title: e.target.value }))}
                            placeholder="e.g., Senior Software Engineer"
                            style={{ padding: '8px 12px', fontSize: '13px' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Role Tracks & Salary */}
                    <div className="card" style={{ padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '14px' }}>
                          Role Tracks ({(profile.roleTracks || []).length}/{maxTracks})
                        </h3>
                        {(profile.roleTracks || []).length < maxTracks ? (
                          <button onClick={() => setShowAddTrackModal(true)} style={{
                            background: '#f7c948', border: 'none', borderRadius: '4px',
                            padding: '4px 12px', fontSize: '12px', cursor: 'pointer', color: '#3d3d3d',
                          }}>
                            + Add Track
                          </button>
                        ) : effectiveTier !== 'pro' && (
                          <button onClick={() => { setPaywallFeature('tracks'); setShowPaywall(true); }} style={{
                            background: '#f5f5f5', border: '1px solid #e5e5e5', borderRadius: '4px',
                            padding: '4px 12px', fontSize: '11px', cursor: 'pointer', color: '#7a7a7a',
                          }}>
                            Upgrade for more tracks
                          </button>
                        )}
                      </div>

                      {/* TAM/SAM/SOM Visual + Probability */}
                      {(profile.roleTracks || []).length > 0 && (
                        <RoleTrackCircles
                          tracks={profile.roleTracks || []}
                          maxTracks={maxTracks}
                          effectiveTier={effectiveTier}
                        />
                      )}

                      {/* Track Cards */}
                      {(profile.roleTracks || []).map((track, i) => (
                        <div key={track.id} style={{
                          padding: '16px',
                          border: track.id === profile.activeTrackId ? '2px solid #f7c948' : '1px solid #e5e5e5',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          background: track.id === profile.activeTrackId ? 'rgba(247,201,72,0.05)' : '#fff',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <input
                              type="text"
                              value={track.label}
                              onChange={(e) => updateTrack(track.id, { label: e.target.value })}
                              style={{ fontSize: '13px', fontWeight: '600', border: 'none', background: 'transparent', color: '#3d3d3d', outline: 'none', padding: 0 }}
                              placeholder="Track name..."
                            />
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {track.id === profile.activeTrackId && (
                                <span style={{ fontSize: '10px', color: '#f7c948', fontWeight: '600', textTransform: 'uppercase' }}>Active</span>
                              )}
                              {track.id !== profile.activeTrackId && (
                                <button onClick={() => switchTrack(track.id)} style={{
                                  fontSize: '11px', color: '#3d85c6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline',
                                }}>
                                  Set Active
                                </button>
                              )}
                              {(profile.roleTracks || []).length > 1 && (
                                <button onClick={() => removeTrack(track.id)} style={{
                                  fontSize: '11px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer',
                                }}>
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Target Role</label>
                              <input
                                type="text"
                                className="input"
                                value={track.targetRole || ''}
                                onChange={(e) => updateTrack(track.id, { targetRole: e.target.value })}
                                placeholder="e.g., Product Manager"
                                style={{ padding: '8px 12px', fontSize: '13px' }}
                              />
                              <div style={{ marginTop: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {['Product Engineer', 'Software Engineer', 'Product Manager', 'Chief of Staff', 'Data Scientist', 'Project Manager'].map(role => (
                                  <button
                                    key={role}
                                    onClick={() => updateTrack(track.id, { targetRole: role })}
                                    style={{
                                      background: track.targetRole === role ? '#f7c948' : '#f5f5f5',
                                      border: '1px solid #e5e5e5',
                                      borderRadius: '3px',
                                      padding: '2px 6px',
                                      fontSize: '10px',
                                      cursor: 'pointer',
                                      color: '#3d3d3d'
                                    }}
                                  >
                                    {role}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Min Salary</label>
                                <input
                                  type="number"
                                  className="input"
                                  value={track.targetSalaryMin || ''}
                                  onChange={(e) => updateTrack(track.id, { targetSalaryMin: parseInt(e.target.value) || 0 })}
                                  placeholder="e.g., 180000"
                                  style={{ padding: '8px 12px', fontSize: '13px' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>Max Salary</label>
                                <input
                                  type="number"
                                  className="input"
                                  value={track.targetSalaryMax || ''}
                                  onChange={(e) => updateTrack(track.id, { targetSalaryMax: parseInt(e.target.value) || 0 })}
                                  placeholder="e.g., 220000"
                                  style={{ padding: '8px 12px', fontSize: '13px' }}
                                />
                              </div>
                            </div>
                            {/* Market estimate button */}
                            <button
                              onClick={() => estimateTrackMarket(track.id)}
                              style={{
                                fontSize: '11px', color: '#3d85c6', background: 'none', border: '1px solid #e5e5e5',
                                borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', alignSelf: 'flex-start',
                              }}
                            >
                              {track.lastMarketCheck ? 'Refresh Market Estimate' : 'Estimate Market Size'}
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* If no tracks yet (shouldn't happen with migration, but safety) */}
                      {(!profile.roleTracks || profile.roleTracks.length === 0) && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                          <p style={{ fontSize: '13px', margin: '0 0 12px' }}>Set up your first role track to get started.</p>
                          <button onClick={() => setShowAddTrackModal(true)} style={{
                            background: '#f7c948', border: 'none', borderRadius: '4px',
                            padding: '8px 16px', fontSize: '12px', cursor: 'pointer', color: '#3d3d3d',
                          }}>
                            + Add Your First Track
                          </button>
                        </div>
                      )}

                      {/* Last Salary baseline */}
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e5e5' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Last Salary (baseline)</label>
                        <input
                          type="number"
                          className="input"
                          value={profile.currentSalary || ''}
                          onChange={(e) => setProfile(p => ({ ...p, currentSalary: parseInt(e.target.value) || 0 }))}
                          placeholder="e.g., 150000"
                          style={{ padding: '8px 12px', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    {/* Add Track Modal */}
                    {showAddTrackModal && (
                      <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 2000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          background: '#fff', borderRadius: '12px', padding: '24px',
                          width: '90%', maxWidth: '400px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        }}>
                          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#3d3d3d' }}>Add Role Track</h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Track Label</label>
                              <input
                                type="text"
                                className="input"
                                id="new-track-label"
                                placeholder="e.g., Dream Role, Linear Path, Pivot"
                                style={{ padding: '8px 12px', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Target Role</label>
                              <input
                                type="text"
                                className="input"
                                id="new-track-role"
                                placeholder="e.g., Chief of Staff"
                                style={{ padding: '8px 12px', fontSize: '13px' }}
                              />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Min Salary</label>
                                <input type="number" className="input" id="new-track-salary-min" placeholder="180000" style={{ padding: '8px 12px', fontSize: '13px' }} />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>Max Salary</label>
                                <input type="number" className="input" id="new-track-salary-max" placeholder="220000" style={{ padding: '8px 12px', fontSize: '13px' }} />
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setShowAddTrackModal(false)}
                              style={{
                                padding: '8px 16px', fontSize: '13px', background: '#f5f5f5',
                                border: '1px solid #e5e5e5', borderRadius: '6px', cursor: 'pointer', color: '#3d3d3d',
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                const label = document.getElementById('new-track-label')?.value || 'New Track';
                                const role = document.getElementById('new-track-role')?.value || '';
                                const salaryMin = parseInt(document.getElementById('new-track-salary-min')?.value) || 0;
                                const salaryMax = parseInt(document.getElementById('new-track-salary-max')?.value) || 0;
                                if (role) {
                                  addTrack(label, role, salaryMin, salaryMax);
                                  setShowAddTrackModal(false);
                                }
                              }}
                              style={{
                                padding: '8px 16px', fontSize: '13px', background: '#f7c948',
                                border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#3d3d3d', fontWeight: '600',
                              }}
                            >
                              Add Track
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Import Your Profile - Required for onboarding */}
                    {(!profile.experiences || profile.experiences.length === 0) && (
                      <div style={{
                        padding: '16px 20px',
                        marginBottom: '16px',
                        background: 'rgba(247, 201, 72, 0.6)',
                        borderRadius: '8px',
                        border: '1px solid rgba(247, 201, 72, 0.3)'
                      }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px', color: '#000000' }}>
                          Import your work history
                        </div>
                        <div style={{ fontSize: '13px', color: '#4b5563' }}>
                          We need your experience to calculate <strong>match %</strong> against job postings.
                        </div>
                      </div>
                    )}
                    {/* Import Profile Card - In Profile section for onboarding */}
                    <div className="card" style={{ padding: '20px', marginBottom: '16px', borderLeft: '4px solid #f7c948' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Import Your Profile</h3>
                      {!resumeUploadStep && !linkedInStep && !profileImportMethod && (
                        <>
                          <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#7a7a7a' }}>
                            Choose how to import your work history and skills:
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', cursor: 'pointer', border: '1px solid #e5e5e5' }}>
                              <ResumeIcon size={20} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '13px' }}>Upload Resume</div>
                                <div style={{ fontSize: '11px', color: '#7a7a7a' }}>PDF, DOCX, or TXT file</div>
                              </div>
                              <input type="file" accept=".pdf,.txt,.docx" onChange={handleResumeUpload} style={{ display: 'none' }} />
                            </label>
                            <button onClick={() => setProfileImportMethod('linkedin')} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', cursor: 'pointer', border: '1px solid #e5e5e5', textAlign: 'left', width: '100%' }}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="#0077b5"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '13px' }}>Import from LinkedIn</div>
                                <div style={{ fontSize: '11px', color: '#7a7a7a' }}>Copy/paste your profile content</div>
                              </div>
                            </button>
                            <button onClick={() => setProfileImportMethod('manual')} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', cursor: 'pointer', border: '1px solid #e5e5e5', textAlign: 'left', width: '100%' }}>
                              <ClipboardIcon size={20} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '13px' }}>Paste Manually</div>
                                <div style={{ fontSize: '11px', color: '#7a7a7a' }}>Copy/paste your work history</div>
                              </div>
                            </button>
                          </div>
                          {profile.experiences?.length > 0 && (
                            <div style={{ marginTop: '16px', padding: '8px 12px', background: '#ecfdf5', borderRadius: '6px', fontSize: '12px', color: '#047857' }}>
                              Profile loaded: {profile.experiences.length} positions, {profile.skills?.length || 0} skills
                            </div>
                          )}
                        </>
                      )}
                      {profileImportMethod === 'linkedin' && !linkedInStep && (
                        <div>
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#7a7a7a' }}>Copy your LinkedIn profile content and paste it below:</p>
                          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', fontSize: '11px', color: '#0369a1' }}>
                            <strong>How to copy:</strong> Go to your LinkedIn profile → Select all text (Ctrl+A / Cmd+A) → Copy (Ctrl+C / Cmd+C) → Paste below
                          </div>
                          <textarea
                            value={linkedInText}
                            onChange={(e) => setLinkedInText(e.target.value)}
                            placeholder="Paste your LinkedIn profile content here...&#10;&#10;Include your experience, skills, and about section."
                            style={{ width: '100%', minHeight: '140px', padding: '12px', fontSize: '12px', border: '1px solid #e5e5e5', borderRadius: '4px', resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button className="btn btn-primary" onClick={handleLinkedInTextParse} disabled={!linkedInText.trim()} style={{ fontSize: '11px', padding: '8px 16px' }}>Parse Profile</button>
                            <button className="btn btn-secondary" onClick={() => { setProfileImportMethod(null); setLinkedInText(''); }} style={{ fontSize: '11px', padding: '8px 16px' }}>← Back</button>
                          </div>
                        </div>
                      )}
                      {profileImportMethod === 'manual' && !linkedInStep && (
                        <div>
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#7a7a7a' }}>Paste your work history, skills, and achievements:</p>
                          <textarea value={manualProfileText} onChange={(e) => setManualProfileText(e.target.value)} placeholder="Senior Software Engineer at Google (2020-2024)&#10;- Led team of 5 engineers&#10;&#10;Skills: Python, Go, Kubernetes..." style={{ width: '100%', minHeight: '120px', padding: '12px', fontSize: '12px', border: '1px solid #e5e5e5', borderRadius: '4px', resize: 'vertical' }} />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button className="btn btn-primary" onClick={() => { setLinkedInText(manualProfileText); handleLinkedInTextParse(); }} disabled={!manualProfileText.trim()} style={{ fontSize: '11px', padding: '8px 16px' }}>Parse Profile</button>
                            <button className="btn btn-secondary" onClick={() => { setProfileImportMethod(null); setManualProfileText(''); }} style={{ fontSize: '11px', padding: '8px 16px' }}>← Back</button>
                          </div>
                        </div>
                      )}
                      {resumeUploadStep === 'extracting' && (<div style={{ textAlign: 'center', padding: '20px' }}><div style={{ width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTopColor: '#f7c948', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} /><p style={{ margin: 0, fontSize: '12px', color: '#7a7a7a' }}>Extracting text...</p></div>)}
                      {resumeUploadStep === 'parsing' && (<div style={{ textAlign: 'center', padding: '20px' }}><div style={{ width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTopColor: '#f7c948', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} /><p style={{ margin: 0, fontSize: '12px', color: '#7a7a7a' }}>Parsing with AI...</p></div>)}
                      {resumeUploadStep === 'error' && (<div style={{ padding: '16px', background: '#fce8e8', borderRadius: '4px' }}><p style={{ margin: '0 0 12px', fontSize: '12px', color: '#943c33' }}>{resumeUploadError || 'Error processing resume.'}</p><button className="btn btn-secondary" onClick={() => { setResumeUploadStep(null); setResumeUploadError(null); }} style={{ fontSize: '11px' }}>Try Again</button></div>)}
                      {resumeUploadStep === 'preview' && parsedResumeData && (<div><p style={{ margin: '0 0 16px', fontSize: '12px', color: '#5a8f5a' }}>Resume parsed! Review below.</p><div style={{ marginBottom: '16px' }}><h4 style={{ margin: '0 0 8px', fontSize: '12px', color: '#7a7a7a' }}>Experience ({parsedResumeData.experiences?.length || 0})</h4><div style={{ maxHeight: '120px', overflow: 'auto', background: '#f9fafb', padding: '8px', borderRadius: '4px' }}>{parsedResumeData.experiences?.slice(0, 3).map((exp, i) => (<div key={i} style={{ fontSize: '11px', marginBottom: '4px' }}><div style={{ fontWeight: '600' }}>{exp.role}</div><div style={{ color: '#7a7a7a' }}>{exp.company}</div></div>))}</div></div><div style={{ display: 'flex', gap: '8px' }}><button className="btn btn-primary" onClick={confirmResumeData} style={{ fontSize: '11px' }}>Save to Profile</button><button className="btn btn-secondary" onClick={() => { setResumeUploadStep(null); setParsedResumeData(null); }} style={{ fontSize: '11px' }}>Cancel</button></div></div>)}
                      {linkedInStep === 'input' && (<div>{linkedInError && <div style={{ padding: '8px', background: '#fef3c7', borderRadius: '4px', marginBottom: '12px', fontSize: '11px', color: '#92400e' }}>{linkedInError}</div>}<p style={{ margin: '0 0 8px', fontSize: '12px', color: '#7a7a7a' }}>Paste your LinkedIn profile content:</p><textarea value={linkedInText} onChange={(e) => setLinkedInText(e.target.value)} placeholder="Paste LinkedIn content..." style={{ width: '100%', minHeight: '120px', padding: '12px', fontSize: '12px', border: '1px solid #e5e5e5', borderRadius: '4px' }} /><div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}><button className="btn btn-primary" onClick={handleLinkedInTextParse} disabled={!linkedInText.trim()} style={{ fontSize: '11px' }}>Parse</button><button className="btn btn-secondary" onClick={() => { setLinkedInStep(null); setLinkedInText(''); setLinkedInError(null); setProfileImportMethod(null); }} style={{ fontSize: '11px' }}>Cancel</button></div></div>)}
                      {linkedInStep === 'parsing' && (<div style={{ textAlign: 'center', padding: '20px' }}><div style={{ width: '32px', height: '32px', border: '3px solid #e5e5e5', borderTopColor: '#0077b5', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} /><p style={{ margin: 0, fontSize: '12px', color: '#7a7a7a' }}>Parsing profile...</p></div>)}
                      {linkedInStep === 'error' && (<div style={{ padding: '16px', background: '#fce8e8', borderRadius: '4px' }}><p style={{ margin: '0 0 12px', fontSize: '12px', color: '#943c33' }}>{linkedInError || 'Error parsing profile.'}</p><button className="btn btn-secondary" onClick={() => { setLinkedInStep('input'); setLinkedInError(null); }} style={{ fontSize: '11px' }}>Try Again</button></div>)}
                      {linkedInStep === 'preview' && parsedLinkedInData && (<div><p style={{ margin: '0 0 16px', fontSize: '12px', color: '#5a8f5a' }}>Profile parsed! Review below.</p><div style={{ marginBottom: '16px' }}><h4 style={{ margin: '0 0 8px', fontSize: '12px', color: '#7a7a7a' }}>Experience ({parsedLinkedInData.experiences?.length || 0})</h4><div style={{ maxHeight: '120px', overflow: 'auto', background: '#f9fafb', padding: '8px', borderRadius: '4px' }}>{parsedLinkedInData.experiences?.slice(0, 3).map((exp, i) => (<div key={i} style={{ fontSize: '11px', marginBottom: '4px' }}><div style={{ fontWeight: '600' }}>{exp.role}</div><div style={{ color: '#7a7a7a' }}>{exp.company}</div></div>))}</div></div><div style={{ display: 'flex', gap: '8px' }}><button className="btn btn-primary" onClick={confirmLinkedInData} style={{ fontSize: '11px' }}>Save to Profile</button><button className="btn btn-secondary" onClick={() => { setLinkedInStep(null); setLinkedInUrl(''); setLinkedInText(''); setParsedLinkedInData(null); setProfileImportMethod(null); }} style={{ fontSize: '11px' }}>Cancel</button></div></div>)}
                    </div>
                  </div>
                )}

                {/* RESUME SECTION */}
                {settingsSection === 'resume' && (
                  <div className="fade-in">
                    {/* Experience Display Card */}
                    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Experience ({profile.experiences?.length || 0})</h3>
                      {profile.experiences?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {profile.experiences?.map((exp, i) => (
                            <div key={i} style={{ fontSize: '12px', padding: '10px', background: '#f9fafb', borderRadius: '4px' }}>
                              <div style={{ fontWeight: '600' }}>{exp.role}</div>
                              <div style={{ color: '#7a7a7a' }}>{exp.company} - {exp.period}</div>
                              {exp.achievements?.length > 0 && (
                                <ul style={{ margin: '6px 0 0', paddingLeft: '16px', color: '#5a5a5a' }}>
                                  {exp.achievements.slice(0, 2).map((ach, j) => (
                                    <li key={j} style={{ fontSize: '11px' }}>{ach}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>No experience loaded yet.</div>
                      )}
                    </div>

                    {/* Skills Display Card */}
                    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Skills ({profile.skills?.length || 0})</h3>
                      {profile.skills?.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {profile.skills?.map((skill, i) => (
                            <span key={i} className="tag tag-green" style={{ fontSize: '11px', padding: '4px 10px' }}>{skill}</span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>No skills loaded yet.</div>
                      )}
                    </div>

                    {/* Education Card */}
                    <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Education</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(profile.education?.length > 0) ? (
                          profile.education.map((edu, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="text"
                                value={typeof edu === 'string' ? edu : `${edu.institution || ''} — ${edu.degree || ''} ${edu.year || ''}`.trim()}
                                onChange={(e) => {
                                  const newEdu = [...(profile.education || [])];
                                  newEdu[i] = e.target.value;
                                  setProfile({ ...profile, education: newEdu });
                                }}
                                style={{ flex: 1, padding: '8px 10px', fontSize: '12px', border: '1px solid #e5e5e5', borderRadius: '4px' }}
                                placeholder="e.g., MIT — B.S. Computer Science — 2020"
                              />
                              <button
                                onClick={() => {
                                  const newEdu = profile.education.filter((_, idx) => idx !== i);
                                  setProfile({ ...profile, education: newEdu });
                                }}
                                style={{ padding: '6px 10px', fontSize: '12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: '12px', color: '#9ca3af' }}>No education added yet.</div>
                        )}
                        <button
                          onClick={() => setProfile({ ...profile, education: [...(profile.education || []), ''] })}
                          style={{ alignSelf: 'flex-start', padding: '8px 12px', fontSize: '12px', background: '#f3f4f6', border: '1px solid #e5e5e5', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          + Add Education
                        </button>
                      </div>
                    </div>

                    {/* Activities Card */}
                    <div className="card" style={{ padding: '20px' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px' }}>Activities</h3>
                      <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#7a7a7a' }}>
                        Board positions, advisorships, volunteer work, memberships
                      </p>
                      <textarea
                        value={profile.activities || ''}
                        onChange={(e) => setProfile({ ...profile, activities: e.target.value })}
                        placeholder="e.g., Advisor to Deep Tech (Starship Ventures), Board Member at XYZ Foundation"
                        style={{ width: '100%', minHeight: '60px', padding: '10px', fontSize: '12px', border: '1px solid #e5e5e5', borderRadius: '4px', resize: 'vertical' }}
                      />
                    </div>
                  </div>
                )}

                {/* INSIGHTS LOG SECTION */}
                {settingsSection === 'insights' && (
                  <div className="fade-in">
                    <div className="card" style={{ padding: '20px' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '14px' }}>Insights Log</h3>
                      <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#7a7a7a' }}>
                        Everything you've shared during gap Q&A sessions ({discoveredInsights.length} insights)
                      </p>

                      {discoveredInsights.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#9ca3af', background: '#f9fafb', padding: '16px', borderRadius: '4px', textAlign: 'center' }}>
                          No insights yet. They'll appear as you answer gap questions during job analysis.
                        </div>
                      ) : (
                        <div style={{ maxHeight: '500px', overflow: 'auto', background: '#f9fafb', borderRadius: '4px' }}>
                          {discoveredInsights.slice().reverse().map(insight => (
                            <div key={insight.id} style={{
                              padding: '14px',
                              borderBottom: '1px solid #e5e5e5',
                              fontSize: '12px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span className={`tag ${insight.source === 'human_feedback' ? 'tag-green' : 'tag-blue'}`} style={{ fontSize: '10px', padding: '2px 8px' }}>
                                  {insight.source === 'human_feedback' ? 'Feedback' : insight.skill}
                                </span>
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {new Date(insight.timestamp).toLocaleDateString()}
                                </span>
                                {insight.jobContext && (
                                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                    • {insight.jobContext}
                                  </span>
                                )}
                              </div>
                              <div style={{ color: '#4b5563', lineHeight: '1.5' }}>
                                {insight.context}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* DATA MANAGEMENT SECTION */}
                {settingsSection === 'data' && (
                  <div className="fade-in">
                    <div className="card" style={{ padding: '20px' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: '14px' }}>Data Management</h3>
                      <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#7a7a7a' }}>
                        Export your data to backup, or import a previous backup.
                      </p>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={exportData}
                          className="btn btn-secondary"
                          style={{ padding: '10px 20px', fontSize: '13px' }}
                        >
                          ↓ Export Data
                        </button>
                        <label className="btn btn-secondary" style={{ padding: '10px 20px', fontSize: '13px', cursor: 'pointer' }}>
                          ↑ Import Data
                          <input type="file" accept=".json" onChange={importData} style={{ display: 'none' }} />
                        </label>
                      </div>

                      <div style={{ marginTop: '24px', padding: '16px', background: '#f9fafb', borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: '#7a7a7a', marginBottom: '8px' }}>Current data:</div>
                        <div style={{ fontSize: '12px', color: '#5a5a5a' }}>
                          <div>• {jobLedger.length} jobs tracked</div>
                          <div>• {discoveredInsights.length} insights</div>
                          <div>• {interviews.length} interviews</div>
                          <div>• {profile.experiences?.length || 0} experience entries</div>
                          <div>• {profile.skills?.length || 0} skills</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Print Preview Modal */}
      {showPrintPreview && generatedResume && (
        <div id="print-preview-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>
            <div className="no-print" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#f9fafb',
              borderRadius: '8px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ fontWeight: '500' }}>Resume for {currentAnalysis?.company}</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} className="no-print">
                <button
                  onClick={copyResumeText}
                  style={{
                    background: copySuccess ? '#16a34a' : '#f7c948',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    minWidth: '120px',
                    transition: 'background 0.2s'
                  }}
                >
                  {copySuccess ? <><SmallCheckIcon size={14} /> Copied!</> : <><SmallClipboardIcon size={14} /> Copy Text</>}
                </button>
                <button
                  onClick={() => setShowPrintPreview(false)}
                  style={{
                    background: '#e5e5e5',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ✕ Close
                </button>
              </div>
            </div>

            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px', textAlign: 'center' }} className="no-print">
              Right-click → Print → Save as PDF
            </div>
            {/* Printable Resume */}
            <div id="printable-resume" style={{ 
              background: 'white', 
              color: '#000',
              padding: '0.5in 0.6in',
              fontFamily: "'Libre Franklin', Arial, sans-serif",
              fontSize: '10pt',
              lineHeight: '1.3',
              maxHeight: '10.5in',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}>
              <div style={{ fontSize: '18pt', fontWeight: '700', textAlign: 'center', marginBottom: '8px' }}>
                {generatedResume.name}
              </div>
              
              <div style={{ textAlign: 'justify', marginBottom: '12px', fontSize: '9.5pt', lineHeight: '1.35' }}>
                {generatedResume.summary}
              </div>
              
              <div style={{ fontWeight: '700', fontSize: '10pt', borderBottom: '1px solid #000', marginTop: '10px', marginBottom: '8px', paddingBottom: '2px' }}>
                RELEVANT EXPERIENCE
              </div>
              
              {generatedResume.experiences?.map((exp, i) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                    <span>
                      <span style={{ fontWeight: '600' }}>{exp.company}</span>
                      {exp.type && ` (${exp.type})`}
                      {exp.location && `, ${exp.location}`}
                      {' — '}<span style={{ fontStyle: 'italic' }}>{exp.role}</span>
                    </span>
                    <span>{exp.period}</span>
                  </div>
                  <div style={{ paddingLeft: '16px' }}>
                    {exp.bullets?.map((bullet, j) => (
                      <div key={j} style={{ marginBottom: '2px', textAlign: 'justify', position: 'relative', paddingLeft: '12px' }}>
                        <span style={{ position: 'absolute', left: 0, fontSize: '6pt', top: '2px' }}>●</span>
                        {bullet}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div style={{ marginTop: '10px', fontSize: '9.5pt' }}>
                <span style={{ fontWeight: '700' }}>SKILLS:</span> {Array.isArray(generatedResume.skills) ? generatedResume.skills.join(', ') : generatedResume.skills}
              </div>
              
              {generatedResume.activities && (
                <div style={{ marginTop: '6px', fontSize: '9.5pt' }}>
                  <span style={{ fontWeight: '700' }}>ACTIVITIES:</span> {generatedResume.activities}
                </div>
              )}
              
              <div style={{ marginTop: '6px', fontSize: '9.5pt' }}>
                <span style={{ fontWeight: '700' }}>EDUCATION:</span> {generatedResume.education}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer style={{
        background: '#ffffff',
        borderTop: '1px solid #e5e5e5',
        padding: '24px 20px',
        marginTop: '40px'
      }}>
        <div style={{ 
          maxWidth: '900px', 
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: '#7a7a7a',
            fontFamily: "'IBM Plex Sans', sans-serif"
          }}>
            © 2025 Generative Intelligence, LLC
          </div>
          <div style={{ display: 'flex', gap: '24px' }}>
            <button
              onClick={() => setShowAbout(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#7a7a7a',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
                padding: 0
              }}
            >
              About
            </button>
            <button
              onClick={() => setShowContact(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#7a7a7a',
                fontSize: '12px',
                cursor: 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
                padding: 0
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </footer>
      
      {/* About Modal */}
      {showAbout && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(61, 61, 61, 0.95)',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{ maxWidth: '600px', margin: '40px auto' }}>
            <div className="card" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div>
                  <h2 style={{ 
                    margin: 0, 
                    fontSize: '24px', 
                    fontFamily: "'Playfair Display', serif",
                    color: '#3d3d3d'
                  }}>
                    About Manifold
                  </h2>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#7a7a7a', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Career Architecture
                  </p>
                </div>
                <button 
                  onClick={() => setShowAbout(false)}
                  style={{ 
                    background: '#3d3d3d', 
                    border: 'none', 
                    color: 'white', 
                    padding: '8px 16px', 
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ✕ Close
                </button>
              </div>
              
              <div style={{ fontSize: '14px', color: '#3d3d3d', lineHeight: '1.7' }}>
                <p style={{ margin: '0 0 16px' }}>
                  <strong>Manifold</strong> helps you navigate the actual shape of your career — not a flattened projection of it.
                </p>
                <p style={{ margin: '0 0 16px' }}>
                  Career paths aren't linear. They're trajectories on a manifold — locally simple, globally complex. The shortest path between where you are and where you want to be follows the curvature of your unique skills, experiences, and opportunities.
                </p>
                <p style={{ margin: '0 0 16px' }}>
                  Manifold is an AI that scores job-resume fit, surfaces skill gaps, generates micro-projects to close them, and maps your fastest path to higher compensation.
                </p>
                <p style={{ margin: '0' }}>
                  Stop applying blindly. Start architecting your career with intention.
                </p>
              </div>
              
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e5e5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <svg width="32" height="32" viewBox="0 0 52 52" fill="none">
                    <path d="M8 38 Q16 28, 22 32 T34 26" stroke="#3d3d3d" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
                    <path d="M6 44 Q14 32, 20 38 T32 30" stroke="#3d3d3d" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.4"/>
                    <circle cx="8" cy="38" r="2.5" fill="#d4736a"/>
                    <g transform="rotate(-45 38 14)">
                      <rect x="24" y="11" width="20" height="6" fill="#f7c948" stroke="#d4a832" strokeWidth="0.5"/>
                      <rect x="44" y="11" width="4" height="6" fill="#b8b8b8"/>
                      <rect x="48" y="11.5" width="3" height="5" rx="0.5" fill="#d4736a"/>
                      <polygon points="24,11 24,17 20,14" fill="#c9a87c"/>
                      <polygon points="20,14 22,12.5 22,15.5" fill="#3d3d3d"/>
                    </g>
                  </svg>
                  <div style={{ fontSize: '12px', color: '#7a7a7a' }}>
                    Built by Generative Intelligence, LLC
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact Modal */}
      {showContact && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(61, 61, 61, 0.95)',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{ maxWidth: '500px', margin: '40px auto' }}>
            <div className="card" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: '24px', 
                  fontFamily: "'Playfair Display', serif",
                  color: '#3d3d3d'
                }}>
                  Contact Us
                </h2>
                <button 
                  onClick={() => setShowContact(false)}
                  style={{ 
                    background: '#3d3d3d', 
                    border: 'none', 
                    color: 'white', 
                    padding: '8px 16px', 
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  ✕ Close
                </button>
              </div>
              
              <div style={{ fontSize: '14px', color: '#3d3d3d', lineHeight: '1.7' }}>
                <p style={{ margin: '0 0 16px' }}>
                  Have questions, feedback, or suggestions? We'd love to hear from you.
                </p>
                <p style={{ margin: '0 0 24px' }}>
                  Reach out and we'll get back to you as soon as possible.
                </p>
              </div>
              
              <a 
                href="mailto:lori.aretemgmt@gmail.com?subject=Manifold Careers Inquiry"
                className="btn btn-primary"
                style={{ 
                  display: 'inline-block',
                  padding: '12px 24px', 
                  fontSize: '14px',
                  textDecoration: 'none',
                  textAlign: 'center'
                }}
              >
                ✉ Email Us
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Paywall Modal */}
      {/* Show paywall if: 1) explicitly triggered (showPaywall), or 2) user selected Pro from marketing and is on free tier */}
      {(showPaywall || (pendingPlan && pendingPlan === 'pro' && subscriptionStatus === 'free')) && (
        <SubscriptionGate
          onSubscribe={handleSubscribe}
          onDismiss={() => { setShowPaywall(false); setPaywallFeature(null); onClearPendingPlan?.(); }}
          feature={paywallFeature || 'resume'}
          currentTier={effectiveTier}
        />
      )}
    </div>
  );
}
